import React, { useState, useEffect, useRef, useCallback, useMemo, JSX } from 'react';
import { 
  Building2, 
  QrCode, 
  Users, 
  BarChart3, 
  Plus, 
  LogOut, 
  Download,
  Eye,
  Edit,
  Trash2,
  Loader2,
  GitBranch,
  Calendar,
  Activity,
  X,
  Save,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Upload,
  ExternalLink,
  ArrowLeft,
  User as UserIcon,
  Mail,
  Lock,
  Shield,
  UserPlus,
  Info,
  MapPin,
  Phone,
  FileText,
  TrendingUp,
  Package,
  Sun,
  Moon,
  Settings,
  UserCircle,
  Key,
  Edit2,
  Check,
  Menu as MenuIcon,
  RefreshCw,
  Printer,
  Copy,
  ChevronUp,
  ChevronDown,
  Search,
  GitMerge,
  CheckSquare,
  Square,
} from 'lucide-react';
import { superAdminAPI, Restaurant, Table, PlatformStats, User, Branch, RestaurantPagination, CounterQR, MenuCategory, MenuItem, RestaurantLogo, AddOn } from '../../services/api';
import { API_BASE_URL } from '../../config/api';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';
import { supabase } from '../../lib/supabase';
import { logger } from '../../lib/logger';
import { ImageCropModal } from './ImageCropModal';
import { PlanBadge } from './PlanBadge';
import { ToastContainer, type Toast } from './Toast';
import { DashboardView } from './views/DashboardView';
import { RestaurantsView } from './views/RestaurantsView';
import { UsersView } from './views/UsersView';
import { BranchesView } from './views/BranchesView';
import { PasswordChangeModal } from './modals/PasswordChangeModal';
import { DeleteConfirmModal } from './modals/DeleteConfirmModal';
import { InfoModal } from './modals/InfoModal';

interface SuperAdminDashboardProps {
  onLogout: () => void;
}

type View = 'dashboard' | 'restaurants' | 'edit-restaurant' | 'users' | 'create-user' | 'branches' | 'create-branch' | 'qr-codes' | 'reports' | 'profile' | 'menu';

export function SuperAdminDashboard({ onLogout }: SuperAdminDashboardProps): JSX.Element {
  const { theme, toggleTheme } = useTheme();
  const [currentView, setCurrentView] = useState<View>('dashboard');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [restaurantPagination, setRestaurantPagination] = useState<RestaurantPagination | null>(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [editingRestaurant, setEditingRestaurant] = useState<Restaurant | null>(null);
  const [restaurantUsers, setRestaurantUsers] = useState<User[]>([]);
  const [restaurantBranches, setRestaurantBranches] = useState<Branch[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [allBranches, setAllBranches] = useState<Branch[]>([]);
  const [allTables, setAllTables] = useState<Table[]>([]);
  const [usersPage, setUsersPage] = useState(1);
  const [branchesPage, setBranchesPage] = useState(1);
  const [tablesPage, setTablesPage] = useState(1);
  const [usersPagination, setUsersPagination] = useState<any>(null);
  const [branchesPagination, setBranchesPagination] = useState<any>(null);
  const [tablesPagination, setTablesPagination] = useState<any>(null);
  const [tables, setTables] = useState<Table[]>([]);
  const [counterQRs, setCounterQRs] = useState<CounterQR[]>([]);
  const [editingCounterQR, setEditingCounterQR] = useState<CounterQR | null>(null);
  const [showCounterQRForm, setShowCounterQRForm] = useState(false);
  const [newCounterQRName, setNewCounterQRName] = useState('');
  const [newCounterQRDescription, setNewCounterQRDescription] = useState('');
  const [newCounterQRBranchId, setNewCounterQRBranchId] = useState<string>('');
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [reports, setReports] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [savingRestaurant, setSavingRestaurant] = useState(false);
  const [deletingRestaurant, setDeletingRestaurant] = useState(false);
  const [clearingRestaurantData, setClearingRestaurantData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiHealth, setApiHealth] = useState<{ status: 'checking' | 'healthy' | 'unhealthy' | 'disabled'; message?: string } | null>(null);
  const [showResetLoyaltyModal, setShowResetLoyaltyModal] = useState(false);
  const [resettingLoyalty, setResettingLoyalty] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Toast helpers - memoized to prevent re-renders
  const addToast = useCallback((message: string, type: Toast['type'] = 'info', duration?: number) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type, duration }]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showSuccess = useCallback((message: string, duration?: number) => addToast(message, 'success', duration), [addToast]);
  const showError = useCallback((message: string, duration?: number) => addToast(message, 'error', duration || 6000), [addToast]);
  const showWarning = useCallback((message: string, duration?: number) => addToast(message, 'warning', duration), [addToast]);
  const showInfo = useCallback((message: string, duration?: number) => addToast(message, 'info', duration), [addToast]);
  // Tracks the last plan we saw for the current editing session (prevents unwanted auto-overwrites)
  const previousPlanRef = useRef<string | null>(null);
  
  // Request deduplication
  const pendingRequestsRef = useRef<Set<string>>(new Set());
  
  // Restaurant filters
  const [restaurantPage, setRestaurantPage] = useState(1);
  const [restaurantFilterPlan, setRestaurantFilterPlan] = useState<string>('');
  const [restaurantFilterActive, setRestaurantFilterActive] = useState<boolean | undefined>(undefined);
  
  // Modals
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreateBranch, setShowCreateBranch] = useState(false);
  const [showQRGenerator, setShowQRGenerator] = useState(false);
  const [showManageQRModal, setShowManageQRModal] = useState(false);
  const [selectedTableForManage, setSelectedTableForManage] = useState<Table | null>(null);
  const [tableMenuFilters, setTableMenuFilters] = useState<{ vegFilter: 'all' | 'veg' | 'non-veg'; categories: string[]; showOnlyAvailable: boolean }>({
    vegFilter: 'all',
    categories: [],
    showOnlyAvailable: false,
  });
  const [counterQRMenuFilters, setCounterQRMenuFilters] = useState<{ vegFilter: 'all' | 'veg' | 'non-veg'; categories: string[]; showOnlyAvailable: boolean }>({
    vegFilter: 'all',
    categories: [],
    showOnlyAvailable: false,
  });
  const [selectedCounterQRForManage, setSelectedCounterQRForManage] = useState<CounterQR | null>(null);
  const [showManageCounterQRModal, setShowManageCounterQRModal] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; restaurant: Restaurant | null }>({
    isOpen: false,
    restaurant: null,
  });
  const [clearDataConfirm, setClearDataConfirm] = useState<{ isOpen: boolean; restaurant: Restaurant | null }>({
    isOpen: false,
    restaurant: null,
  });
  
  // Delete confirmation modals for various actions
  const [deleteTableQRConfirm, setDeleteTableQRConfirm] = useState<{ isOpen: boolean; tableId: string; tableNumber: string } | null>(null);
  const [deleteCounterQRConfirm, setDeleteCounterQRConfirm] = useState<{ isOpen: boolean; counterQRId?: string; name?: string } | null>(null);
  const [deleteMenuCategoryConfirm, setDeleteMenuCategoryConfirm] = useState<{ isOpen: boolean; categoryId: string } | null>(null);
  const [resetLoyaltyConfirm, setResetLoyaltyConfirm] = useState(false);
  
  const [newTableNumber, setNewTableNumber] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<'OWNER' | 'MANAGER' | 'WAITER' | 'KITCHEN' | 'DELIVERY'>('WAITER');
  const [newUserBranchId, setNewUserBranchId] = useState<string>('');
  const [newBranchName, setNewBranchName] = useState('');
  const [newBranchAddress, setNewBranchAddress] = useState('');
  const [newBranchPhone, setNewBranchPhone] = useState('');
  const [newBranchEmail, setNewBranchEmail] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoType, setLogoType] = useState<'regular' | 'horizontal' | 'vertical'>('regular');
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  
  // Profile state
  const [currentUser, setCurrentUser] = useState<{ name: string; email: string; id: string } | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  
  // Password change for other users
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [selectedUserForPasswordChange, setSelectedUserForPasswordChange] = useState<User | null>(null);
  const [newUserPasswordForChange, setNewUserPasswordForChange] = useState('');
  const [confirmUserPasswordForChange, setConfirmUserPasswordForChange] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState<string | null>(null);
  const [changingUserPassword, setChangingUserPassword] = useState(false);
  
  // Test print state
  const [testingPrint, setTestingPrint] = useState(false);
  const [showTestPrintModal, setShowTestPrintModal] = useState(false);
  const [selectedRestaurantForPrint, setSelectedRestaurantForPrint] = useState<Restaurant | null>(null);
  const [restaurantOrders, setRestaurantOrders] = useState<any[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedPrintType, setSelectedPrintType] = useState<'VEG_KOT' | 'NON_VEG_KOT' | 'BEVERAGE' | 'VEG' | 'NON-VEG' | 'ALL' | 'FULL_BILL'>('VEG_KOT');
  
  // Menu management state
  const [restaurantMenu, setRestaurantMenu] = useState<MenuCategory[]>([]);
  const [editingMenuCategory, setEditingMenuCategory] = useState<MenuCategory | null>(null);
  const [showMenuForm, setShowMenuForm] = useState(false);
  const [newMenuCategory, setNewMenuCategory] = useState('');
  const [newMenuCategorySerialNumber, setNewMenuCategorySerialNumber] = useState<number | undefined>(undefined);
  const [editingSerialNumber, setEditingSerialNumber] = useState<string | null>(null); // category._id
  const [tempSerialNumber, setTempSerialNumber] = useState<number | undefined>(undefined);
  const [newMenuItems, setNewMenuItems] = useState<MenuItem[]>([]);
  const [categoryAddOns, setCategoryAddOns] = useState<{ name: string; price: number }[]>([]);
  const [showPDFUploadModal, setShowPDFUploadModal] = useState(false);
  const [extractedMenuData, setExtractedMenuData] = useState<{ category: string; items: MenuItem[] }[]>([]);
  const [uploadingPDF, setUploadingPDF] = useState(false);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [selectedCategoriesForMerge, setSelectedCategoriesForMerge] = useState<Set<string>>(new Set());
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeTargetCategoryId, setMergeTargetCategoryId] = useState<string | null>(null);
  const [mergeCategoryName, setMergeCategoryName] = useState('');
  const [mergingCategories, setMergingCategories] = useState(false);
  
  // Menu optimization: local state for editing items without full re-renders
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [savingMenu, setSavingMenu] = useState(false);
  const [pendingMenuChanges, setPendingMenuChanges] = useState<Map<string, MenuCategory>>(new Map());
  
  // Logo selection for QR codes
  const [selectedLogoIndexForTable, setSelectedLogoIndexForTable] = useState<number | null>(null);
  const [selectedLogoIndexForCounter, setSelectedLogoIndexForCounter] = useState<number | null>(null);
  const [selectedLogoIndexForManageQR, setSelectedLogoIndexForManageQR] = useState<number | null>(null);
  const [selectedLogoIndexForManageCounterQR, setSelectedLogoIndexForManageCounterQR] = useState<number | null>(null);

  // Fetch current user info
  const fetchCurrentUser = useCallback(async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      if (user) {
        setCurrentUser({
          id: user.id,
          email: user.email || '',
          name: user.user_metadata?.name || user.email?.split('@')[0] || 'Super Admin'
        });
        setProfileName(user.user_metadata?.name || user.email?.split('@')[0] || 'Super Admin');
        setProfileEmail(user.email || '');
      }
    } catch (err) {
      logger.error('Failed to fetch user', err);
    }
  }, []);

  // Update user name
  const handleUpdateName = useCallback(async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.updateUser({
        data: { name: profileName }
      });
      if (error) throw error;
      
      if (currentUser) {
        setCurrentUser({ ...currentUser, name: profileName });
      }
      setEditingName(false);
      showSuccess('Name updated successfully!');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update name');
    } finally {
      setLoading(false);
    }
  }, [profileName, currentUser, showSuccess, showError]);

  // Update user email
  const handleUpdateEmail = useCallback(async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.updateUser({
        email: profileEmail
      });
      if (error) throw error;
      
      showInfo('Email update request sent! Please check your new email for confirmation.');
      setEditingEmail(false);
      await fetchCurrentUser();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update email');
    } finally {
      setLoading(false);
    }
  }, [profileEmail, fetchCurrentUser, showInfo, showError]);

  // Change password
  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess(false);
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    try {
      setChangingPassword(true);
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      setTimeout(() => {
        setPasswordSuccess(false);
      }, 3000);
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showProfileMenu && !target.closest('.profile-menu-container')) {
        setShowProfileMenu(false);
      }
    };
    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showProfileMenu]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const reportsData = await superAdminAPI.getReports();
      setReports(reportsData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch reports';
      setError(errorMessage);
      logger.error('Fetch reports error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // API Health Check
  const checkApiHealth = useCallback(async () => {
    if (apiHealth?.status === 'disabled') return;
    
    try {
      setApiHealth({ status: 'checking' });
      const healthResponse = await fetch(`${API_BASE_URL}/health`);
      
      if (healthResponse.ok) {
        const healthData = await healthResponse.json();
        setApiHealth({ 
          status: 'healthy', 
          message: `Backend: ${healthData.status}, Database: ${healthData.database?.status || 'Unknown'}` 
        });
      } else {
        setApiHealth({ status: 'unhealthy', message: `Backend responded with status ${healthResponse.status}` });
      }
    } catch (err) {
      setApiHealth({ 
        status: 'unhealthy', 
        message: err instanceof Error ? err.message : 'Connection failed' 
      });
    }
  }, [apiHealth]);

  const disableHealthCheck = useCallback(() => {
    setApiHealth({ status: 'disabled' });
  }, []);

  // Test API connection
  const testConnection = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      logger.debug('Testing connection to:', API_BASE_URL);
      
      // Test health endpoint (no auth required)
      const healthResponse = await fetch(`${API_BASE_URL}/health`);
      if (healthResponse.ok) {
        const healthData = await healthResponse.json();
        logger.debug('Health check passed', healthData);
        
        // Test authenticated endpoint
        const token = localStorage.getItem('sb-auth-token');
        if (token) {
          try {
            const parsed = JSON.parse(token);
            const authResponse = await fetch(`${API_BASE_URL}/api/super-admin/stats`, {
              headers: {
                'Authorization': `Bearer ${parsed.access_token}`,
                'Content-Type': 'application/json',
              },
            });
            
            if (authResponse.ok) {
              setError(null);
              showSuccess('Connection successful!', 5000);
              showInfo(
                `Backend: ${healthData.status}\nService: ${healthData.service}\nDatabase: ${healthData.database.status}\n\nAuthentication: Working`,
                6000
              );
            } else {
              const errorData = await authResponse.json().catch(() => ({}));
              setError(`Backend is running but authentication failed.\nStatus: ${authResponse.status}\nError: ${errorData.message || 'Unauthorized'}\n\nPlease check:\n1. You are logged in\n2. Your user has SUPER_ADMIN role\n3. Token is valid`);
            }
          } catch (authErr) {
            setError(`Backend is running but authentication test failed.\nError: ${authErr instanceof Error ? authErr.message : 'Unknown error'}\n\nPlease check your login credentials.`);
          }
        } else {
          setError(`Backend is running but you are not logged in.\n\nPlease login first.`);
        }
      } else {
        setError(`Backend responded with error status: ${healthResponse.status}`);
      }
    } catch (err) {
      logger.error('Connection test failed', err);
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      const apiUrl = API_BASE_URL;
      const isProduction = apiUrl.startsWith('https://');
      const errorDetails = isProduction
        ? `Cannot connect to backend server.\n\nError: ${errorMsg}\n\nPlease check:\n1. Backend server is running and accessible at ${apiUrl}\n2. CORS is configured correctly in Railway\n3. Network connection is active\n4. Verify the backend URL is correct in Vercel environment variables`
        : `Cannot connect to backend server.\n\nError: ${errorMsg}\n\nPlease check:\n1. Backend is running on port 8080\n2. CORS is configured correctly\n3. Network connection is active\n4. Firewall is not blocking the connection`;
      setError(errorDetails);
    } finally {
      setLoading(false);
    }
  }, [showSuccess, showError, showInfo]);

  // Fetch restaurant details when entering edit mode
  useEffect(() => {
    if (currentView === 'edit-restaurant' && selectedRestaurant && !editingRestaurant) {
      fetchRestaurantDetails(selectedRestaurant._id);
    }
  }, [currentView, selectedRestaurant]);

  // Fetch menu when menu view is active
  useEffect(() => {
    if (currentView === 'menu' && selectedRestaurant?._id) {
      // Always fetch menu when menu view becomes active to ensure fresh data
      // This ensures serial numbers are always loaded from the API
      logger.debug('Menu view active, fetching menu', { restaurantId: selectedRestaurant._id });
      fetchRestaurantMenu(selectedRestaurant._id);
    } else if (currentView === 'menu' && !selectedRestaurant) {
      // If menu view is active but no restaurant is selected, clear the menu
      logger.debug('Menu view active but no restaurant selected, clearing menu');
      setRestaurantMenu([]);
    }
  }, [currentView, selectedRestaurant?._id]);

  // Reset logo preview when editing restaurant changes
  useEffect(() => {
    if (editingRestaurant) {
      setLogoPreview(null);
      setLogoFile(null);
    }
  }, [editingRestaurant?._id]);

  // Helper function to get plan-specific defaults
  const getPlanDefaults = (plan: 'SILVER' | 'GOLD' | 'PLATINUM') => {
    if (plan === 'SILVER') {
      return {
        features: {
          menu: true,
          orders: true,
          dineIn: true,
          takeaway: false,
          delivery: false,
          reservations: true,
          qrCodes: true,
          customerLookup: true,
          reports: true,
          realTimeUpdates: true,
          loyalty: false,
          testPayment: false,
          dineInInCounterQR: false,
          showSerialNumber: false,
        },
        appAccess: {
          staffApp: true,
          kitchenApp: false,
          deliveryApp: false,
          adminApp: true,
        },
      };
    } else if (plan === 'GOLD') {
      return {
        features: {
          menu: true,
          orders: true,
          dineIn: true,
          takeaway: true,
          delivery: false,
          reservations: true,
          qrCodes: true,
          customerLookup: true,
          reports: true,
          realTimeUpdates: true,
          loyalty: false,
          testPayment: false,
          dineInInCounterQR: false,
          showSerialNumber: false,
        },
        appAccess: {
          staffApp: true,
          kitchenApp: true,
          deliveryApp: false,
          adminApp: true,
        },
      };
    } else {
      // PLATINUM
      return {
        features: {
          menu: true,
          orders: true,
          dineIn: true,
          takeaway: true,
          delivery: true,
          reservations: true,
          qrCodes: true,
          customerLookup: true,
          reports: true,
          realTimeUpdates: true,
          loyalty: true,
          testPayment: false,
          dineInInCounterQR: false,
          showSerialNumber: false,
        },
        appAccess: {
          staffApp: true,
          kitchenApp: true,
          deliveryApp: true,
          adminApp: true,
        },
      };
    }
  };

  // Auto-update features and appAccess when plan changes
  useEffect(() => {
    if (editingRestaurant && editingRestaurant.plan) {
      const currentPlan = editingRestaurant.plan;
      // Only update if plan actually changed (not on initial load)
      if (previousPlanRef.current !== null && previousPlanRef.current !== currentPlan) {
        const defaults = getPlanDefaults(currentPlan);
        setEditingRestaurant({
          ...editingRestaurant,
          features: {
            ...editingRestaurant.features,
            ...defaults.features,
          },
          appAccess: {
            ...editingRestaurant.appAccess,
            ...defaults.appAccess,
          },
        });
      }
      // Update the ref to track current plan
      previousPlanRef.current = currentPlan;
    }
  }, [editingRestaurant?.plan]);

  // Fetch data when view changes
  useEffect(() => {
    if (currentView === 'create-user' && selectedRestaurant) {
      // Fetch branches when creating user to show branch selection
      fetchRestaurantBranches(selectedRestaurant._id);
      fetchCounterQRs();
    }
    if (currentView === 'qr-codes' && !selectedRestaurant) {
      // No tables to fetch without a selected restaurant
    }
  }, [currentView, tablesPage, selectedRestaurant]);

  // Fetch tables and restaurant data when QR codes view is opened
  useEffect(() => {
    if (selectedRestaurant && currentView === 'qr-codes') {
      // Always fetch fresh restaurant data to ensure counter QR code is loaded
      const fetchData = async () => {
        try {
          // Fetch both restaurant details and tables in parallel
          const [fullRestaurant, tablesData] = await Promise.all([
            superAdminAPI.getRestaurant(selectedRestaurant._id),
            superAdminAPI.getTables(selectedRestaurant._id)
          ]);
          setSelectedRestaurant(fullRestaurant);
          setTables(tablesData);
          // Fetch counter QR codes
          const counterQRsData = await superAdminAPI.getCounterQRs(selectedRestaurant._id);
          setCounterQRs(counterQRsData);
        } catch (err) {
          logger.error('Failed to fetch QR code data', err);
          setError(err instanceof Error ? err.message : 'Failed to load QR code data');
          // Still try to fetch tables even if restaurant fetch fails
          try {
            const tablesData = await superAdminAPI.getTables(selectedRestaurant._id);
            setTables(tablesData);
          } catch (tableErr) {
            logger.error('Failed to fetch tables', tableErr);
          }
        }
      };
      fetchData();
    }
  }, [selectedRestaurant?._id, currentView]);

  const fetchStats = useCallback(async () => {
    const requestKey = 'fetchStats';
    if (pendingRequestsRef.current.has(requestKey)) {
      return;
    }
    
    try {
      pendingRequestsRef.current.add(requestKey);
      setLoadingStats(true);
      setError(null);
      logger.debug('Fetching platform stats');
      const data = await superAdminAPI.getStats();
      logger.debug('Stats received', data);
      if (data) {
        setStats(data);
      }
    } catch (err) {
      logger.error('Failed to fetch stats', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load statistics';
      setError(errorMessage);
      if (errorMessage.includes('Unauthorized') || errorMessage.includes('401')) {
        setTimeout(() => {
          onLogout();
        }, 2000);
      }
    } finally {
      setLoadingStats(false);
      pendingRequestsRef.current.delete(requestKey);
    }
  }, [onLogout]);

  // Fetch stats on mount - must be after all functions are defined
  useEffect(() => {
    const initialize = async () => {
      await fetchStats();
      checkApiHealth();
      fetchCurrentUser();
    };
    initialize();
    const healthCheckInterval = setInterval(checkApiHealth, 30000);
    return () => clearInterval(healthCheckInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const handleDeleteRestaurant = useCallback(async (restaurant: Restaurant) => {
    if (!restaurant?._id) {
      showError('Invalid restaurant data');
      return;
    }
    
    const requestKey = `deleteRestaurant-${restaurant._id}`;
    if (pendingRequestsRef.current.has(requestKey)) {
      return;
    }
    
    try {
      pendingRequestsRef.current.add(requestKey);
      setDeletingRestaurant(true);
      setError(null);
      await superAdminAPI.deleteRestaurant(restaurant._id);
      showSuccess('Restaurant deleted successfully!');
      
      setRestaurants(prev => prev.filter(r => r?._id && r._id !== restaurant._id));
      setDeleteConfirm({ isOpen: false, restaurant: null });
      
      await fetchStats();
    } catch (err) {
      logger.error('Failed to delete restaurant', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete restaurant';
      showError(errorMessage);
    } finally {
      setDeletingRestaurant(false);
      pendingRequestsRef.current.delete(requestKey);
    }
  }, [showSuccess, showError, fetchStats]);

  const handleClearRestaurantData = useCallback(async (restaurant: Restaurant) => {
    if (!restaurant?._id) {
      showError('Invalid restaurant data');
      return;
    }

    const requestKey = `clearRestaurantData-${restaurant._id}`;
    if (pendingRequestsRef.current.has(requestKey)) {
      return;
    }

    try {
      pendingRequestsRef.current.add(requestKey);
      setClearingRestaurantData(true);
      setError(null);
      await superAdminAPI.clearRestaurantData(restaurant._id);
      showSuccess('Restaurant data cleared successfully!');
      setClearDataConfirm({ isOpen: false, restaurant: null });
      await fetchStats();
    } catch (err) {
      logger.error('Failed to clear restaurant data', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to clear restaurant data';
      showError(errorMessage);
    } finally {
      setClearingRestaurantData(false);
      pendingRequestsRef.current.delete(requestKey);
    }
  }, [showSuccess, showError, fetchStats]);

  const fetchRestaurants = useCallback(async () => {
    const requestKey = `fetchRestaurants-${restaurantPage}-${restaurantFilterPlan}-${restaurantFilterActive}`;
    if (pendingRequestsRef.current.has(requestKey)) {
      return;
    }
    
    try {
      pendingRequestsRef.current.add(requestKey);
      setLoadingRestaurants(true);
      setError(null);
      const response = await superAdminAPI.getRestaurants({
        page: restaurantPage,
        limit: 12,
        plan: restaurantFilterPlan || undefined,
        isActive: restaurantFilterActive,
      });
      if (response?.data) {
        setRestaurants(Array.isArray(response.data) ? response.data : []);
      }
      if (response?.pagination) {
        setRestaurantPagination(response.pagination);
      }
    } catch (err) {
      logger.error('Failed to fetch restaurants', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load restaurants';
      setError(errorMessage);
      if (errorMessage.includes('Unauthorized') || errorMessage.includes('401')) {
        setTimeout(() => {
          onLogout();
        }, 2000);
      }
    } finally {
      setLoadingRestaurants(false);
      pendingRequestsRef.current.delete(requestKey);
    }
  }, [restaurantPage, restaurantFilterPlan, restaurantFilterActive, onLogout]);

  // Fetch restaurants when view changes or filters change
  useEffect(() => {
    if (currentView === 'restaurants') {
      fetchRestaurants();
    }
  }, [currentView, restaurantPage, restaurantFilterPlan, restaurantFilterActive, fetchRestaurants]);

  // Handle cropped image - must be after fetchRestaurants is defined
  const handleCropComplete = useCallback(async (croppedFile: File) => {
    if (!editingRestaurant?._id) {
      showError('Restaurant ID is required');
      return;
    }

    try {
      setShowCropModal(false);
      setUploadingLogo(true);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(croppedFile);
      
      const result = await superAdminAPI.uploadLogo(croppedFile, editingRestaurant._id, logoType);
      
      const updatedLogos = editingRestaurant.logos ? [...editingRestaurant.logos] : [];
      updatedLogos.push({
        url: result.url,
        public_id: result.public_id,
        format: result.format,
        uploadedAt: new Date().toISOString(),
      });
      const updatedRestaurant = { 
        ...editingRestaurant, 
        logos: updatedLogos,
        logoUrl: result.url,
      };
      setEditingRestaurant(updatedRestaurant);
      setLogoFile(croppedFile);
      
      try {
        const freshRestaurant = await superAdminAPI.getRestaurant(editingRestaurant._id);
        setEditingRestaurant(freshRestaurant);
        showSuccess('Logo uploaded and saved successfully!');
        await fetchRestaurants();
      } catch (saveError) {
        showWarning('Logo uploaded but failed to save. Please save the restaurant manually.');
      }
    } catch (err) {
      logger.error('Failed to upload cropped logo', err);
      showError(err instanceof Error ? err.message : 'Failed to upload logo');
      setLogoFile(null);
      setLogoPreview(null);
    } finally {
      setUploadingLogo(false);
      setImageToCrop(null);
    }
  }, [editingRestaurant, logoType, fetchRestaurants, showSuccess, showError, showWarning]);

  const fetchRestaurantDetails = async (restaurantId: string) => {
    try {
      setLoading(true);
      setError(null);
      const restaurant = await superAdminAPI.getRestaurant(restaurantId);
      
      // Migrate old payment methods structure to new one if needed
      if (restaurant.paymentMethods) {
        const oldMethods = restaurant.paymentMethods as any;
        // Check if it's the old structure (cash, card, upi)
        if ('cash' in oldMethods || 'card' in oldMethods || ('upi' in oldMethods && !('upi1' in oldMethods))) {
          // Migrate to new structure
          restaurant.paymentMethods = {
            payAtCounter: oldMethods.cash || oldMethods.card || true, // Default to true if either was enabled
            upi1: oldMethods.upi || true, // Migrate old upi to upi1
            upi2: true, // Default to true for new upi2
          };
        }
      } else {
        // Set default payment methods if none exist
        restaurant.paymentMethods = {
          payAtCounter: true,
          upi1: true,
          upi2: true,
        };
      }
      
      setSelectedRestaurant(restaurant);
      setEditingRestaurant(restaurant);
      // Reset plan ref when loading a restaurant
      previousPlanRef.current = restaurant.plan;
      // Fetch counter QR codes when restaurant is selected
      await fetchCounterQRs();
      // Fetch menu when editing a restaurant
      if (restaurant._id) {
        await fetchRestaurantMenu(restaurant._id);
      }
    } catch (err) {
      logger.error('Failed to fetch restaurant details', err);
      setError(err instanceof Error ? err.message : 'Failed to load restaurant details');
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurantUsers = async (restaurantId: string) => {
    try {
      setLoading(true);
      setError(null);
      const users = await superAdminAPI.getRestaurantUsers(restaurantId);
      logger.debug('Restaurant users fetched', { count: users.length, sample: users[0] });
      setRestaurantUsers(users);
    } catch (err) {
      logger.error('Failed to fetch restaurant users', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurantBranches = async (restaurantId: string) => {
    try {
      setLoading(true);
      setError(null);
      const branches = await superAdminAPI.getRestaurantBranches(restaurantId);
      setRestaurantBranches(branches);
    } catch (err) {
      logger.error('Failed to fetch restaurant branches', err);
      setError(err instanceof Error ? err.message : 'Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async (page?: number) => {
    try {
      setLoading(true);
      setError(null);
      const pageToUse = page !== undefined ? page : usersPage;
      const response = await superAdminAPI.getAllUsers({ page: pageToUse, limit: 50 });
      setAllUsers(response.data || []);
      setUsersPagination(response.pagination);
      if (page !== undefined) {
        setUsersPage(page);
      }
    } catch (err) {
      logger.error('Failed to fetch all users', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBranches = async (page?: number) => {
    try {
      setLoading(true);
      setError(null);
      const pageToUse = page !== undefined ? page : branchesPage;
      const response = await superAdminAPI.getAllBranches({ page: pageToUse, limit: 50 });
      setAllBranches(response.data || []);
      setBranchesPagination(response.pagination);
      if (page !== undefined) {
        setBranchesPage(page);
      }
    } catch (err) {
      logger.error('Failed to fetch all branches', err);
      setError(err instanceof Error ? err.message : 'Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  const fetchTables = async (restaurantId: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await superAdminAPI.getTables(restaurantId);
      setTables(data);
    } catch (err) {
      logger.error('Failed to fetch tables', err);
      setError(err instanceof Error ? err.message : 'Failed to load tables');
    } finally {
      setLoading(false);
    }
  };


  const handleUpdateRestaurant = useCallback(async () => {
    if (!editingRestaurant) {
      showError('No restaurant data to save');
      return;
    }

    if (!editingRestaurant.name?.trim()) {
      showError('Restaurant name is required');
      return;
    }

    const requestKey = `updateRestaurant-${editingRestaurant._id || 'new'}`;
    if (pendingRequestsRef.current.has(requestKey)) {
      return;
    }

    try {
      pendingRequestsRef.current.add(requestKey);
      setSavingRestaurant(true);
      setError(null);
      
      const isCreating = !editingRestaurant._id || editingRestaurant._id === '';
      
      let updated;
      if (isCreating) {
        updated = await superAdminAPI.createRestaurant({
          name: editingRestaurant.name.trim(),
          plan: editingRestaurant.plan || 'SILVER',
          logoUrl: editingRestaurant.logoUrl || undefined,
          packageStartDate: editingRestaurant.packageStartDate || undefined,
          packageEndDate: editingRestaurant.packageEndDate || undefined,
          features: editingRestaurant.features || {},
          appAccess: editingRestaurant.appAccess || {},
          gstNumber: editingRestaurant.gstNumber || undefined,
          contactPhone: editingRestaurant.contactPhone || undefined,
          contactEmail: editingRestaurant.contactEmail || undefined,
          address: editingRestaurant.address || undefined,
          city: editingRestaurant.city || undefined,
          state: editingRestaurant.state || undefined,
          pincode: editingRestaurant.pincode || undefined,
          website: editingRestaurant.website || undefined,
          description: editingRestaurant.description || undefined,
          ownerName: editingRestaurant.ownerName || undefined,
          registrationNumber: editingRestaurant.registrationNumber || undefined,
        } as any);
      } else {
        updated = await superAdminAPI.updateRestaurant(editingRestaurant._id, {
          name: editingRestaurant.name.trim(),
          plan: editingRestaurant.plan || 'SILVER',
          logoUrl: editingRestaurant.logoUrl || null,
          packageStartDate: editingRestaurant.packageStartDate || null,
          packageEndDate: editingRestaurant.packageEndDate || null,
          features: editingRestaurant.features || {},
          appAccess: editingRestaurant.appAccess || {},
          paymentMethods: editingRestaurant.paymentMethods || {},
          isCafe: editingRestaurant.isCafe ?? false,
          isActive: editingRestaurant.isActive ?? true,
          gstNumber: editingRestaurant.gstNumber || null,
          contactPhone: editingRestaurant.contactPhone || null,
          contactEmail: editingRestaurant.contactEmail || null,
          address: editingRestaurant.address || null,
          city: editingRestaurant.city || null,
          state: editingRestaurant.state || null,
          pincode: editingRestaurant.pincode || null,
          website: editingRestaurant.website || null,
          description: editingRestaurant.description || null,
          ownerName: editingRestaurant.ownerName || null,
          registrationNumber: editingRestaurant.registrationNumber || null,
        } as any);
      }
      
      if (updated) {
        setEditingRestaurant(updated);
        setSelectedRestaurant(updated);
        await fetchRestaurants();
        await fetchStats();
        showSuccess(isCreating ? 'Restaurant created successfully!' : 'Restaurant updated successfully!');
      }
    } catch (err) {
      logger.error('Failed to save restaurant', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save restaurant';
      showError(errorMessage);
    } finally {
      setSavingRestaurant(false);
      pendingRequestsRef.current.delete(requestKey);
    }
  }, [editingRestaurant, fetchRestaurants, fetchStats, showSuccess, showError]);

  const handleCreateUser = async () => {
    if (!selectedRestaurant) {
      setError('Please select a restaurant first');
      return;
    }

    if (!newUserName.trim() || !newUserEmail.trim() || !newUserPassword.trim()) {
      setError('Name, email, and password are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.createRestaurantUser(selectedRestaurant._id, {
        name: newUserName,
        email: newUserEmail,
        password: newUserPassword,
        role: newUserRole,
        branchId: newUserBranchId || undefined,
      });
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('WAITER');
      setNewUserBranchId('');
      setShowCreateUser(false);
      setCurrentView('users'); // Navigate back to users view
      await fetchRestaurantUsers(selectedRestaurant._id);
    } catch (err) {
      logger.error('Failed to create user', err);
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserStatus = async (userId: string, status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED') => {
    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.updateUserStatus(userId, status);
      if (selectedRestaurant) {
        await fetchRestaurantUsers(selectedRestaurant._id);
      } else {
        await fetchAllUsers();
      }
    } catch (err) {
      logger.error('Failed to update user status', err);
      setError(err instanceof Error ? err.message : 'Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAllLoyaltyPoints = async () => {
    setResetLoyaltyConfirm(true);
  };

  const confirmResetLoyaltyPoints = async () => {
    setResetLoyaltyConfirm(false);
    try {
      setResettingLoyalty(true);
      setError(null);
      const result = await superAdminAPI.resetAllLoyaltyPoints();
      showSuccess(`Reset loyalty points for ${result.data.customersModified} customers. Legacy records cleaned: ${result.data.legacyRecordsCleaned}`);
      setShowResetLoyaltyModal(false);
      // Refresh stats to reflect changes
      await fetchStats();
    } catch (err) {
      logger.error('Failed to reset loyalty points', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset loyalty points';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setResettingLoyalty(false);
    }
  };

  const handleChangeUserPassword = async () => {
    if (!selectedUserForPasswordChange) return;
    
    setPasswordChangeError(null);
    
    if (!newUserPasswordForChange || !confirmUserPasswordForChange) {
      setPasswordChangeError('All fields are required');
      return;
    }
    
    if (newUserPasswordForChange !== confirmUserPasswordForChange) {
      setPasswordChangeError('Passwords do not match');
      return;
    }
    
    if (newUserPasswordForChange.length < 6) {
      setPasswordChangeError('Password must be at least 6 characters');
      return;
    }
    
    try {
      setChangingUserPassword(true);
      // Use backend API to reset user password
      await superAdminAPI.resetUserPassword(selectedUserForPasswordChange._id, newUserPasswordForChange);
      
      // Success - close modal and reset
      setShowPasswordChangeModal(false);
      setSelectedUserForPasswordChange(null);
      setNewUserPasswordForChange('');
      setConfirmUserPasswordForChange('');
      setPasswordChangeError(null);
      
      // Show success message
      showSuccess(`Password reset successfully for ${selectedUserForPasswordChange.name}`);
      
      if (selectedRestaurant) {
        await fetchRestaurantUsers(selectedRestaurant._id);
      }
    } catch (err) {
      logger.error('Failed to reset user password', err);
      setPasswordChangeError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setChangingUserPassword(false);
    }
  };

  const handleCreateBranch = async () => {
    if (!selectedRestaurant) {
      setError('Please select a restaurant first');
      return;
    }

    if (!newBranchName.trim() || !newBranchAddress.trim() || !newBranchPhone.trim()) {
      setError('Name, address, and phone are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.createBranch(selectedRestaurant._id, {
        name: newBranchName,
        address: newBranchAddress,
        phone: newBranchPhone,
        email: newBranchEmail || undefined,
      });
      setNewBranchName('');
      setNewBranchAddress('');
      setNewBranchPhone('');
      setNewBranchEmail('');
      setCurrentView('branches');
      await fetchRestaurantBranches(selectedRestaurant._id);
    } catch (err) {
      logger.error('Failed to create branch', err);
      setError(err instanceof Error ? err.message : 'Failed to create branch');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBranch = async (branchId: string, isActive: boolean) => {
    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.updateBranch(branchId, { isActive });
      if (selectedRestaurant) {
        await fetchRestaurantBranches(selectedRestaurant._id);
      } else {
        await fetchAllBranches();
      }
    } catch (err) {
      logger.error('Failed to update branch', err);
      setError(err instanceof Error ? err.message : 'Failed to update branch');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTableQR = async () => {
    if (!selectedRestaurant) {
      setError('Please select a restaurant first');
      return;
    }

    if (!newTableNumber.trim()) {
      setError('Table number is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.generateTableQR(selectedRestaurant._id, {
        tableNumber: newTableNumber,
        logoIndex: null,
      });
      setNewTableNumber('');
      setSelectedLogoIndexForTable(null);
      setShowQRGenerator(false);
      await fetchTables(selectedRestaurant._id);
    } catch (err) {
      logger.error('Failed to generate QR code:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenManageQR = async (table: Table) => {
    setSelectedTableForManage(table);
    setSelectedLogoIndexForManageQR(table.logoIndex ?? null);
    setTableMenuFilters(table.menuFilters || {
      vegFilter: 'all',
      categories: [],
      showOnlyAvailable: false,
    });
    
    // Fetch available categories from the restaurant menu
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer/menu?restaurantId=${selectedRestaurant?._id}`);
      const data = await response.json();
      if (data.success && data.data?.categories) {
        const categories = data.data.categories.map((cat: { name?: string; category?: string }) => cat.name || cat.category).filter(Boolean);
        setAvailableCategories(categories);
      }
    } catch (err) {
      logger.error('Failed to fetch categories:', err);
      setAvailableCategories([]);
    }
    
    setShowManageQRModal(true);
  };

  const handleUpdateTableMenuFilters = async () => {
    if (!selectedRestaurant || !selectedTableForManage) return;
    
    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.updateTableMenuFilters(
        selectedRestaurant._id,
        selectedTableForManage._id,
        tableMenuFilters,
        selectedLogoIndexForManageQR
      );
      await fetchTables(selectedRestaurant._id);
      setShowManageQRModal(false);
      setSelectedTableForManage(null);
      setSelectedLogoIndexForManageQR(null);
    } catch (err) {
      logger.error('Failed to update menu filters:', err);
      setError(err instanceof Error ? err.message : 'Failed to update menu filters');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCounterQR = async () => {
    if (!selectedRestaurant) {
      setError('Please select a restaurant first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await superAdminAPI.generateCounterQR(selectedRestaurant._id, null);
      
      // Fetch the full restaurant data to ensure we have the persisted counter QR code
      const fullRestaurant = await superAdminAPI.getRestaurant(selectedRestaurant._id);
      setSelectedRestaurant(fullRestaurant);
      setSelectedLogoIndexForCounter(null);
      setSuccessMessage('Counter QR code generated successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      // Refresh restaurants list to get updated counter QR code
      await fetchRestaurants();
      showSuccess('Counter QR code generated successfully!');
    } catch (err) {
      logger.error('Failed to generate counter QR:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate QR code';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = async (type: 'table' | 'counter', tableId?: string) => {
    if (!selectedRestaurant) return;

    try {
      setLoading(true);
      const blob = await superAdminAPI.downloadQRPDF(selectedRestaurant._id, type, tableId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-qr-${tableId || 'counter'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      logger.error('Failed to download QR:', err);
      setError(err instanceof Error ? err.message : 'Failed to download QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTableQR = (tableId: string, tableNumber: string) => {
    if (!selectedRestaurant) return;
    setDeleteTableQRConfirm({ isOpen: true, tableId, tableNumber });
  };

  const confirmDeleteTableQR = async () => {
    if (!selectedRestaurant || !deleteTableQRConfirm) return;
    const { tableId } = deleteTableQRConfirm;
    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.deleteTableQR(selectedRestaurant._id, tableId);
      showSuccess('QR code deleted successfully!');
      // Refresh tables list
      await fetchTables(selectedRestaurant._id);
      setDeleteTableQRConfirm(null);
    } catch (err) {
      logger.error('Failed to delete QR code:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete QR code';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCounterQR = () => {
    if (!selectedRestaurant) return;
    setDeleteCounterQRConfirm({ isOpen: true });
  };

  const confirmDeleteCounterQR = async () => {
    if (!selectedRestaurant) return;
    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.deleteCounterQR(selectedRestaurant._id);
      showSuccess('Counter QR code deleted successfully!');
      // Refresh restaurants list to update counter QR code
      await fetchRestaurants();
      // Fetch full restaurant details to ensure we have the latest data
      const updatedRestaurant = await superAdminAPI.getRestaurant(selectedRestaurant._id);
      setSelectedRestaurant(updatedRestaurant);
      setDeleteCounterQRConfirm(null);
    } catch (err) {
      logger.error('Failed to delete counter QR code:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete counter QR code';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch counter QR codes for selected restaurant
  const fetchCounterQRs = async () => {
    if (!selectedRestaurant) return;
    try {
      setLoading(true);
      setError(null);
      const qrs = await superAdminAPI.getCounterQRs(selectedRestaurant._id);
      setCounterQRs(qrs);
    } catch (err) {
      logger.error('Failed to fetch counter QR codes:', err);
      setError(err instanceof Error ? err.message : 'Failed to load counter QR codes');
    } finally {
      setLoading(false);
    }
  };

  // Create new counter QR code
  const handleCreateCounterQR = async () => {
    if (!selectedRestaurant) {
      setError('Please select a restaurant first');
      return;
    }
    if (!newCounterQRName.trim()) {
      setError('Counter QR name is required');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.createCounterQR(selectedRestaurant._id, {
        name: newCounterQRName.trim(),
        description: newCounterQRDescription.trim() || undefined,
        branchId: newCounterQRBranchId || undefined,
      });
      setNewCounterQRName('');
      setNewCounterQRDescription('');
      setNewCounterQRBranchId('');
      setShowCounterQRForm(false);
      await fetchCounterQRs();
      showSuccess('Counter QR code created successfully!');
    } catch (err) {
      logger.error('Failed to create counter QR code:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create counter QR code';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Update counter QR code
  const handleUpdateCounterQR = async () => {
    if (!selectedRestaurant || !editingCounterQR) return;
    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.updateCounterQR(selectedRestaurant._id, editingCounterQR._id, {
        name: editingCounterQR.name,
        description: editingCounterQR.description || undefined,
        branchId: editingCounterQR.branchId || null,
        isActive: editingCounterQR.isActive,
      });
      setEditingCounterQR(null);
      await fetchCounterQRs();
      showSuccess('Counter QR code updated successfully!');
    } catch (err) {
      logger.error('Failed to update counter QR code:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update counter QR code';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Delete counter QR code by ID
  const handleDeleteCounterQRById = (counterQRId: string, name: string) => {
    if (!selectedRestaurant) return;
    setDeleteCounterQRConfirm({ isOpen: true, counterQRId, name });
  };

  const confirmDeleteCounterQRById = async () => {
    if (!selectedRestaurant || !deleteCounterQRConfirm || !deleteCounterQRConfirm.counterQRId) return;
    const { counterQRId } = deleteCounterQRConfirm;
    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.deleteCounterQRById(selectedRestaurant._id, counterQRId);
      await fetchCounterQRs();
      showSuccess('Counter QR code deleted successfully!');
      setDeleteCounterQRConfirm(null);
    } catch (err) {
      logger.error('Failed to delete counter QR code:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete counter QR code';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Regenerate counter QR code
  const handleRegenerateCounterQR = async (counterQRId: string) => {
    if (!selectedRestaurant) return;
    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.regenerateCounterQR(selectedRestaurant._id, counterQRId);
      await fetchCounterQRs();
      showSuccess('Counter QR code regenerated successfully!');
    } catch (err) {
      logger.error('Failed to regenerate counter QR code:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to regenerate counter QR code';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Open manage menu filters for counter QR
  const handleOpenManageCounterQR = async (counterQR: CounterQR) => {
    setSelectedCounterQRForManage(counterQR);
    setSelectedLogoIndexForManageCounterQR((counterQR as any).logoIndex ?? null);
    setCounterQRMenuFilters(counterQR.menuFilters || {
      vegFilter: 'all',
      categories: [],
      showOnlyAvailable: false,
    });
    
    // Fetch available categories from the restaurant menu
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer/menu?restaurantId=${selectedRestaurant?._id}`);
      const data = await response.json();
      if (data.success && data.data?.categories) {
        const categories = data.data.categories.map((cat: { name?: string; category?: string }) => cat.name || cat.category).filter(Boolean);
        setAvailableCategories(categories);
      }
    } catch (err) {
      logger.error('Failed to fetch categories:', err);
      setAvailableCategories([]);
    }
    
    setShowManageCounterQRModal(true);
  };

  // Update counter QR menu filters
  const handleUpdateCounterQRMenuFilters = async () => {
    if (!selectedRestaurant || !selectedCounterQRForManage) return;
    
    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.updateCounterQRMenuFilters(
        selectedRestaurant._id,
        selectedCounterQRForManage._id,
        counterQRMenuFilters,
        selectedLogoIndexForManageCounterQR
      );
      await fetchCounterQRs();
      setShowManageCounterQRModal(false);
      setSelectedCounterQRForManage(null);
      setSelectedLogoIndexForManageCounterQR(null);
      showSuccess('Menu filters updated successfully!');
    } catch (err) {
      logger.error('Failed to update counter QR menu filters:', err);
      setError(err instanceof Error ? err.message : 'Failed to update menu filters');
    } finally {
      setLoading(false);
    }
  };

  // Menu management functions
  const fetchRestaurantMenu = async (restaurantId: string) => {
    try {
      setLoading(true);
      const menu = await superAdminAPI.getRestaurantMenu(restaurantId);
      
      // Log menu data to debug serial number issues
      logger.debug('Fetched menu data', { 
        restaurantId, 
        categoryCount: menu.length,
        categoriesWithSerial: menu.filter(cat => cat.serialNumber !== undefined && cat.serialNumber !== null).length,
        serialNumbers: menu.map(cat => ({ category: cat.category, serialNumber: cat.serialNumber }))
      });
      
      // Sort categories by serial number (lower numbers first)
      // Categories without serial numbers (null/undefined) appear last
      const sortedMenu = [...menu].sort((a, b) => {
        const aSerial = (a.serialNumber !== undefined && a.serialNumber !== null) 
          ? a.serialNumber 
          : Number.MAX_SAFE_INTEGER;
        const bSerial = (b.serialNumber !== undefined && b.serialNumber !== null) 
          ? b.serialNumber 
          : Number.MAX_SAFE_INTEGER;
        return aSerial - bSerial;
      });
      
      // Ensure serial numbers are preserved in state
      setRestaurantMenu(sortedMenu);
      
      logger.debug('Menu state updated', { 
        menuLength: sortedMenu.length,
        serialNumbersInState: sortedMenu.map(cat => ({ category: cat.category, serialNumber: cat.serialNumber }))
      });
    } catch (err) {
      logger.error('Failed to fetch menu', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch menu');
      showError(err instanceof Error ? err.message : 'Failed to fetch menu');
    } finally {
      setLoading(false);
    }
  };

  // Optimized menu save with background saving and optimistic updates
  const handleSaveMenu = useCallback(async () => {
    const restaurant = selectedRestaurant;
    if (!restaurant) {
      showError('No restaurant selected');
      return;
    }
    if (!newMenuCategory.trim()) {
      showError('Category name is required');
      return;
    }
    const validItems = newMenuItems.filter(item => item.name?.trim() && item.price > 0);
    if (validItems.length === 0) {
      showError('Please add at least one valid menu item with name and price');
      return;
    }

    const requestKey = editingMenuCategory?._id || `new-${Date.now()}`;
    if (pendingRequestsRef.current.has(`saveMenu-${requestKey}`)) {
      return;
    }

    try {
      pendingRequestsRef.current.add(`saveMenu-${requestKey}`);
      setSavingMenu(true);
      setError(null);
      
      // Optimistic update - update UI immediately
      const optimisticCategory: MenuCategory = editingMenuCategory ? {
        ...editingMenuCategory,
        category: newMenuCategory,
        items: validItems,
        addOns: categoryAddOns,
        serialNumber: newMenuCategorySerialNumber,
      } : {
        _id: `temp-${Date.now()}`,
        restaurantId: restaurant._id,
        category: newMenuCategory,
        items: validItems,
        addOns: categoryAddOns,
        isActive: true,
        serialNumber: newMenuCategorySerialNumber,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Update local state optimistically
      if (editingMenuCategory) {
        setRestaurantMenu(prev => prev.map(cat => 
          cat._id === editingMenuCategory._id ? optimisticCategory : cat
        ));
      } else {
        setRestaurantMenu(prev => [...prev, optimisticCategory]);
      }

      // Save in background
      if (editingMenuCategory) {
        await superAdminAPI.updateRestaurantMenuCategory(
          restaurant._id, 
          editingMenuCategory._id, 
          newMenuCategory, 
          validItems, 
          categoryAddOns, 
          newMenuCategorySerialNumber
        );
        showSuccess('Menu category updated successfully!');
      } else {
        await superAdminAPI.upsertRestaurantMenu(restaurant._id, {
          category: newMenuCategory,
          items: validItems,
          addOns: categoryAddOns,
          serialNumber: newMenuCategorySerialNumber,
        });
        showSuccess('Menu category created successfully!');
      }
      
      // Refresh to get server state (with IDs)
      await fetchRestaurantMenu(restaurant._id);
      
      // Reset form
      setNewMenuCategory('');
      setNewMenuCategorySerialNumber(undefined);
      setNewMenuItems([]);
      setCategoryAddOns([]);
      setShowMenuForm(false);
      setEditingMenuCategory(null);
    } catch (err) {
      logger.error('Failed to save menu:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save menu';
      showError(errorMessage);
      // Revert optimistic update on error
      await fetchRestaurantMenu(restaurant._id);
    } finally {
      setSavingMenu(false);
      pendingRequestsRef.current.delete(`saveMenu-${requestKey}`);
    }
  }, [selectedRestaurant, newMenuCategory, newMenuItems, categoryAddOns, newMenuCategorySerialNumber, editingMenuCategory, fetchRestaurantMenu, showSuccess, showError]);

  const handleDeleteMenuCategory = async (categoryId: string) => {
    const restaurant = editingRestaurant || selectedRestaurant;
    if (!restaurant) return;
    setDeleteMenuCategoryConfirm({ isOpen: true, categoryId });
  };

  const confirmDeleteMenuCategory = useCallback(async () => {
    const restaurant = editingRestaurant || selectedRestaurant;
    if (!restaurant || !deleteMenuCategoryConfirm?.categoryId) return;

    try {
      setLoading(true);
      setError(null);
      await superAdminAPI.deleteRestaurantMenuCategory(restaurant._id, deleteMenuCategoryConfirm.categoryId);
      await fetchRestaurantMenu(restaurant._id);
      showSuccess('Menu category deleted successfully!');
      setDeleteMenuCategoryConfirm({ isOpen: false, categoryId: '' });
    } catch (err) {
      logger.error('Failed to delete menu category:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete menu category';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [editingRestaurant, selectedRestaurant, deleteMenuCategoryConfirm, fetchRestaurantMenu, showSuccess, showError]);

  const handleToggleCategorySelection = (categoryId: string) => {
    setSelectedCategoriesForMerge((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleSelectAllCategories = () => {
    const filteredCategories = restaurantMenu.filter((category) => 
      category.category.toLowerCase().includes(categorySearchTerm.toLowerCase())
    );
    if (selectedCategoriesForMerge.size === filteredCategories.length) {
      setSelectedCategoriesForMerge(new Set());
    } else {
      setSelectedCategoriesForMerge(new Set(filteredCategories.map(c => c._id)));
    }
  };

  const handleMergeCategories = async () => {
    const restaurant = editingRestaurant || selectedRestaurant;
    if (!restaurant) return;

    if (selectedCategoriesForMerge.size < 2) {
      showWarning('Please select at least 2 categories to merge');
      return;
    }

    if (!mergeCategoryName.trim()) {
      showError('Please enter a name for the merged category');
      return;
    }

    try {
      setMergingCategories(true);
      setError(null);

      // Get selected category IDs
      const categoryIds = Array.from(selectedCategoriesForMerge);

      // Use the new atomic merge endpoint
      const result = await superAdminAPI.mergeMenuCategories(
        restaurant._id,
        categoryIds,
        mergeCategoryName.trim(),
        mergeTargetCategoryId || undefined
      );

      // Refresh menu
      await fetchRestaurantMenu(restaurant._id);

      // Reset merge state
      setSelectedCategoriesForMerge(new Set());
      setShowMergeModal(false);
      setMergeCategoryName('');
      setMergeTargetCategoryId(null);

      showSuccess(
        `Successfully merged ${categoryIds.length} categories into "${mergeCategoryName.trim()}"! ` +
        `(${result.mergedItemCount} items, ${result.mergedAddOnCount} add-ons)`
      );
    } catch (err) {
      logger.error('Failed to merge categories:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to merge categories';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setMergingCategories(false);
    }
  };

  const handleEditMenuCategory = (category: MenuCategory) => {
    setEditingMenuCategory(category);
    setNewMenuCategory(category.category);
    // Handle both null and undefined for serial number
    setNewMenuCategorySerialNumber(
      category.serialNumber !== undefined && category.serialNumber !== null 
        ? category.serialNumber 
        : undefined
    );
    setNewMenuItems(category.items || []);
    setCategoryAddOns(category.addOns || []);
    setShowMenuForm(true);
  };

  const handleDuplicateMenuCategory = (category: MenuCategory) => {
    // Deep clone the category data
    const duplicatedItems = (category.items || []).map(item => ({
      ...item,
      addOns: item.addOns ? item.addOns.map(addOn => ({ ...addOn })) : [],
    }));
    const duplicatedAddOns = (category.addOns || []).map(addOn => ({ ...addOn }));

    // Set the form with duplicated data, with "Copy of" prefix
    setEditingMenuCategory(null);
    setNewMenuCategory(`Copy of ${category.category}`);
    setNewMenuCategorySerialNumber(undefined); // Don't copy serial number
    setNewMenuItems(duplicatedItems);
    setCategoryAddOns(duplicatedAddOns);
    setShowMenuForm(true);
  };

  // Quick update serial number
  const handleQuickUpdateSerialNumber = async (categoryId: string, newSerialNumber: number | undefined) => {
    const restaurant = editingRestaurant || selectedRestaurant;
    if (!restaurant) {
      showError('No restaurant selected');
      return;
    }

    try {
      setLoading(true);
      const category = restaurantMenu.find(c => c._id === categoryId);
      if (!category) {
        showError('Category not found');
        return;
      }

      // Ensure serialNumber is either a valid number >= 1 or undefined/null
      const serialToSave = (newSerialNumber !== undefined && newSerialNumber !== null && newSerialNumber >= 1) 
        ? newSerialNumber 
        : undefined;

      logger.debug('Updating serial number', { categoryId, oldSerial: category.serialNumber, newSerial: serialToSave });

      await superAdminAPI.updateRestaurantMenuCategory(
        restaurant._id,
        categoryId,
        category.category,
        category.items,
        category.addOns,
        serialToSave
      );
      
      // Refresh the menu to get updated data
      await fetchRestaurantMenu(restaurant._id);
      showSuccess('Serial number updated successfully!');
      setEditingSerialNumber(null);
      setTempSerialNumber(undefined);
    } catch (err) {
      logger.error('Failed to update serial number', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update serial number';
      showError(errorMessage);
      // Don't clear editing state on error so user can retry
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditSerialNumber = (category: MenuCategory) => {
    setEditingSerialNumber(category._id);
    // Handle both null and undefined for serial number
    setTempSerialNumber(
      category.serialNumber !== undefined && category.serialNumber !== null 
        ? category.serialNumber 
        : undefined
    );
  };

  const handleCancelEditSerialNumber = () => {
    setEditingSerialNumber(null);
    setTempSerialNumber(undefined);
  };

  const handleSaveSerialNumber = async (categoryId: string) => {
    // Validate the serial number before saving
    if (tempSerialNumber !== undefined && tempSerialNumber !== null) {
      if (tempSerialNumber < 1) {
        showError('Serial number must be at least 1');
        return;
      }
    }
    await handleQuickUpdateSerialNumber(categoryId, tempSerialNumber);
  };

  const handleIncrementSerialNumber = (categoryId: string) => {
    const currentSerial = tempSerialNumber ?? 0;
    setTempSerialNumber(currentSerial + 1);
  };

  const handleDecrementSerialNumber = (categoryId: string) => {
    const currentSerial = tempSerialNumber ?? 1;
    if (currentSerial > 1) {
      setTempSerialNumber(currentSerial - 1);
    }
  };

  // Move category up/down in the list
  const handleMoveCategoryUp = async (categoryId: string) => {
    const restaurant = editingRestaurant || selectedRestaurant;
    if (!restaurant) return;

    try {
      setLoading(true);
      const currentIndex = restaurantMenu.findIndex(c => c._id === categoryId);
      if (currentIndex <= 0) {
        showWarning('Category is already at the top');
        return;
      }

      const currentCategory = restaurantMenu[currentIndex];
      const previousCategory = restaurantMenu[currentIndex - 1];

      if (!currentCategory || !previousCategory) {
        showError('Category not found');
        return;
      }

      // Get serial numbers, assign if missing
      let currentSerial = currentCategory.serialNumber;
      let previousSerial = previousCategory.serialNumber;

      // If current category has no serial number (null or undefined), assign based on position
      if (currentSerial === undefined || currentSerial === null) {
        currentSerial = currentIndex + 1;
      }

      // If previous category has no serial number (null or undefined), assign based on position
      if (previousSerial === undefined || previousSerial === null) {
        previousSerial = currentIndex;
      }

      // Swap serial numbers
      await Promise.all([
        superAdminAPI.updateRestaurantMenuCategory(
          restaurant._id,
          currentCategory._id,
          currentCategory.category,
          currentCategory.items,
          currentCategory.addOns,
          previousSerial
        ),
        superAdminAPI.updateRestaurantMenuCategory(
          restaurant._id,
          previousCategory._id,
          previousCategory.category,
          previousCategory.items,
          previousCategory.addOns,
          currentSerial
        )
      ]);

      await fetchRestaurantMenu(restaurant._id);
      showSuccess('Category moved up successfully!');
    } catch (err) {
      logger.error('Failed to move category up', err);
      showError(err instanceof Error ? err.message : 'Failed to move category');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveCategoryDown = async (categoryId: string) => {
    const restaurant = editingRestaurant || selectedRestaurant;
    if (!restaurant) return;

    try {
      setLoading(true);
      const currentIndex = restaurantMenu.findIndex(c => c._id === categoryId);
      if (currentIndex < 0 || currentIndex >= restaurantMenu.length - 1) {
        showWarning('Category is already at the bottom');
        return;
      }

      const currentCategory = restaurantMenu[currentIndex];
      const nextCategory = restaurantMenu[currentIndex + 1];

      if (!currentCategory || !nextCategory) {
        showError('Category not found');
        return;
      }

      // Get serial numbers, assign if missing
      let currentSerial = currentCategory.serialNumber;
      let nextSerial = nextCategory.serialNumber;

      // If current category has no serial number, assign based on position
      if (currentSerial === undefined) {
        currentSerial = currentIndex + 1;
      }

      // If next category has no serial number, assign based on position
      if (nextSerial === undefined) {
        nextSerial = currentIndex + 2;
      }

      // Swap serial numbers
      await Promise.all([
        superAdminAPI.updateRestaurantMenuCategory(
          restaurant._id,
          currentCategory._id,
          currentCategory.category,
          currentCategory.items,
          currentCategory.addOns,
          nextSerial
        ),
        superAdminAPI.updateRestaurantMenuCategory(
          restaurant._id,
          nextCategory._id,
          nextCategory.category,
          nextCategory.items,
          nextCategory.addOns,
          currentSerial
        )
      ]);

      await fetchRestaurantMenu(restaurant._id);
      showSuccess('Category moved down successfully!');
    } catch (err) {
      logger.error('Failed to move category down', err);
      showError(err instanceof Error ? err.message : 'Failed to move category');
    } finally {
      setLoading(false);
    }
  };

  const handlePDFUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      showError('Please upload a PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showError('PDF file size must be less than 10MB');
      return;
    }

    const restaurant = selectedRestaurant;
    if (!restaurant) {
      showError('No restaurant selected');
      return;
    }

    try {
      setUploadingPDF(true);
      setError(null);
      const extractedData = await superAdminAPI.uploadPDFMenu(restaurant._id, file);
      logger.debug('[PDF Upload] Extracted data', { length: extractedData?.length, data: extractedData });
      
      // Ensure extractedData is an array
      const dataArray = Array.isArray(extractedData) ? extractedData : [];
      
      if (dataArray.length === 0) {
        showError('No menu items could be extracted from the PDF. Please check the PDF format and try again.');
        return;
      }
      
      setExtractedMenuData(dataArray);
      setShowPDFUploadModal(true);
      showSuccess(`PDF parsed successfully! Found ${dataArray.length} categories with ${dataArray.reduce((sum, cat) => sum + (cat.items?.length || 0), 0)} items. Please review and confirm.`);
    } catch (err) {
      logger.error('Failed to parse PDF:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to parse PDF menu';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setUploadingPDF(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleConfirmExtractedMenu = async () => {
    const restaurant = selectedRestaurant;
    if (!restaurant) {
      showError('No restaurant selected');
      return;
    }

    if (extractedMenuData.length === 0) {
      showError('No menu data to save');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Save each category
      for (const categoryData of extractedMenuData) {
        if (categoryData.category && categoryData.items && categoryData.items.length > 0) {
          await superAdminAPI.upsertRestaurantMenu(restaurant._id, {
            category: categoryData.category,
            items: categoryData.items,
          });
        }
      }

      await fetchRestaurantMenu(restaurant._id);
      setShowPDFUploadModal(false);
      setExtractedMenuData([]);
      showSuccess(`Successfully imported ${extractedMenuData.length} menu categories!`);
    } catch (err) {
      logger.error('Failed to save extracted menu:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save menu';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900 from-zinc-50 via-zinc-100 to-zinc-50 flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Navigation - Hidden on mobile, overlay on mobile when open */}
      <aside className={`fixed lg:sticky top-0 h-screen w-64 bg-gradient-to-b from-zinc-900 to-zinc-800 dark:from-zinc-900 dark:to-zinc-800 from-white to-zinc-50 border-r border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 flex flex-col z-50 flex-shrink-0 transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        {/* Logo/Header */}
        <div className="p-4 border-b border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20">
          <div className="flex flex-col items-center">
            <img 
              src={theme === 'dark' ? "/logowhite.png" : "/logowg.png"}
              alt="SwadSeva OS Logo" 
              className="w-full max-w-[120px] h-auto mb-2"
            />
            <div className="text-center w-full">
              <h1 className="text-lg font-serif text-amber-50 dark:text-amber-50 text-zinc-900">SwadSeva Super Admin</h1>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <button
            onClick={() => {
              setCurrentView('dashboard');
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium text-left ${
              currentView === 'dashboard'
                ? 'bg-gradient-to-r from-amber-600 to-amber-500 text-white shadow-lg shadow-amber-600/30'
                : 'text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-800/50 dark:hover:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 hover:text-amber-50 dark:hover:text-amber-50 hover:text-amber-600'
            }`}
          >
            <BarChart3 className="w-5 h-5" />
            <span>Dashboard</span>
          </button>

          <button
            onClick={() => {
              setCurrentView('restaurants');
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium text-left ${
              currentView === 'restaurants'
                ? 'bg-gradient-to-r from-amber-600 to-amber-500 text-white shadow-lg shadow-amber-600/30'
                : 'text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-800/50 dark:hover:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 hover:text-amber-50 dark:hover:text-amber-50 hover:text-amber-600'
            }`}
          >
            <Building2 className="w-5 h-5" />
            <span>Restaurants</span>
          </button>

          <button
            onClick={() => {
              setCurrentView('reports');
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium text-left ${
              currentView === 'reports'
                ? 'bg-gradient-to-r from-amber-600 to-amber-500 text-white shadow-lg shadow-amber-600/30'
                : 'text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-800/50 dark:hover:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 hover:text-amber-50 dark:hover:text-amber-50 hover:text-amber-600'
            }`}
          >
            <FileText className="w-5 h-5" />
            <span>Reports</span>
          </button>
        </nav>

        {/* API Health Check */}
        <div className="p-4 border-t border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20">
          {apiHealth && apiHealth.status !== 'disabled' && (
            <div className="mb-3 p-3 bg-zinc-800/50 dark:bg-zinc-800/50 bg-zinc-100 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 font-medium">API Status</span>
                <div className={`w-2 h-2 rounded-full ${
                  apiHealth.status === 'healthy' ? 'bg-green-500' :
                  apiHealth.status === 'unhealthy' ? 'bg-red-500' :
                  'bg-yellow-500 animate-pulse'
                }`} />
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 truncate" title={apiHealth.message}>
                {apiHealth.status === 'healthy' ? 'Connected' :
                 apiHealth.status === 'unhealthy' ? 'Disconnected' :
                 'Checking...'}
              </p>
              <button
                onClick={disableHealthCheck}
                className="mt-2 w-full text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 hover:text-zinc-400 dark:hover:text-zinc-400 hover:text-zinc-800 transition-colors"
              >
                Disable Check
              </button>
            </div>
          )}
          {apiHealth?.status === 'disabled' && (
            <button
              onClick={checkApiHealth}
              className="w-full mb-3 text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 hover:text-zinc-400 dark:hover:text-zinc-400 hover:text-zinc-800 transition-colors"
            >
              Enable API Check
            </button>
          )}
        </div>

        {/* Profile Section */}
        <div className="p-4 border-t border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20">
          <div className="relative profile-menu-container">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-zinc-800/50 dark:bg-zinc-800/50 bg-zinc-100 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:text-zinc-900 dark:hover:text-white transition-all font-medium"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-600 to-amber-500 flex items-center justify-center text-white font-semibold text-sm">
                {currentUser ? currentUser.name.charAt(0).toUpperCase() : 'SA'}
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium">{currentUser ? currentUser.name : 'Super Admin'}</p>
                <p className="text-xs opacity-70">Profile</p>
              </div>
              <Settings className="w-4 h-4" />
            </button>

            {/* Profile Menu Dropdown */}
            {showProfileMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-zinc-800 dark:bg-zinc-800 bg-white border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 rounded-xl shadow-xl overflow-hidden z-50">
                <div className="p-2">
                  {/* Profile Page */}
                  <button
                    onClick={() => {
                      setCurrentView('profile');
                      setShowProfileMenu(false);
                      setSidebarOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-zinc-700 dark:hover:bg-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-300 dark:text-zinc-300 text-zinc-700 dark:text-zinc-300 transition-all"
                  >
                    <UserCircle className="w-5 h-5 text-amber-500" />
                    <span className="font-medium">View Profile</span>
                  </button>

                  {/* Logout */}
                  <button
                    onClick={onLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-red-600/20 dark:hover:bg-red-600/20 hover:bg-red-50 text-red-400 dark:text-red-400 transition-all mt-1"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="font-medium">Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto w-full min-w-0">
        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-30 bg-gradient-to-b from-zinc-900 to-zinc-800 dark:from-zinc-900 dark:to-zinc-800 from-white to-zinc-50 border-b border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-zinc-800/50 dark:bg-zinc-800/50 bg-zinc-100 hover:bg-zinc-700 dark:hover:bg-zinc-700 hover:bg-zinc-200 text-amber-50 dark:text-amber-50 text-zinc-900 transition-colors"
          >
            <MenuIcon className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <img 
              src={theme === 'dark' ? "/logowhite.png" : "/logowg.png"}
              alt="SwadSeva OS Logo" 
              className="h-8 w-auto"
            />
            <h1 className="text-sm font-serif text-amber-50 dark:text-amber-50 text-zinc-900">Super Admin</h1>
          </div>
          <div className="w-10" /> {/* Spacer for centering */}
        </div>

        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-3 sm:py-4 lg:py-6 xl:py-8">
        {/* Success Notification */}
        {successMessage && (
          <div className="mb-6 animate-in slide-in-from-top-5 fade-in duration-300">
            <div className="p-4 bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/30 rounded-2xl shadow-xl">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                <p className="text-green-400 font-semibold">{successMessage}</p>
                <button
                  onClick={() => setSuccessMessage(null)}
                  className="ml-auto text-green-400/70 hover:text-green-400 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-6 bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/30 rounded-2xl shadow-xl animate-in slide-in-from-top-5 fade-in duration-300">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <p className="text-red-400 font-semibold">Error: {error}</p>
                </div>
                <div className="text-red-300/70 text-sm space-y-1">
                  {error.includes('connect to server') || error.includes('Network error') ? (
                    <>
                      {API_BASE_URL.startsWith('https://') ? (
                        <>
                          <p>• Backend server at <code className="bg-red-900/30 px-1 rounded">{API_BASE_URL}</code> is not accessible</p>
                          <p>• Check Railway dashboard to ensure server is running</p>
                          <p>• Verify CORS_ORIGIN in Railway includes your frontend URL: <code className="bg-red-900/30 px-1 rounded">{window.location.origin}</code></p>
                          <p>• Check browser console (F12) for detailed error messages</p>
                        </>
                      ) : (
                        <>
                          <p>• Make sure the backend server is running at <code className="bg-red-900/30 px-1 rounded">http://localhost:8080</code></p>
                          <p>• Check browser console (F12) for detailed error messages</p>
                          <p>• Verify CORS is configured in Server/.env</p>
                        </>
                      )}
                    </>
                  ) : error.includes('Unauthorized') ? (
                    <>
                      <p>• Your session may have expired</p>
                      <p>• Please login again</p>
                      <p>• Verify your user has SUPER_ADMIN role</p>
                    </>
                  ) : error.includes('CORS') ? (
                    <>
                      <p>• CORS error detected</p>
                      {API_BASE_URL.startsWith('https://') ? (
                        <>
                          <p>• Update Railway environment variable <code className="bg-red-900/30 px-1 rounded">CORS_ORIGIN</code> to include: <code className="bg-red-900/30 px-1 rounded">{window.location.origin}</code></p>
                          <p>• Railway will automatically redeploy after updating</p>
                          <p>• Current frontend URL: <code className="bg-red-900/30 px-1 rounded">{window.location.origin}</code></p>
                        </>
                      ) : (
                        <>
                          <p>• Update Server/.env: <code className="bg-red-900/30 px-1 rounded">CORS_ORIGIN=http://localhost:5173,http://localhost:5174,http://localhost:5175,http://localhost:5176</code></p>
                          <p>• Restart backend server after updating .env</p>
                        </>
                      )}
                    </>
                  ) : (
                    <p>Check browser console (F12) for more details</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setError(null)}
                  className="text-red-400/70 hover:text-red-400 transition-colors p-1"
                  aria-label="Dismiss error"
                >
                  <X className="w-4 h-4" />
                </button>
                <button
                  onClick={testConnection}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-lg hover:shadow-xl"
                >
                  {loading ? 'Testing...' : 'Test Connection'}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && !stats && (
          <div className="flex flex-col justify-center items-center py-12 space-y-4">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
            <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">Loading...</p>
          </div>
        )}

        {/* Dashboard View */}
        {currentView === 'dashboard' && stats && <DashboardView stats={stats} />}

        {/* Restaurants View */}
        {currentView === 'restaurants' && (
          <RestaurantsView
            restaurants={restaurants}
            pagination={restaurantPagination}
            restaurantPage={restaurantPage}
            restaurantFilterPlan={restaurantFilterPlan}
            restaurantFilterActive={restaurantFilterActive}
            onPageChange={setRestaurantPage}
            onFilterPlanChange={setRestaurantFilterPlan}
            onFilterActiveChange={setRestaurantFilterActive}
            onCreateNew={() => {
                  setSelectedRestaurant(null);
                  const silverDefaults = getPlanDefaults('SILVER');
                  setEditingRestaurant({
                    _id: '',
                    name: '',
                    plan: 'SILVER',
                    logoUrl: undefined,
                    isActive: true,
                    packageStartDate: null,
                    packageEndDate: null,
                    features: silverDefaults.features,
                    appAccess: silverDefaults.appAccess,
                    paymentMethods: {
                      payAtCounter: true,
                      upi1: true,
                      upi2: true,
                    },
                    createdAt: '',
                    updatedAt: '',
                  });
                  previousPlanRef.current = 'SILVER';
                  setCurrentView('edit-restaurant');
                }}
            onEdit={(restaurant) => {
                        setSelectedRestaurant(restaurant);
                        fetchRestaurantDetails(restaurant._id);
                        setCurrentView('edit-restaurant');
                      }}
            onViewUsers={(restaurant) => {
                        setSelectedRestaurant(restaurant);
                        setCurrentView('users');
                        fetchRestaurantUsers(restaurant._id);
                      }}
            onViewBranches={(restaurant) => {
                        setSelectedRestaurant(restaurant);
                        setCurrentView('branches');
                        fetchRestaurantBranches(restaurant._id);
                      }}
            onViewQRCodes={async (restaurant) => {
                        setSelectedRestaurant(restaurant);
                        setCurrentView('qr-codes');
                        try {
                          const fullRestaurant = await superAdminAPI.getRestaurant(restaurant._id);
                          setSelectedRestaurant(fullRestaurant);
                        } catch (err) {
                logger.error('Failed to fetch restaurant details', err);
                          setError(err instanceof Error ? err.message : 'Failed to load restaurant');
                        }
                      }}
            onTestPrint={async (restaurant) => {
                        setSelectedRestaurantForPrint(restaurant);
                        setShowTestPrintModal(true);
                        setLoadingOrders(true);
                        setError(null);
                        try {
                          const result = await superAdminAPI.getRestaurantOrders(restaurant._id, { limit: 50 });
                          setRestaurantOrders(result.data || []);
                        } catch (err) {
                logger.error('Failed to load orders', err);
                          setError(err instanceof Error ? err.message : 'Failed to load orders');
                        } finally {
                          setLoadingOrders(false);
                        }
                      }}
            onViewMenu={async (restaurant) => {
                        setSelectedRestaurant(restaurant);
                        setShowMenuForm(false);
                        setEditingMenuCategory(null);
                        setCategoryAddOns([]);
                        setNewMenuCategory('');
                        setNewMenuCategorySerialNumber(undefined);
                        setNewMenuItems([]);
                        try {
                          await fetchRestaurantMenu(restaurant._id);
                          setCurrentView('menu');
                        } catch (err) {
                logger.error('Failed to load menu', err);
                const errorMessage = err instanceof Error ? err.message : 'Failed to load menu';
                setError(errorMessage);
                showError(errorMessage);
              }
            }}
            onClearData={(restaurant) => {
                        setClearDataConfirm({ isOpen: true, restaurant });
                      }}
            onDelete={(restaurant) => {
                        setDeleteConfirm({ isOpen: true, restaurant });
                      }}
          />
        )}

        {/* Edit Restaurant Page */}
        {currentView === 'edit-restaurant' && editingRestaurant && (
          <div className="flex flex-col h-full max-h-[calc(100vh-8rem)]">
            <div className="flex-shrink-0 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-2xl sm:text-3xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">
                    {(!editingRestaurant._id || editingRestaurant._id === '') ? 'Create Restaurant' : 'Edit Restaurant'}
                  </h2>
                  <p className="text-sm sm:text-base text-zinc-400 dark:text-zinc-400 text-zinc-600">
                    {(!editingRestaurant._id || editingRestaurant._id === '') ? 'Add a new restaurant to the platform' : 'Manage restaurant settings and features'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setCurrentView('restaurants');
                    setEditingRestaurant(null);
                    setSelectedRestaurant(null);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-xl transition-all font-medium"
                >
                  <X className="w-5 h-5" />
                  Back
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto pr-2 -mr-2">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Basic Information */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-xl">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-6">Basic Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Restaurant Name</label>
                      <input
                        type="text"
                        value={editingRestaurant.name}
                        onChange={(e) => setEditingRestaurant({ ...editingRestaurant, name: e.target.value })}
                        className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Plan</label>
                      <select
                        value={editingRestaurant.plan}
                        onChange={(e) => setEditingRestaurant({ ...editingRestaurant, plan: e.target.value as 'SILVER' | 'GOLD' | 'PLATINUM' })}
                        className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                      >
                        <option value="SILVER">SILVER</option>
                        <option value="GOLD">GOLD</option>
                        <option value="PLATINUM">PLATINUM</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">Logos</label>
                      <div className="space-y-4">
                        {/* Display existing logos */}
                        {editingRestaurant.logos && editingRestaurant.logos.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 mb-2">Existing Logos:</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                              {editingRestaurant.logos.map((logo: RestaurantLogo, index: number) => {
                                const isHorizontal = logo.type === 'horizontal';
                                const isVertical = logo.type === 'vertical';
                                return (
                                  <div key={index} className="relative">
                                    <div className={`bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 rounded-xl border border-zinc-700 dark:border-zinc-700 border-zinc-300 p-2 ${
                                      isHorizontal ? 'h-16' : 
                                      isVertical ? 'w-20 h-auto max-h-48' : 
                                      'h-24'
                                    }`}>
                                      <img
                                        src={logo.url}
                                        alt={`Logo ${index + 1} (${logo.type || 'regular'})`}
                                        className={`${isHorizontal ? 'w-full h-full' : isVertical ? 'w-full h-auto' : 'w-full h-full'} object-contain`}
                                      />
                                    </div>
                                    <div className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-amber-600 text-white text-xs rounded">
                                      {logo.type || 'regular'}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* Logo preview or existing logoUrl */}
                        {(logoPreview || (editingRestaurant.logoUrl && !editingRestaurant.logos?.length)) && (
                          <div className="relative inline-block">
                            {logoPreview ? (
                              <img
                                src={logoPreview}
                                alt="Logo preview"
                                className={`object-contain bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 rounded-xl border border-zinc-700 dark:border-zinc-700 border-zinc-300 p-2 ${
                                  logoType === 'horizontal' ? 'h-16 w-auto' : 
                                  logoType === 'vertical' ? 'w-20 h-auto max-h-48' : 
                                  'w-32 h-32'
                                }`}
                              />
                            ) : (
                              <img
                                src={editingRestaurant.logoUrl?.startsWith('http://') || editingRestaurant.logoUrl?.startsWith('https://') 
                                  ? editingRestaurant.logoUrl 
                                  : `${API_BASE_URL}${editingRestaurant.logoUrl}`}
                                alt="Logo preview"
                                className="w-32 h-32 object-contain bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 rounded-xl border border-zinc-700 dark:border-zinc-700 border-zinc-300 p-2"
                              />
                            )}
                            <button
                              onClick={() => {
                                if (logoPreview) {
                                  setLogoFile(null);
                                  setLogoPreview(null);
                                } else {
                                  setEditingRestaurant({ ...editingRestaurant, logoUrl: undefined });
                                }
                              }}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 hover:bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        {/* Logo type selector */}
                        <div>
                          <label className="block text-xs font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Logo Type</label>
                          <select
                            value={logoType}
                            onChange={(e) => setLogoType(e.target.value as 'regular' | 'horizontal' | 'vertical')}
                            className="w-full px-3 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm"
                          >
                            <option value="regular">Regular (Square)</option>
                            <option value="horizontal">Horizontal</option>
                            <option value="vertical">Vertical (200x500)</option>
                          </select>
                        </div>
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-zinc-700 dark:border-zinc-700 border-zinc-300 border-dashed rounded-xl cursor-pointer bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 hover:bg-zinc-800 dark:hover:bg-zinc-800 hover:bg-zinc-200 transition-colors">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-8 h-8 text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-2" />
                            <p className="mb-2 text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600">
                              <span className="font-semibold">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600">PNG, JPG, JPEG or SVG (MAX. 5MB)</p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                // For SVG files, upload directly without cropping
                                if (file.type === 'image/svg+xml') {
                                  const reader = new FileReader();
                                  reader.onloadend = async () => {
                                    setLogoPreview(reader.result as string);
                                    try {
                                      setUploadingLogo(true);
                                      if (!editingRestaurant?._id) {
                                        throw new Error('Restaurant ID is required');
                                      }
                                      const result = await superAdminAPI.uploadLogo(file, editingRestaurant._id, logoType);
                                      
                                      // Update local state
                                      const updatedRestaurant = { ...editingRestaurant, logoUrl: result.url };
                                      setEditingRestaurant(updatedRestaurant);
                                      
                                      // Automatically save logoUrl to database
                                      try {
                                        await superAdminAPI.updateRestaurant(editingRestaurant._id, {
                                          logoUrl: result.url,
                                        });
                                        setSuccessMessage('Logo uploaded and saved successfully!');
                                        setTimeout(() => setSuccessMessage(null), 3000);
                                        // Refresh restaurant list to show updated logo
                                        await fetchRestaurants();
                                      } catch (saveError) {
                                        // Don't throw - the upload was successful, just the save failed
                                        // User can still save manually later
                                        setError('Logo uploaded but failed to save. Please save the restaurant manually.');
                                      }
                                    } catch (err) {
                                      logger.error('Failed to upload logo:', err);
                                      setError(err instanceof Error ? err.message : 'Failed to upload logo');
                                      setLogoFile(null);
                                      setLogoPreview(null);
                                    } finally {
                                      setUploadingLogo(false);
                                    }
                                  };
                                  reader.readAsDataURL(file);
                                } else {
                                  // For other image types, show crop modal
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    const imageSrc = reader.result as string;
                                    setImageToCrop(imageSrc);
                                    setShowCropModal(true);
                                  };
                                  reader.readAsDataURL(file);
                                }
                                setLogoFile(file);
                              }
                            }}
                          />
                        </label>
                        {uploadingLogo && (
                          <div className="flex items-center gap-2 text-sm text-amber-400">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Uploading...
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Package Start Date</label>
                      <input
                        type="datetime-local"
                        value={editingRestaurant.packageStartDate ? new Date(editingRestaurant.packageStartDate).toISOString().slice(0, 16) : ''}
                        onChange={(e) => {
                          const date = e.target.value ? new Date(e.target.value).toISOString() : null;
                          setEditingRestaurant({ ...editingRestaurant, packageStartDate: date });
                        }}
                        className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                      />
                      <p className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 mt-1">When the package subscription starts</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Package End Date</label>
                      <input
                        type="datetime-local"
                        value={editingRestaurant.packageEndDate ? new Date(editingRestaurant.packageEndDate).toISOString().slice(0, 16) : ''}
                        onChange={(e) => {
                          const date = e.target.value ? new Date(e.target.value).toISOString() : null;
                          setEditingRestaurant({ ...editingRestaurant, packageEndDate: date });
                        }}
                        className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                      />
                      <p className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 mt-1">Restaurant will be automatically disabled after this date</p>
                      {editingRestaurant.packageEndDate && new Date(editingRestaurant.packageEndDate) < new Date() && (
                        <p className="text-xs text-red-400 dark:text-red-400 text-red-700 mt-1">⚠️ This date has passed. Restaurant will be disabled.</p>
                      )}
                    </div>
                    <div className="flex items-center justify-between p-4 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl">
                      <label className="text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700">Restaurant is Active</label>
                      <button
                        onClick={() => setEditingRestaurant({ ...editingRestaurant, isActive: !editingRestaurant.isActive })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          editingRestaurant.isActive ? 'bg-amber-600' : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            editingRestaurant.isActive ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Business & Contact Information */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-xl">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-6">Business & Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Contact Phone</label>
                      <input
                        type="tel"
                        value={editingRestaurant.contactPhone || ''}
                        onChange={(e) => setEditingRestaurant({ ...editingRestaurant, contactPhone: e.target.value })}
                        placeholder="Enter contact phone"
                        className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Contact Email</label>
                      <input
                        type="email"
                        value={editingRestaurant.contactEmail || ''}
                        onChange={(e) => setEditingRestaurant({ ...editingRestaurant, contactEmail: e.target.value })}
                        placeholder="Enter contact email"
                        className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">GST Number</label>
                      <input
                        type="text"
                        value={editingRestaurant.gstNumber || ''}
                        onChange={(e) => setEditingRestaurant({ ...editingRestaurant, gstNumber: e.target.value })}
                        placeholder="Enter GST number"
                        className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Website</label>
                      <input
                        type="url"
                        value={editingRestaurant.website || ''}
                        onChange={(e) => setEditingRestaurant({ ...editingRestaurant, website: e.target.value })}
                        placeholder="https://example.com"
                        className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Owner Name</label>
                      <input
                        type="text"
                        value={editingRestaurant.ownerName || ''}
                        onChange={(e) => setEditingRestaurant({ ...editingRestaurant, ownerName: e.target.value })}
                        placeholder="Enter owner name"
                        className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Registration Number</label>
                      <input
                        type="text"
                        value={editingRestaurant.registrationNumber || ''}
                        onChange={(e) => setEditingRestaurant({ ...editingRestaurant, registrationNumber: e.target.value })}
                        placeholder="Enter registration number"
                        className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Description</label>
                    <textarea
                      value={editingRestaurant.description || ''}
                      onChange={(e) => setEditingRestaurant({ ...editingRestaurant, description: e.target.value })}
                      placeholder="Enter restaurant description"
                      rows={3}
                      className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all resize-none"
                    />
                  </div>
                </div>

                {/* Address Information */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-xl">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-6">Address Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Address</label>
                      <textarea
                        value={editingRestaurant.address || ''}
                        onChange={(e) => setEditingRestaurant({ ...editingRestaurant, address: e.target.value })}
                        placeholder="Enter full address"
                        rows={2}
                        className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all resize-none"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">City</label>
                        <input
                          type="text"
                          value={editingRestaurant.city || ''}
                          onChange={(e) => setEditingRestaurant({ ...editingRestaurant, city: e.target.value })}
                          placeholder="Enter city"
                          className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">State</label>
                        <input
                          type="text"
                          value={editingRestaurant.state || ''}
                          onChange={(e) => setEditingRestaurant({ ...editingRestaurant, state: e.target.value })}
                          placeholder="Enter state"
                          className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Pincode</label>
                      <input
                        type="text"
                        value={editingRestaurant.pincode || ''}
                        onChange={(e) => setEditingRestaurant({ ...editingRestaurant, pincode: e.target.value })}
                        placeholder="Enter pincode"
                        className="w-full px-4 py-3 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Features Toggle */}
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-xl">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-6">Features</h3>
                  <div className="space-y-4">
                    {[
                      { key: 'dineIn', label: 'Dine-In Orders', icon: '🪑', description: 'Enable dine-in order type' },
                      { key: 'takeaway', label: 'Takeaway Orders', icon: '🥡', description: 'Enable takeaway order type' },
                      { key: 'delivery', label: 'Delivery Orders', icon: '🚚', description: 'Enable delivery order type' },
                      { key: 'reservations', label: 'Reservations', icon: '📅', description: 'Enable table reservation system' },
                      { key: 'qrCodes', label: 'QR Codes', icon: '📱', description: 'Enable QR code menu system' },
                      { key: 'reports', label: 'Reports & Analytics', icon: '📊', description: 'Enable advanced reports and analytics' },
                      { key: 'loyalty', label: 'Loyalty Points System', icon: '🎁', description: 'Enable loyalty points and rewards' },
                      { key: 'showSerialNumber', label: 'Show Serial Numbers', icon: '🔢', description: 'Display serial numbers on menu items in QR menu' },
                    ].map((feature) => (
                      <div key={feature.key} className="flex items-center justify-between p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200 transition-all">
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-xl">{feature.icon}</span>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 block">{feature.label}</span>
                            {feature.description && (
                              <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mt-0.5">{feature.description}</p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const currentFeatures = editingRestaurant.features || {};
                            setEditingRestaurant({
                              ...editingRestaurant,
                              features: {
                                ...currentFeatures,
                                [feature.key]: !currentFeatures[feature.key as keyof typeof currentFeatures],
                              },
                            });
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            editingRestaurant.features?.[feature.key as keyof typeof editingRestaurant.features]
                              ? 'bg-amber-600'
                              : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              editingRestaurant.features?.[feature.key as keyof typeof editingRestaurant.features]
                                ? 'translate-x-6'
                                : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Counter QR Dine-In Toggle */}
                  <div className="mt-6 pt-6 border-t border-amber-600/20 dark:border-amber-600/20 border-amber-300/20">
                    <div className="flex items-center justify-between p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200 transition-all">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">🪑</span>
                        <div>
                          <span className="text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700">Dine-In in Counter QR</span>
                          <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mt-0.5">Allow customers to select Dine-In option when scanning counter QR</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const currentFeatures = editingRestaurant.features || {};
                          setEditingRestaurant({
                            ...editingRestaurant,
                            features: {
                              ...currentFeatures,
                              dineInInCounterQR: !currentFeatures.dineInInCounterQR,
                            },
                          });
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          editingRestaurant.features?.dineInInCounterQR
                            ? 'bg-amber-600'
                            : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            editingRestaurant.features?.dineInInCounterQR
                              ? 'translate-x-6'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* App Access Control */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-xl">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-6">App Access Control</h3>
                  <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-4">Control which apps restaurant staff can access</p>
                  <div className="space-y-4">
                    {[
                      { key: 'staffApp', label: 'Staff App', icon: '👥', description: 'Allow access to Staff/Waiter app' },
                      { key: 'kitchenApp', label: 'Kitchen App', icon: '🍳', description: 'Allow access to Kitchen app' },
                      { key: 'deliveryApp', label: 'Delivery App', icon: '🚚', description: 'Allow access to Delivery app' },
                      { key: 'adminApp', label: 'Admin App', icon: '⚙️', description: 'Allow access to Admin/Manager app' },
                    ].map((app) => (
                      <div key={app.key} className="flex items-center justify-between p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200 transition-all">
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-xl">{app.icon}</span>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700">{app.label}</span>
                            <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mt-0.5">{app.description}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const currentAppAccess = editingRestaurant.appAccess || {
                              staffApp: true,
                              kitchenApp: true,
                              deliveryApp: true,
                              adminApp: true,
                            };
                            const existingValue = currentAppAccess[app.key as keyof typeof currentAppAccess] ?? true;
                            setEditingRestaurant({
                              ...editingRestaurant,
                              appAccess: {
                                staffApp: currentAppAccess.staffApp ?? true,
                                kitchenApp: currentAppAccess.kitchenApp ?? true,
                                deliveryApp: currentAppAccess.deliveryApp ?? true,
                                adminApp: currentAppAccess.adminApp ?? true,
                                [app.key]: !existingValue,
                              },
                            });
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            editingRestaurant.appAccess?.[app.key as keyof typeof editingRestaurant.appAccess]
                              ? 'bg-amber-600'
                              : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              editingRestaurant.appAccess?.[app.key as keyof typeof editingRestaurant.appAccess]
                                ? 'translate-x-6'
                                : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Methods Configuration */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-xl">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">Payment Methods</h3>
                  <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-6">Configure payment options for Silver, Gold, and Platinum plans</p>
                  <div className="space-y-4">
                    {/* Pay at Counter */}
                    <div className="flex items-center justify-between p-4 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200 transition-all border border-amber-600/10">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 bg-amber-600/20 rounded-lg flex items-center justify-center">
                          <span className="text-xl">💳</span>
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-semibold text-amber-100 dark:text-amber-100 text-zinc-700 block">Pay at Counter</span>
                          <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mt-0.5">Available for Silver, Gold, and Platinum plans</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const currentPaymentMethods = editingRestaurant.paymentMethods || { payAtCounter: true, upi1: true, upi2: true };
                          setEditingRestaurant({
                            ...editingRestaurant,
                            paymentMethods: {
                              ...currentPaymentMethods,
                              payAtCounter: !currentPaymentMethods.payAtCounter,
                            },
                          });
                        }}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50 ${
                          editingRestaurant.paymentMethods?.payAtCounter ?? true
                            ? 'bg-amber-600'
                            : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${
                            editingRestaurant.paymentMethods?.payAtCounter ?? true
                              ? 'translate-x-6'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* UPI Option 1 */}
                    <div className="flex items-center justify-between p-4 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200 transition-all border border-amber-600/10">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                          <span className="text-xl">📱</span>
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-semibold text-amber-100 dark:text-amber-100 text-zinc-700 block">UPI Option 1</span>
                          <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mt-0.5">First UPI payment method</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const currentPaymentMethods = editingRestaurant.paymentMethods || { payAtCounter: true, upi1: true, upi2: true };
                          setEditingRestaurant({
                            ...editingRestaurant,
                            paymentMethods: {
                              ...currentPaymentMethods,
                              upi1: !currentPaymentMethods.upi1,
                            },
                          });
                        }}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50 ${
                          editingRestaurant.paymentMethods?.upi1 ?? true
                            ? 'bg-amber-600'
                            : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${
                            editingRestaurant.paymentMethods?.upi1 ?? true
                              ? 'translate-x-6'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* UPI Option 2 */}
                    <div className="flex items-center justify-between p-4 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200 transition-all border border-amber-600/10">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center">
                          <span className="text-xl">📲</span>
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-semibold text-amber-100 dark:text-amber-100 text-zinc-700 block">UPI Option 2</span>
                          <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mt-0.5">Second UPI payment method</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const currentPaymentMethods = editingRestaurant.paymentMethods || { payAtCounter: true, upi1: true, upi2: true };
                          setEditingRestaurant({
                            ...editingRestaurant,
                            paymentMethods: {
                              ...currentPaymentMethods,
                              upi2: !currentPaymentMethods.upi2,
                            },
                          });
                        }}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50 ${
                          editingRestaurant.paymentMethods?.upi2 ?? true
                            ? 'bg-amber-600'
                            : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${
                            editingRestaurant.paymentMethods?.upi2 ?? true
                              ? 'translate-x-6'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Cafe Mode Toggle */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-xl">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-6">Cafe Mode</h3>
                  <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-4">Enable cafe mode for a single QR code that supports both Dine-In and Takeaway (no tables, no delivery)</p>
                  <div className="flex items-center justify-between p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200 transition-all">
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-xl">☕</span>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700">Cafe Mode</span>
                        <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mt-0.5">Single QR for Dine-In & Takeaway</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const newIsCafe = !editingRestaurant.isCafe;
                        const currentAppAccess = editingRestaurant.appAccess || {
                          staffApp: true,
                          kitchenApp: true,
                          deliveryApp: true,
                          adminApp: true,
                        };
                        
                        // When cafe mode is enabled, disable staffApp and deliveryApp
                        // When cafe mode is disabled, restore previous values (default to true)
                        setEditingRestaurant({
                          ...editingRestaurant,
                          isCafe: newIsCafe,
                          appAccess: {
                            staffApp: newIsCafe ? false : (currentAppAccess.staffApp ?? true),
                            deliveryApp: newIsCafe ? false : (currentAppAccess.deliveryApp ?? true),
                            kitchenApp: currentAppAccess.kitchenApp ?? true,
                            adminApp: currentAppAccess.adminApp ?? true,
                          },
                        });
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        editingRestaurant.isCafe ?? false
                          ? 'bg-amber-600'
                          : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          editingRestaurant.isCafe ?? false
                            ? 'translate-x-6'
                            : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

              </div>
              </div>
            </div>

            <div className="flex-shrink-0 mt-6 pt-4 border-t border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900 from-zinc-50 via-zinc-100 to-zinc-50 sticky bottom-0 z-10 -mx-6 px-6 pb-2">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-full">
                <button
                  onClick={handleUpdateRestaurant}
                  disabled={loading}
                  className="flex-1 sm:flex-none sm:min-w-[200px] bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white px-6 py-3 rounded-xl transition-all font-medium disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-amber-600/30 text-sm sm:text-base"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      <span>{(!editingRestaurant._id || editingRestaurant._id === '') ? 'Create Restaurant' : 'Save Changes'}</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentView('restaurants');
                    setEditingRestaurant(null);
                    setSelectedRestaurant(null);
                  }}
                  className="flex-1 sm:flex-none sm:min-w-[150px] bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 px-6 py-3 rounded-xl transition-all font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <X className="w-5 h-5" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Menu Management View */}
        {currentView === 'menu' && selectedRestaurant && (
          <div className="space-y-6 pt-4 sm:pt-6 lg:pt-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => {
                    setCurrentView('restaurants');
                    setSelectedRestaurant(null);
                    setShowMenuForm(false);
                    setEditingMenuCategory(null);
                    setNewMenuCategory('');
                    setNewMenuCategorySerialNumber(undefined);
                    setNewMenuItems([]);
                    setCategoryAddOns([]);
                  }}
                  className="flex items-center gap-2 text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-amber-400 dark:hover:text-amber-400 hover:text-amber-600 transition-colors px-3 py-2 rounded-lg hover:bg-zinc-800/50 dark:hover:bg-zinc-800/50 hover:bg-zinc-100"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Back to Restaurants</span>
                </button>
                <div>
                  <h2 className="text-3xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">Menu Management</h2>
                  <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600">Manage menu for <span className="text-amber-400 dark:text-amber-400 text-amber-600 font-medium">{selectedRestaurant.name}</span></p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className={`flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white px-4 py-2 rounded-lg hover:from-blue-500 hover:to-blue-600 transition-all font-medium cursor-pointer ${uploadingPDF ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {uploadingPDF ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Parsing PDF...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>Upload PDF Menu</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handlePDFUpload}
                    disabled={uploadingPDF}
                    className="hidden"
                  />
                </label>
                <button
                  onClick={() => {
                    setEditingMenuCategory(null);
                    setNewMenuCategory('');
                    setNewMenuCategorySerialNumber(undefined);
                    setNewMenuItems([{ name: '', price: 0, isAvailable: true, itemType: 'veg', addOns: [] }]);
                    setCategoryAddOns([]);
                    setShowMenuForm(true);
                  }}
                  className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-500 text-white px-4 py-2 rounded-lg hover:from-amber-500 hover:to-amber-600 transition-all font-medium"
                >
                  <Plus className="w-5 h-5" />
                  Add Category
                </button>
                {selectedCategoriesForMerge.size > 0 && (
                  <button
                    onClick={() => {
                      const selectedCategories = restaurantMenu.filter(cat => 
                        selectedCategoriesForMerge.has(cat._id)
                      );
                      if (selectedCategories.length > 0 && selectedCategories[0]) {
                        setMergeCategoryName(selectedCategories[0].category);
                        setMergeTargetCategoryId(selectedCategories[0]._id);
                      }
                      setShowMergeModal(true);
                    }}
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-4 py-2 rounded-lg hover:from-purple-500 hover:to-purple-600 transition-all font-medium"
                  >
                    <GitMerge className="w-5 h-5" />
                    Merge ({selectedCategoriesForMerge.size})
                  </button>
                )}
              </div>
            </div>

            {loading && restaurantMenu.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : restaurantMenu.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-4">
                <div className="w-24 h-24 bg-gradient-to-br from-amber-600/20 to-amber-500/20 dark:from-amber-600/20 dark:to-amber-500/20 from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mb-6">
                  <MenuIcon className="w-12 h-12 text-amber-500" />
                </div>
                <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">No Menu Categories</h3>
                <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm mb-6 text-center max-w-md">
                  Get started by creating your first menu category and adding items to it.
                </p>
                <button
                  onClick={() => {
                    setEditingMenuCategory(null);
                    setNewMenuCategory('');
                    setNewMenuCategorySerialNumber(undefined);
                    setNewMenuItems([{ name: '', price: 0, isAvailable: true, itemType: 'veg', addOns: [] }]);
                    setCategoryAddOns([]);
                    setShowMenuForm(true);
                  }}
                  className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-500 text-white px-6 py-3 rounded-lg hover:from-amber-500 hover:to-amber-600 transition-all font-medium shadow-lg"
                >
                  <Plus className="w-5 h-5" />
                  Create First Category
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Category Search and Selection */}
                <div className="mb-4 space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400 dark:text-zinc-400 text-zinc-600" />
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={categorySearchTerm}
                      onChange={(e) => setCategorySearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-zinc-800/50 dark:bg-zinc-800/50 bg-white border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-zinc-100 dark:text-zinc-100 text-zinc-900 placeholder-zinc-500 dark:placeholder-zinc-500 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-amber-500 dark:focus:ring-amber-500 focus:ring-amber-600 focus:border-transparent transition-all"
                    />
                    {categorySearchTerm && (
                      <button
                        onClick={() => setCategorySearchTerm('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-zinc-300 dark:hover:text-zinc-300 hover:text-zinc-700 transition-colors"
                        aria-label="Clear search"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {restaurantMenu.length > 0 && (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleSelectAllCategories}
                        className="flex items-center gap-2 text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-amber-400 dark:hover:text-amber-400 hover:text-amber-600 transition-colors"
                      >
                        {restaurantMenu.filter((category) => 
                          category.category.toLowerCase().includes(categorySearchTerm.toLowerCase())
                        ).every(cat => selectedCategoriesForMerge.has(cat._id)) ? (
                          <CheckSquare className="w-4 h-4" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                        <span>Select All</span>
                      </button>
                      {selectedCategoriesForMerge.size > 0 && (
                        <button
                          onClick={() => setSelectedCategoriesForMerge(new Set())}
                          className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-red-400 dark:hover:text-red-400 hover:text-red-600 transition-colors"
                        >
                          Clear Selection
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {restaurantMenu
                  .filter((category) => 
                    category.category.toLowerCase().includes(categorySearchTerm.toLowerCase())
                  ).length === 0 && categorySearchTerm ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-600/20 to-amber-500/20 dark:from-amber-600/20 dark:to-amber-500/20 from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mb-4">
                      <Search className="w-8 h-8 text-amber-500" />
                    </div>
                    <h3 className="text-lg font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">No Categories Found</h3>
                    <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm text-center max-w-md">
                      No categories match your search for &quot;{categorySearchTerm}&quot;
                    </p>
                    <button
                      onClick={() => setCategorySearchTerm('')}
                      className="mt-4 text-amber-400 dark:text-amber-400 text-amber-600 hover:text-amber-300 dark:hover:text-amber-300 hover:text-amber-700 transition-colors text-sm font-medium"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  restaurantMenu
                    .filter((category) => 
                      category.category.toLowerCase().includes(categorySearchTerm.toLowerCase())
                    )
                    .map((category) => (
                  <div key={category._id} className={`bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border p-6 shadow-xl hover:shadow-2xl transition-all duration-300 ${
                    selectedCategoriesForMerge.has(category._id)
                      ? 'border-purple-500 dark:border-purple-500 border-purple-400 ring-2 ring-purple-500/50 dark:ring-purple-500/50 ring-purple-400/50'
                      : 'border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 hover:border-amber-600/40 dark:hover:border-amber-600/40 hover:border-amber-500/40'
                  }`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3 flex-1">
                        <button
                          onClick={() => handleToggleCategorySelection(category._id)}
                          className="flex-shrink-0 text-purple-400 dark:text-purple-400 text-purple-600 hover:text-purple-300 dark:hover:text-purple-300 hover:text-purple-700 transition-colors"
                          aria-label={selectedCategoriesForMerge.has(category._id) ? 'Deselect category' : 'Select category for merge'}
                        >
                          {selectedCategoriesForMerge.has(category._id) ? (
                            <CheckSquare className="w-5 h-5" />
                          ) : (
                            <Square className="w-5 h-5" />
                          )}
                        </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {editingSerialNumber === category._id ? (
                            <div className="flex items-center gap-1 bg-amber-600/30 dark:bg-amber-600/30 bg-amber-200 border border-amber-500 dark:border-amber-500 border-amber-400 rounded px-2 py-1">
                              <button
                                onClick={() => handleDecrementSerialNumber(category._id)}
                                className="text-amber-200 dark:text-amber-200 text-amber-800 hover:text-amber-100 dark:hover:text-amber-100 hover:text-amber-900 transition-colors"
                                title="Decrease serial number"
                                disabled={loading}
                              >
                                <ChevronDown className="w-4 h-4" />
                              </button>
                              <input
                                type="number"
                                value={tempSerialNumber ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') {
                                    setTempSerialNumber(undefined);
                                  } else {
                                    const numValue = parseInt(value, 10);
                                    if (!isNaN(numValue) && numValue >= 1) {
                                      setTempSerialNumber(numValue);
                                    }
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSaveSerialNumber(category._id);
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    handleCancelEditSerialNumber();
                                  }
                                }}
                                onBlur={() => {
                                  // Auto-save on blur if value changed
                                  if (tempSerialNumber !== category.serialNumber) {
                                    handleSaveSerialNumber(category._id);
                                  }
                                }}
                                className="w-12 text-center text-sm font-bold text-amber-50 dark:text-amber-50 text-amber-900 bg-transparent border-none focus:outline-none focus:ring-0"
                                min="1"
                                step="1"
                                autoFocus
                              />
                              <button
                                onClick={() => handleIncrementSerialNumber(category._id)}
                                className="text-amber-200 dark:text-amber-200 text-amber-800 hover:text-amber-100 dark:hover:text-amber-100 hover:text-amber-900 transition-colors"
                                title="Increase serial number"
                                disabled={loading}
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleSaveSerialNumber(category._id)}
                                className="ml-1 text-green-400 dark:text-green-400 text-green-600 hover:text-green-300 dark:hover:text-green-300 hover:text-green-700 transition-colors"
                                title="Save"
                                disabled={loading}
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={handleCancelEditSerialNumber}
                                className="text-red-400 dark:text-red-400 text-red-600 hover:text-red-300 dark:hover:text-red-300 hover:text-red-700 transition-colors"
                                title="Cancel"
                                disabled={loading}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              {category.serialNumber !== undefined && category.serialNumber !== null && category.serialNumber >= 1 ? (
                                <span 
                                  className="text-sm font-bold text-amber-400 dark:text-amber-400 text-amber-600 bg-amber-600/20 dark:bg-amber-600/20 bg-amber-100 px-2 py-1 rounded cursor-pointer hover:bg-amber-600/30 dark:hover:bg-amber-600/30 hover:bg-amber-200 transition-colors"
                                  onClick={() => handleStartEditSerialNumber(category)}
                                  title="Click to edit serial number"
                                >
                                  #{category.serialNumber}
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleStartEditSerialNumber(category)}
                                  className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-amber-400 dark:hover:text-amber-400 hover:text-amber-600 border border-zinc-600 dark:border-zinc-600 border-zinc-400 px-2 py-1 rounded hover:border-amber-500 dark:hover:border-amber-500 hover:border-amber-400 transition-colors"
                                  title="Add serial number"
                                  disabled={loading}
                                >
                                  + Add #
                                </button>
                              )}
                              {category.serialNumber !== undefined && category.serialNumber !== null && (
                                <div className="flex flex-col gap-0.5">
                                  <button
                                    onClick={() => {
                                      const currentSerial = category.serialNumber ?? 0;
                                      const newSerial = currentSerial + 1;
                                      handleQuickUpdateSerialNumber(category._id, newSerial);
                                    }}
                                    className="text-amber-400 dark:text-amber-400 text-amber-600 hover:text-amber-300 dark:hover:text-amber-300 hover:text-amber-700 transition-colors"
                                    title="Increase serial number"
                                    disabled={loading}
                                  >
                                    <ChevronUp className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      const currentSerial = category.serialNumber ?? 1;
                                      const newSerial = currentSerial - 1;
                                      if (newSerial >= 1) {
                                        handleQuickUpdateSerialNumber(category._id, newSerial);
                                      }
                                    }}
                                    className="text-amber-400 dark:text-amber-400 text-amber-600 hover:text-amber-300 dark:hover:text-amber-300 hover:text-amber-700 transition-colors"
                                    title="Decrease serial number"
                                    disabled={loading}
                                  >
                                    <ChevronDown className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">{category.category}</h3>
                          </div>
                        </div>
                        {category.addOns && category.addOns.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className="text-xs text-amber-400/70 dark:text-amber-400/70 text-amber-700 font-medium">Category Add-ons:</span>
                              {category.addOns.map((addOn, idx) => (
                                <span key={`addon-${category._id}-${idx}-${addOn.name}`} className="text-xs bg-amber-600/20 dark:bg-amber-600/20 bg-amber-100 text-amber-400 dark:text-amber-400 text-amber-700 px-2 py-1 rounded">
                                  + {addOn.name} (₹{addOn.price.toFixed(2)})
                                </span>
                              ))}
                          </div>
                        )}
                      </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Up/Down movement buttons */}
                        <div className="flex flex-col gap-0.5 mr-2 border-r border-zinc-700 dark:border-zinc-700 border-zinc-300 pr-2">
                          <button
                            onClick={() => handleMoveCategoryUp(category._id)}
                            disabled={loading || restaurantMenu.findIndex(c => c._id === category._id) === 0}
                            className="text-amber-400 dark:text-amber-400 text-amber-600 hover:text-amber-300 dark:hover:text-amber-300 hover:text-amber-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Move category up"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleMoveCategoryDown(category._id)}
                            disabled={loading || restaurantMenu.findIndex(c => c._id === category._id) === restaurantMenu.length - 1}
                            className="text-amber-400 dark:text-amber-400 text-amber-600 hover:text-amber-300 dark:hover:text-amber-300 hover:text-amber-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Move category down"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => handleEditMenuCategory(category)}
                          className="text-amber-400 dark:text-amber-400 text-amber-600 hover:text-amber-300 dark:hover:text-amber-300 hover:text-amber-700"
                          title="Edit category"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDuplicateMenuCategory(category)}
                          className="text-blue-400 dark:text-blue-400 text-blue-600 hover:text-blue-300 dark:hover:text-blue-300 hover:text-blue-700"
                          title="Duplicate category"
                        >
                          <Copy className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteMenuCategory(category._id)}
                          className="text-red-400 dark:text-red-400 text-red-600 hover:text-red-300 dark:hover:text-red-300 hover:text-red-700"
                          title="Delete category"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {category.items.map((item, idx) => {
                        const itemId = item._id || item.menuItemId || `item-${category._id}-${idx}`;
                        const isEditing = editingItemId === itemId;
                        return (
                        <div key={itemId} className="bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={item.name}
                                    onChange={(e) => {
                                      setRestaurantMenu(prev => prev.map(cat => 
                                        cat._id === category._id 
                                          ? { ...cat, items: cat.items.map((it, i) => i === idx ? { ...it, name: e.target.value } : it) }
                                          : cat
                                      ));
                                    }}
                                    onBlur={async () => {
                                      if (selectedRestaurant?._id) {
                                        const updatedCategory = restaurantMenu.find(c => c._id === category._id);
                                        if (updatedCategory) {
                                          try {
                                            await superAdminAPI.updateRestaurantMenuCategory(
                                              selectedRestaurant._id,
                                              category._id,
                                              category.category,
                                              updatedCategory.items,
                                              category.addOns,
                                              category.serialNumber
                                            );
                                            showSuccess('Item updated');
                                          } catch (err) {
                                            await fetchRestaurantMenu(selectedRestaurant._id);
                                            showError('Failed to update item');
                                          }
                                        }
                                      }
                                      setEditingItemId(null);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.currentTarget.blur();
                                      } else if (e.key === 'Escape') {
                                        setEditingItemId(null);
                                        fetchRestaurantMenu(selectedRestaurant?._id || '');
                                      }
                                    }}
                                    className="px-2 py-1 bg-zinc-800 dark:bg-zinc-800 bg-white border border-amber-500 rounded text-white dark:text-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                                    autoFocus
                                  />
                                ) : (
                                  <h4 
                                    className="text-amber-50 dark:text-amber-50 text-zinc-900 font-medium cursor-pointer hover:text-amber-400 transition-colors"
                                    onClick={() => setEditingItemId(itemId)}
                                    title="Click to edit name"
                                  >
                                    {item.name}
                                  </h4>
                                )}
                                {item.itemType && (
                                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                    item.itemType === 'veg'
                                      ? 'bg-green-500/20 text-green-400 dark:text-green-400 text-green-700 border border-green-500/30' 
                                      : item.itemType === 'non-veg'
                                      ? 'bg-red-500/20 text-red-400 dark:text-red-400 text-red-700 border border-red-500/30'
                                      : 'bg-blue-500/20 text-blue-400 dark:text-blue-400 text-blue-700 border border-blue-500/30'
                                  }`}>
                                    {item.itemType === 'veg' ? '🟢 VEG' : item.itemType === 'non-veg' ? '🔴 NON-VEG' : '🥤 BEVERAGE'}
                                  </span>
                                )}
                              </div>
                              {item.description && <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600 mt-1">{item.description}</p>}
                              {item.addOns && item.addOns.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  <p className="text-xs text-amber-400/70 dark:text-amber-400/70 text-amber-700 font-medium">Add-ons:</p>
                                  {item.addOns.map((addOn, addOnIdx) => (
                                    <div key={`item-addon-${item._id || item.menuItemId || idx}-${addOnIdx}-${addOn.name}`} className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 flex items-center gap-2">
                                      <span>+ {addOn.name}</span>
                                      <span className="text-amber-400 dark:text-amber-400 text-amber-600">₹{addOn.price.toFixed(2)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <span className={`px-2 py-1 rounded text-xs ${item.isAvailable ? 'bg-green-500/20 text-green-400 dark:text-green-400 text-green-700' : 'bg-red-500/20 text-red-400 dark:text-red-400 text-red-700'}`}>
                              {item.isAvailable ? 'Available' : 'Unavailable'}
                            </span>
                          </div>
                          {item.variant && (
                            <div className="mb-2">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                item.variant === 'most popular' ? 'bg-blue-500/20 text-blue-400 dark:text-blue-400 text-blue-700' :
                                item.variant === 'spicy' ? 'bg-red-500/20 text-red-400 dark:text-red-400 text-red-700' :
                                item.variant === 'sweet' ? 'bg-pink-500/20 text-pink-400 dark:text-pink-400 text-pink-700' :
                                'bg-amber-500/20 text-amber-400 dark:text-amber-400 text-amber-700'
                              }`}>
                                {item.variant === 'most popular' ? 'Most Popular' : item.variant.charAt(0).toUpperCase() + item.variant.slice(1)}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            {isEditing ? (
                              <input
                                type="number"
                                value={item.price}
                                onChange={(e) => {
                                  setRestaurantMenu(prev => prev.map(cat => 
                                    cat._id === category._id 
                                      ? { ...cat, items: cat.items.map((it, i) => i === idx ? { ...it, price: parseFloat(e.target.value) || 0 } : it) }
                                      : cat
                                  ));
                                }}
                                onBlur={async () => {
                                  if (selectedRestaurant?._id) {
                                    const updatedCategory = restaurantMenu.find(c => c._id === category._id);
                                    if (updatedCategory) {
                                      try {
                                        await superAdminAPI.updateRestaurantMenuCategory(
                                          selectedRestaurant._id,
                                          category._id,
                                          category.category,
                                          updatedCategory.items,
                                          category.addOns,
                                          category.serialNumber
                                        );
                                        showSuccess('Price updated');
                                      } catch (err) {
                                        await fetchRestaurantMenu(selectedRestaurant._id);
                                        showError('Failed to update price');
                                      }
                                    }
                                  }
                                }}
                                className="w-24 px-2 py-1 bg-zinc-800 dark:bg-zinc-800 bg-white border border-amber-500 rounded text-white dark:text-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                                step="0.01"
                                autoFocus
                              />
                            ) : (
                              <span 
                                className="text-lg font-bold text-amber-400 dark:text-amber-400 text-amber-600 cursor-pointer hover:text-amber-300 transition-colors"
                                onClick={() => setEditingItemId(itemId)}
                                title="Click to edit price"
                              >
                                ₹{item.price.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                    ))
                )}
              </div>
            )}

            {showMenuForm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-zinc-800 dark:bg-zinc-800 bg-white rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">
                      {editingMenuCategory ? 'Edit Category' : 'Add Category'}
                    </h3>
                    <button onClick={() => setShowMenuForm(false)} className="text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-zinc-300 dark:hover:text-zinc-300 hover:text-zinc-800">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Category Name</label>
                      <input
                        type="text"
                        value={newMenuCategory}
                        onChange={(e) => setNewMenuCategory(e.target.value)}
                        className="w-full px-4 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                        placeholder="e.g., Main Course, Appetizers"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">
                        Serial Number <span className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600">(Optional - for ordering categories)</span>
                      </label>
                      <input
                        type="number"
                        value={newMenuCategorySerialNumber ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNewMenuCategorySerialNumber(value === '' ? undefined : parseInt(value, 10));
                        }}
                        className="w-full px-4 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                        placeholder="e.g., 1, 2, 3"
                        min="1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">
                        Category Add-ons <span className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600">(Available for all items in this category)</span>
                      </label>
                      <div className="space-y-2">
                        {categoryAddOns.map((addOn, addOnIdx) => (
                          <div key={addOnIdx} className="flex gap-2">
                            <input
                              type="text"
                              value={addOn.name}
                              onChange={(e) => {
                                const updated = [...categoryAddOns];
                                if (updated[addOnIdx]) {
                                  updated[addOnIdx] = { ...updated[addOnIdx], name: e.target.value || '' };
                                  setCategoryAddOns(updated);
                                }
                              }}
                              className="flex-1 px-3 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                              placeholder="Add-on name (e.g., Extra Cheese)"
                            />
                            <input
                              type="number"
                              value={addOn.price}
                              onChange={(e) => {
                                const updated = [...categoryAddOns];
                                if (updated[addOnIdx]) {
                                  updated[addOnIdx] = { ...updated[addOnIdx], price: parseFloat(e.target.value) || 0 };
                                  setCategoryAddOns(updated);
                                }
                              }}
                              className="w-24 px-3 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                              placeholder="Price"
                              step="0.01"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setCategoryAddOns(categoryAddOns.filter((_, i) => i !== addOnIdx));
                              }}
                              className="px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 dark:text-red-400 text-red-700 rounded-lg text-sm transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            setCategoryAddOns([...categoryAddOns, { name: '', price: 0 }]);
                          }}
                          className="w-full py-2 border border-dashed border-zinc-600 dark:border-zinc-600 border-zinc-400 rounded-lg text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:border-amber-500 dark:hover:border-amber-500 hover:border-amber-600 hover:text-amber-400 dark:hover:text-amber-400 hover:text-amber-600 transition-all text-sm flex items-center justify-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add Category Add-on
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Menu Items</label>
                      <div className="space-y-3">
                        {/* Bulk paste button */}
                        <div className="flex items-center gap-2 mb-2">
                          <button
                            type="button"
                            onClick={() => {
                              const text = prompt('Paste menu items (one per line, format: Name | Price | Description):');
                              if (!text) return;
                              
                              const lines = text.split('\n').filter(l => l.trim());
                              const bulkItems: MenuItem[] = lines.map(line => {
                                const parts = line.split('|').map(p => p.trim());
                                const name = parts[0] || '';
                                const price = parseFloat(parts[1] || '0') || 0;
                                const description = parts[2] || '';
                                return {
                                  name,
                                  price,
                                  description,
                                  isAvailable: true,
                                  itemType: 'veg' as const,
                                  addOns: [],
                                };
                              }).filter(item => item.name && item.price > 0);
                              
                              if (bulkItems.length > 0) {
                                setNewMenuItems(prev => [...prev, ...bulkItems]);
                                showSuccess(`Added ${bulkItems.length} items from paste`);
                              }
                            }}
                            className="px-3 py-1.5 text-xs bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg transition-all flex items-center gap-1"
                            title="Paste items from spreadsheet (Name | Price | Description)"
                          >
                            <Copy className="w-3 h-3" />
                            Bulk Paste
                          </button>
                          <span className="text-xs text-zinc-500">Format: Name | Price | Description</span>
                        </div>
                        
                        {newMenuItems.map((item, idx) => {
                          const itemKey = item._id || item.menuItemId || `item-${idx}`;
                          return (
                          <div key={itemKey} className="bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-50 rounded-lg p-4 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                type="text"
                                value={item.name}
                                onChange={(e) => {
                                  setNewMenuItems(prev => {
                                    const updated = [...prev];
                                    if (updated[idx]) {
                                      updated[idx] = { ...updated[idx], name: e.target.value || '', price: updated[idx].price || 0, isAvailable: updated[idx].isAvailable ?? true };
                                    }
                                    return updated;
                                  });
                                }}
                                className="px-3 py-2 bg-zinc-800 dark:bg-zinc-800 bg-white border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                                placeholder="Item name"
                              />
                              <input
                                type="number"
                                value={item.price}
                                onChange={(e) => {
                                  setNewMenuItems(prev => {
                                    const updated = [...prev];
                                    if (updated[idx]) {
                                      updated[idx] = { ...updated[idx], price: parseFloat(e.target.value) || 0, name: updated[idx].name || '', isAvailable: updated[idx].isAvailable ?? true };
                                    }
                                    return updated;
                                  });
                                }}
                                className="px-3 py-2 bg-zinc-800 dark:bg-zinc-800 bg-white border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                                placeholder="Price"
                                step="0.01"
                              />
                            </div>
                            <textarea
                              value={item.description || ''}
                              onChange={(e) => {
                                setNewMenuItems(prev => {
                                  const updated = [...prev];
                                  if (updated[idx]) {
                                    updated[idx] = { ...updated[idx], description: e.target.value, name: updated[idx].name || '', price: updated[idx].price || 0, isAvailable: updated[idx].isAvailable ?? true };
                                  }
                                  return updated;
                                });
                              }}
                              className="w-full px-3 py-2 bg-zinc-800 dark:bg-zinc-800 bg-white border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                              placeholder="Description (optional)"
                              rows={2}
                            />
                            <div>
                              <label className="block text-xs font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Variant</label>
                              <div className="grid grid-cols-4 gap-2">
                                {(['most popular', 'spicy', 'sweet', 'chef special'] as const).map((variant) => (
                                  <button
                                    key={variant}
                                    type="button"
                                    onClick={() => {
                                      setNewMenuItems(prev => {
                                        const updated = [...prev];
                                        if (updated[idx]) {
                                          updated[idx] = { ...updated[idx], variant: item.variant === variant ? undefined : variant, name: updated[idx].name || '', price: updated[idx].price || 0, isAvailable: updated[idx].isAvailable ?? true };
                                        }
                                        return updated;
                                      });
                                    }}
                                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                      item.variant === variant
                                        ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/30'
                                        : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300'
                                    }`}
                                  >
                                    {variant === 'most popular' ? 'Most Popular' : variant.charAt(0).toUpperCase() + variant.slice(1)}
                                  </button>
                                ))}
                              </div>
                            </div>
                            
                            <div className="mb-3">
                              <label className="block text-xs font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Type</label>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewMenuItems(prev => {
                                      const updated = [...prev];
                                      if (updated[idx]) {
                                        updated[idx] = { ...updated[idx], itemType: updated[idx].itemType === 'veg' ? undefined : 'veg', name: updated[idx].name || '', price: updated[idx].price || 0, isAvailable: updated[idx].isAvailable ?? true };
                                      }
                                      return updated;
                                    });
                                  }}
                                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                    item.itemType === 'veg'
                                      ? 'bg-green-600 text-white shadow-lg shadow-green-600/30'
                                      : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300'
                                  }`}
                                >
                                  🟢 Veg
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewMenuItems(prev => {
                                      const updated = [...prev];
                                      if (updated[idx]) {
                                        updated[idx] = { ...updated[idx], itemType: updated[idx].itemType === 'non-veg' ? undefined : 'non-veg', name: updated[idx].name || '', price: updated[idx].price || 0, isAvailable: updated[idx].isAvailable ?? true };
                                      }
                                      return updated;
                                    });
                                  }}
                                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                    item.itemType === 'non-veg'
                                      ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                                      : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300'
                                  }`}
                                >
                                  🔴 Non-Veg
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewMenuItems(prev => {
                                      const updated = [...prev];
                                      if (updated[idx]) {
                                        updated[idx] = { ...updated[idx], itemType: updated[idx].itemType === 'beverage' ? undefined : 'beverage', name: updated[idx].name || '', price: updated[idx].price || 0, isAvailable: updated[idx].isAvailable ?? true };
                                      }
                                      return updated;
                                    });
                                  }}
                                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                    item.itemType === 'beverage'
                                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                                      : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300'
                                  }`}
                                >
                                  🥤 Beverage
                                </button>
                              </div>
                            </div>

                            <div className="mb-3">
                              <label className="block text-xs font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Add-ons</label>
                              <div className="space-y-2">
                                {(item.addOns || []).map((addOn, addOnIdx) => {
                                  if (!addOn || !item) return null;
                                  return (
                                  <div key={addOnIdx} className="flex gap-2">
                                    <input
                                      type="text"
                                      value={addOn.name}
                                      onChange={(e) => {
                                        setNewMenuItems(prev => {
                                          const updated = [...prev];
                                          if (updated[idx] && !updated[idx].addOns) updated[idx].addOns = [];
                                          if (updated[idx] && updated[idx].addOns && updated[idx].addOns[addOnIdx]) {
                                            const currentAddOn = updated[idx].addOns[addOnIdx];
                                            updated[idx].addOns[addOnIdx] = { name: e.target.value || '', price: currentAddOn?.price || 0 };
                                          }
                                          return updated;
                                        });
                                      }}
                                      className="flex-1 px-2 py-1.5 bg-zinc-800 dark:bg-zinc-800 bg-white border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded text-white dark:text-white text-zinc-900 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                                      placeholder="Add-on name (e.g., Extra Cheese)"
                                    />
                                    <input
                                      type="number"
                                      value={addOn.price}
                                      onChange={(e) => {
                                        setNewMenuItems(prev => {
                                          const updated = [...prev];
                                          if (updated[idx] && !updated[idx].addOns) updated[idx].addOns = [];
                                          if (updated[idx] && updated[idx].addOns && updated[idx].addOns[addOnIdx]) {
                                            const currentAddOn = updated[idx].addOns[addOnIdx];
                                            updated[idx].addOns[addOnIdx] = { name: currentAddOn?.name || '', price: parseFloat(e.target.value) || 0 };
                                          }
                                          return updated;
                                        });
                                      }}
                                      className="w-20 px-2 py-1.5 bg-zinc-800 dark:bg-zinc-800 bg-white border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded text-white dark:text-white text-zinc-900 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                                      placeholder="Price"
                                      step="0.01"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setNewMenuItems(prev => {
                                          const updated = [...prev];
                                          if (updated[idx] && updated[idx].addOns) {
                                            updated[idx].addOns = updated[idx].addOns.filter((_, i) => i !== addOnIdx);
                                          }
                                          return updated;
                                        });
                                      }}
                                      className="px-2 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 dark:text-red-400 text-red-700 rounded text-xs transition-colors"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                  );
                                })}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewMenuItems(prev => {
                                      const updated = [...prev];
                                      if (updated[idx]) {
                                        if (!updated[idx].addOns) updated[idx].addOns = [];
                                        updated[idx].addOns.push({ name: '', price: 0 });
                                      }
                                      return updated;
                                    });
                                  }}
                                  className="w-full py-1.5 border border-dashed border-zinc-600 dark:border-zinc-600 border-zinc-400 rounded text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:border-amber-500 dark:hover:border-amber-500 hover:border-amber-600 hover:text-amber-400 dark:hover:text-amber-400 hover:text-amber-600 transition-all text-xs flex items-center justify-center gap-1"
                                >
                                  <Plus className="w-3 h-3" />
                                  Add Add-on
                                </button>
                              </div>
                            </div>

                            <div className="flex items-center justify-between">
                              <label className="flex items-center gap-2 text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600">
                                <input
                                  type="checkbox"
                                  checked={item.isAvailable ?? true}
                                  onChange={(e) => {
                                    setNewMenuItems(prev => {
                                      const updated = [...prev];
                                      if (updated[idx]) {
                                        updated[idx] = { ...updated[idx], isAvailable: e.target.checked, name: updated[idx].name || '', price: updated[idx].price || 0 };
                                      }
                                      return updated;
                                    });
                                  }}
                                  className="rounded"
                                />
                                Available
                              </label>
                              <button
                                onClick={() => {
                                  setNewMenuItems(prev => prev.filter((_, i) => i !== idx));
                                }}
                                className="text-red-400 dark:text-red-400 text-red-600 hover:text-red-300 dark:hover:text-red-300 hover:text-red-700 text-sm"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          );
                        })}
                        <button
                          onClick={() => {
                            setNewMenuItems(prev => [...prev, { name: '', price: 0, isAvailable: true, itemType: 'veg' as const, addOns: [] }]);
                          }}
                          className="w-full py-2 border-2 border-dashed border-zinc-700 dark:border-zinc-700 border-zinc-400 rounded-lg text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:border-amber-500 dark:hover:border-amber-500 hover:border-amber-600 hover:text-amber-400 dark:hover:text-amber-400 hover:text-amber-600 transition-all text-sm"
                        >
                          <Plus className="w-4 h-4 inline mr-2" />
                          Add Item
                        </button>
                        <button
                          onClick={() => {
                            const count = parseInt(prompt('How many items to add?', '5') || '5');
                            if (count > 0 && count <= 100) {
                              const newItems = Array.from({ length: count }, () => ({
                                name: '',
                                price: 0,
                                isAvailable: true,
                                itemType: 'veg' as const,
                                addOns: [] as AddOn[],
                              }));
                              setNewMenuItems(prev => [...prev, ...newItems]);
                            }
                          }}
                          className="w-full py-2 border-2 border-dashed border-blue-600/50 dark:border-blue-600/50 border-blue-400/50 rounded-lg text-blue-400 dark:text-blue-400 text-blue-600 hover:border-blue-500 dark:hover:border-blue-500 hover:border-blue-600 hover:text-blue-300 dark:hover:text-blue-300 hover:text-blue-700 transition-all text-sm mt-2"
                        >
                          <Plus className="w-4 h-4 inline mr-2" />
                          Add Multiple Items
                        </button>
                      </div>
                    </div>

                    <div className="flex gap-4 pt-4">
                      <button
                        onClick={handleSaveMenu}
                        disabled={savingMenu || loading}
                        className="flex-1 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white px-6 py-2 rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {savingMenu ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-5 h-5" />
                            Save
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setShowMenuForm(false)}
                        className="px-6 py-2 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-lg transition-all font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Merge Categories Modal */}
            {showMergeModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-zinc-800 dark:bg-zinc-800 bg-white rounded-2xl border border-purple-600/20 dark:border-purple-600/20 border-purple-300/20 p-6 max-w-lg w-full">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
                        <GitMerge className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">Merge Categories</h3>
                    </div>
                    <button 
                      onClick={() => {
                        setShowMergeModal(false);
                        setMergeCategoryName('');
                        setMergeTargetCategoryId(null);
                      }} 
                      className="text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-zinc-300 dark:hover:text-zinc-300 hover:text-zinc-800"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-3">
                        Merging <span className="text-purple-400 dark:text-purple-400 text-purple-600 font-medium">{selectedCategoriesForMerge.size}</span> categories. All items will be combined into one category.
                      </p>
                      <div className="bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg p-3 mb-4 max-h-32 overflow-y-auto">
                        <p className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 mb-2 font-medium">Categories to merge:</p>
                        <ul className="space-y-1">
                          {restaurantMenu
                            .filter(cat => selectedCategoriesForMerge.has(cat._id))
                            .filter(cat => cat?._id)
                            .map(cat => (
                              <li key={cat._id} className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-purple-400 dark:bg-purple-400 text-purple-600 rounded-full"></span>
                                {cat.category} ({cat.items?.length || 0} items)
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">
                        Merged Category Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={mergeCategoryName}
                        onChange={(e) => setMergeCategoryName(e.target.value)}
                        className="w-full px-4 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                        placeholder="Enter category name"
                        autoFocus
                      />
                    </div>


                    <div className="bg-amber-500/10 dark:bg-amber-500/10 bg-amber-50 border border-amber-500/30 dark:border-amber-500/30 border-amber-300/30 rounded-lg p-3">
                      <p className="text-xs text-amber-400 dark:text-amber-400 text-amber-700">
                        <strong>Note:</strong> All items from selected categories will be combined. Source categories will be deleted after merging.
                      </p>
                    </div>

                    <div className="flex gap-4 pt-4">
                      <button
                        onClick={handleMergeCategories}
                        disabled={mergingCategories || !mergeCategoryName.trim()}
                        className="flex-1 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-600 text-white px-6 py-2 rounded-lg transition-all font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {mergingCategories ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Merging...
                          </>
                        ) : (
                          <>
                            <GitMerge className="w-5 h-5" />
                            Merge Categories
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setShowMergeModal(false);
                          setMergeCategoryName('');
                          setMergeTargetCategoryId(null);
                        }}
                        className="px-6 py-2 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-lg transition-all font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create User View - Full Page */}
        {currentView === 'create-user' && selectedRestaurant && (
          <div className="space-y-6">
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
                <button
                  onClick={() => {
                    setCurrentView('users');
                    setNewUserName('');
                    setNewUserEmail('');
                    setNewUserPassword('');
                    setNewUserRole('WAITER');
                    setNewUserBranchId('');
                  }}
                  className="flex items-center gap-2 text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-amber-400 dark:hover:text-amber-400 hover:text-amber-600 transition-colors px-3 py-2 rounded-lg hover:bg-zinc-800/50 dark:hover:bg-zinc-800/50 hover:bg-zinc-100 self-start"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-sm sm:text-base">Back to Users</span>
                </button>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-amber-600 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-600/30 flex-shrink-0">
                  <UserPlus className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl sm:text-3xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">Create New User</h2>
                  <p className="text-sm sm:text-base text-zinc-400 dark:text-zinc-400 text-zinc-600">Add a new user for <span className="text-amber-400 dark:text-amber-400 text-amber-600 font-medium">{selectedRestaurant.name}</span></p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-8 shadow-xl max-w-3xl">
              {error && (
                <div className="mb-6 p-4 bg-red-500/10 dark:bg-red-500/10 bg-red-50 border border-red-500/30 dark:border-red-500/30 border-red-300/30 rounded-lg">
                  <p className="text-red-400 dark:text-red-400 text-red-700 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); handleCreateUser(); }} className="space-y-6">
                {/* Role Selection - Visual Cards */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-amber-100 mb-4">
                    <Shield className="w-4 h-4" />
                    <span>Role <span className="text-red-400">*</span></span>
                  </label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Owner Role */}
                    <button
                      type="button"
                      onClick={() => setNewUserRole('OWNER')}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        newUserRole === 'OWNER'
                          ? 'bg-amber-500/20 dark:bg-amber-500/20 bg-amber-100 border-amber-500 dark:border-amber-500 border-amber-400 shadow-lg shadow-amber-500/30'
                          : 'bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border-zinc-700 dark:border-zinc-700 border-zinc-300 hover:border-amber-500/50 dark:hover:border-amber-500/50 hover:border-amber-400 hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          newUserRole === 'OWNER' ? 'bg-amber-500/30 dark:bg-amber-500/30 bg-amber-200' : 'bg-zinc-800 dark:bg-zinc-800 bg-zinc-200'
                        }`}>
                          <span className="text-2xl">👑</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-amber-50 dark:text-amber-50 text-zinc-900 font-semibold">Owner</h3>
                          <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-xs">Full access</p>
                        </div>
                        {newUserRole === 'OWNER' && (
                          <CheckCircle2 className="w-5 h-5 text-amber-400 dark:text-amber-400 text-amber-600" />
                        )}
                      </div>
                      <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-xs mt-2">Full access to restaurant management, orders, menu, staff, and settings</p>
                    </button>

                    {/* Manager Role */}
                    <button
                      type="button"
                      onClick={() => setNewUserRole('MANAGER')}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        newUserRole === 'MANAGER'
                          ? 'bg-amber-500/20 dark:bg-amber-500/20 bg-amber-100 border-amber-500 dark:border-amber-500 border-amber-400 shadow-lg shadow-amber-500/30'
                          : 'bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border-zinc-700 dark:border-zinc-700 border-zinc-300 hover:border-amber-500/50 dark:hover:border-amber-500/50 hover:border-amber-400 hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          newUserRole === 'MANAGER' ? 'bg-amber-500/30 dark:bg-amber-500/30 bg-amber-200' : 'bg-zinc-800 dark:bg-zinc-800 bg-zinc-200'
                        }`}>
                          <span className="text-2xl">💼</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-amber-50 dark:text-amber-50 text-zinc-900 font-semibold">Manager</h3>
                          <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-xs">Management access</p>
                        </div>
                        {newUserRole === 'MANAGER' && (
                          <CheckCircle2 className="w-5 h-5 text-amber-400 dark:text-amber-400 text-amber-600" />
                        )}
                      </div>
                      <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-xs mt-2">Can manage orders, menu, staff, and view reports</p>
                    </button>

                    {/* Waiter Role */}
                    <button
                      type="button"
                      onClick={() => setNewUserRole('WAITER')}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        newUserRole === 'WAITER'
                          ? 'bg-amber-500/20 dark:bg-amber-500/20 bg-amber-100 border-amber-500 dark:border-amber-500 border-amber-400 shadow-lg shadow-amber-500/30'
                          : 'bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border-zinc-700 dark:border-zinc-700 border-zinc-300 hover:border-amber-500/50 dark:hover:border-amber-500/50 hover:border-amber-400 hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          newUserRole === 'WAITER' ? 'bg-amber-500/30 dark:bg-amber-500/30 bg-amber-200' : 'bg-zinc-800 dark:bg-zinc-800 bg-zinc-200'
                        }`}>
                          <span className="text-2xl">🍽️</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-amber-50 dark:text-amber-50 text-zinc-900 font-semibold">Waiter</h3>
                          <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-xs">Service staff</p>
                        </div>
                        {newUserRole === 'WAITER' && (
                          <CheckCircle2 className="w-5 h-5 text-amber-400 dark:text-amber-400 text-amber-600" />
                        )}
                      </div>
                      <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-xs mt-2">Can manage dine-in orders, tables, and serve customers</p>
                    </button>

                    {/* Kitchen Staff Role */}
                    <button
                      type="button"
                      onClick={() => setNewUserRole('KITCHEN')}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        newUserRole === 'KITCHEN'
                          ? 'bg-amber-500/20 dark:bg-amber-500/20 bg-amber-100 border-amber-500 dark:border-amber-500 border-amber-400 shadow-lg shadow-amber-500/30'
                          : 'bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border-zinc-700 dark:border-zinc-700 border-zinc-300 hover:border-amber-500/50 dark:hover:border-amber-500/50 hover:border-amber-400 hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          newUserRole === 'KITCHEN' ? 'bg-amber-500/30 dark:bg-amber-500/30 bg-amber-200' : 'bg-zinc-800 dark:bg-zinc-800 bg-zinc-200'
                        }`}>
                          <span className="text-2xl">👨‍🍳</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-amber-50 dark:text-amber-50 text-zinc-900 font-semibold">Kitchen Staff</h3>
                          <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-xs">Kitchen access</p>
                        </div>
                        {newUserRole === 'KITCHEN' && (
                          <CheckCircle2 className="w-5 h-5 text-amber-400 dark:text-amber-400 text-amber-600" />
                        )}
                      </div>
                      <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-xs mt-2">Can view and update order status in kitchen dashboard</p>
                    </button>

                    {/* Delivery Personnel Role */}
                    <button
                      type="button"
                      onClick={() => setNewUserRole('DELIVERY')}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        newUserRole === 'DELIVERY'
                          ? 'bg-amber-500/20 dark:bg-amber-500/20 bg-amber-100 border-amber-500 dark:border-amber-500 border-amber-400 shadow-lg shadow-amber-500/30'
                          : 'bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border-zinc-700 dark:border-zinc-700 border-zinc-300 hover:border-amber-500/50 dark:hover:border-amber-500/50 hover:border-amber-400 hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          newUserRole === 'DELIVERY' ? 'bg-amber-500/30 dark:bg-amber-500/30 bg-amber-200' : 'bg-zinc-800 dark:bg-zinc-800 bg-zinc-200'
                        }`}>
                          <span className="text-2xl">🚚</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-amber-50 dark:text-amber-50 text-zinc-900 font-semibold">Delivery Personnel</h3>
                          <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-xs">Delivery access</p>
                        </div>
                        {newUserRole === 'DELIVERY' && (
                          <CheckCircle2 className="w-5 h-5 text-amber-400 dark:text-amber-400 text-amber-600" />
                        )}
                      </div>
                      <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-xs mt-2">Can manage delivery orders and update delivery status</p>
                    </button>
                  </div>
                </div>

                {/* Full Name */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">
                    <UserIcon className="w-4 h-4" />
                    <span>Full Name <span className="text-red-400 dark:text-red-400 text-red-700">*</span></span>
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500 dark:text-zinc-500 text-zinc-600" />
                    <input
                      type="text"
                      value={newUserName}
                      onChange={(e) => setNewUserName(e.target.value)}
                      placeholder="Enter full name"
                      required
                      className="w-full pl-12 pr-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">
                    <Mail className="w-4 h-4" />
                    <span>Email Address <span className="text-red-400 dark:text-red-400 text-red-700">*</span></span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500 dark:text-zinc-500 text-zinc-600" />
                    <input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      placeholder="user@example.com"
                      required
                      className="w-full pl-12 pr-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">
                    <Lock className="w-4 h-4" />
                    <span>Password <span className="text-red-400 dark:text-red-400 text-red-700">*</span></span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500 dark:text-zinc-500 text-zinc-600" />
                    <input
                      type="password"
                      value={newUserPassword}
                      onChange={(e) => setNewUserPassword(e.target.value)}
                      placeholder="Enter password (min 6 characters)"
                      required
                      minLength={6}
                      className="w-full pl-12 pr-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all"
                    />
                  </div>
                  <p className="text-zinc-500 dark:text-zinc-500 text-zinc-600 text-xs mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Password must be at least 6 characters long
                  </p>
                </div>

                {/* Branch Selection (if branches exist) */}
                {restaurantBranches.length > 0 && (
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">
                      <GitBranch className="w-4 h-4" />
                      <span>Branch (Optional)</span>
                    </label>
                    <div className="relative">
                      <GitBranch className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500 dark:text-zinc-500 text-zinc-600 z-10 pointer-events-none" />
                      <select
                        value={newUserBranchId}
                        onChange={(e) => setNewUserBranchId(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all appearance-none cursor-pointer"
                      >
                        <option value="">No specific branch (All branches)</option>
                        {restaurantBranches.map(branch => (
                          <option key={branch._id} value={branch._id}>
                            {branch.name} - {branch.address}
                          </option>
                        ))}
                      </select>
                    </div>
                    <p className="text-zinc-500 dark:text-zinc-500 text-zinc-600 text-xs mt-2 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Assign user to a specific branch or leave blank for all branches
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-4 pt-6 border-t border-zinc-700/50 dark:border-zinc-700/50 border-zinc-300/50">
                  <button
                    type="submit"
                    disabled={loading || !newUserName.trim() || !newUserEmail.trim() || !newUserPassword.trim()}
                    className="flex-1 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white px-6 py-4 rounded-xl transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-amber-600/30 hover:shadow-amber-600/50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Creating User...</span>
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-5 h-5" />
                        <span>Create User</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentView('users');
                      setNewUserName('');
                      setNewUserEmail('');
                      setNewUserPassword('');
                      setNewUserRole('WAITER');
                      setNewUserBranchId('');
                      setError(null);
                    }}
                    disabled={loading}
                    className="flex-1 sm:flex-none px-6 py-3 sm:py-4 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-xl transition-all font-medium disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base"
                  >
                    <X className="w-5 h-5" />
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Users View */}
        {currentView === 'users' && selectedRestaurant && (
          <UsersView
            restaurant={selectedRestaurant}
            users={restaurantUsers}
            onBack={() => setCurrentView('restaurants')}
            onCreateUser={() => {
                    setCurrentView('create-user');
                    setNewUserName('');
                    setNewUserEmail('');
                    setNewUserPassword('');
                    setNewUserRole('WAITER');
                    setNewUserBranchId('');
                  }}
            onChangePassword={(user) => {
                        setSelectedUserForPasswordChange(user);
                        setNewUserPasswordForChange('');
                        setConfirmUserPasswordForChange('');
                        setPasswordChangeError(null);
                        setShowPasswordChangeModal(true);
                      }}
            onUpdateStatus={handleUpdateUserStatus}
          />
        )}

        {/* Change Password Modal */}
        {showPasswordChangeModal && selectedUserForPasswordChange && (
          <PasswordChangeModal
            user={selectedUserForPasswordChange}
            newPassword={newUserPasswordForChange}
            confirmPassword={confirmUserPasswordForChange}
            error={passwordChangeError}
            loading={changingUserPassword}
            onClose={() => {
                    setShowPasswordChangeModal(false);
                    setSelectedUserForPasswordChange(null);
                    setNewUserPasswordForChange('');
                    setConfirmUserPasswordForChange('');
                    setPasswordChangeError(null);
                  }}
            onPasswordChange={() => {
              if (newUserPasswordForChange !== confirmUserPasswordForChange) {
                setPasswordChangeError('Passwords do not match');
                return;
              }
              if (!newUserPasswordForChange || newUserPasswordForChange.length < 6) {
                setPasswordChangeError('Password must be at least 6 characters');
                return;
              }
              handleChangeUserPassword();
            }}
            onNewPasswordChange={setNewUserPasswordForChange}
            onConfirmPasswordChange={setConfirmUserPasswordForChange}
          />
        )}

        {/* Create Branch View - Full Page */}
        {currentView === 'create-branch' && selectedRestaurant && (
          <div className="space-y-6">
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={() => {
                    setCurrentView('branches');
                    setNewBranchName('');
                    setNewBranchAddress('');
                    setNewBranchPhone('');
                    setNewBranchEmail('');
                    setError(null);
                  }}
                  className="flex items-center gap-2 text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-amber-400 dark:hover:text-amber-400 hover:text-amber-600 transition-colors px-3 py-2 rounded-lg hover:bg-zinc-800/50 dark:hover:bg-zinc-800/50 hover:bg-zinc-100"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back to Branches
                </button>
              </div>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-600 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-600/30">
                  <GitBranch className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">Create New Branch</h2>
                  <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600">Add a new branch for <span className="text-amber-400 dark:text-amber-400 text-amber-600 font-medium">{selectedRestaurant.name}</span></p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-8 shadow-xl max-w-3xl">
              {error && (
                <div className="mb-6 p-4 bg-red-500/10 dark:bg-red-500/10 bg-red-50 border border-red-500/30 dark:border-red-500/30 border-red-300/30 rounded-lg">
                  <p className="text-red-400 dark:text-red-400 text-red-700 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); handleCreateBranch(); }} className="space-y-6">
                {/* Branch Name */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">
                    <Building2 className="w-4 h-4" />
                    <span>Branch Name <span className="text-red-400 dark:text-red-400 text-red-700">*</span></span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500 dark:text-zinc-500 text-zinc-600" />
                    <input
                      type="text"
                      value={newBranchName}
                      onChange={(e) => setNewBranchName(e.target.value)}
                      placeholder="Enter branch name"
                      required
                      className="w-full pl-12 pr-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all"
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">
                    <MapPin className="w-4 h-4" />
                    <span>Address <span className="text-red-400 dark:text-red-400 text-red-700">*</span></span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-4 w-5 h-5 text-zinc-500 dark:text-zinc-500 text-zinc-600" />
                    <textarea
                      value={newBranchAddress}
                      onChange={(e) => setNewBranchAddress(e.target.value)}
                      placeholder="Enter full address"
                      required
                      rows={3}
                      className="w-full pl-12 pr-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all resize-none"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">
                    <Phone className="w-4 h-4" />
                    <span>Phone Number <span className="text-red-400 dark:text-red-400 text-red-700">*</span></span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500 dark:text-zinc-500 text-zinc-600" />
                    <input
                      type="tel"
                      value={newBranchPhone}
                      onChange={(e) => setNewBranchPhone(e.target.value)}
                      placeholder="Enter phone number"
                      required
                      className="w-full pl-12 pr-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">
                    <Mail className="w-4 h-4" />
                    <span>Email Address (Optional)</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500 dark:text-zinc-500 text-zinc-600" />
                    <input
                      type="email"
                      value={newBranchEmail}
                      onChange={(e) => setNewBranchEmail(e.target.value)}
                      placeholder="branch@example.com"
                      className="w-full pl-12 pr-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-xl text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all"
                    />
                  </div>
                  <p className="text-zinc-500 dark:text-zinc-500 text-zinc-600 text-xs mt-2 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    Optional: Add an email address for this branch
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-6 border-t border-zinc-700/50 dark:border-zinc-700/50 border-zinc-300/50">
                  <button
                    type="submit"
                    disabled={loading || !newBranchName.trim() || !newBranchAddress.trim() || !newBranchPhone.trim()}
                    className="flex-1 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white px-6 py-4 rounded-xl transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-amber-600/30 hover:shadow-amber-600/50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Creating Branch...</span>
                      </>
                    ) : (
                      <>
                        <GitBranch className="w-5 h-5" />
                        <span>Create Branch</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentView('branches');
                      setNewBranchName('');
                      setNewBranchAddress('');
                      setNewBranchPhone('');
                      setNewBranchEmail('');
                      setError(null);
                    }}
                    disabled={loading}
                    className="px-6 py-4 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-xl transition-all font-medium disabled:opacity-50 flex items-center gap-2"
                  >
                    <X className="w-5 h-5" />
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Branches View */}
        {currentView === 'branches' && selectedRestaurant && (
          <BranchesView
            restaurant={selectedRestaurant}
            branches={restaurantBranches}
            onBack={() => setCurrentView('restaurants')}
            onCreateBranch={() => {
                    setCurrentView('create-branch');
                    setNewBranchName('');
                    setNewBranchAddress('');
                    setNewBranchPhone('');
                    setNewBranchEmail('');
                    setError(null);
                  }}
            onToggleBranch={handleUpdateBranch}
          />
        )}

        {/* QR Codes View */}
        {currentView === 'qr-codes' && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h2 className="text-2xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">QR Code Management</h2>
              {selectedRestaurant && (
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                  <button
                    onClick={() => setShowQRGenerator(true)}
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-amber-600 to-amber-500 text-white px-4 py-2 rounded-lg hover:from-amber-500 hover:to-amber-600 transition-all font-medium text-sm sm:text-base"
                  >
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="whitespace-nowrap">Generate Table QR</span>
                  </button>
                  <button
                    onClick={handleGenerateCounterQR}
                    disabled={loading}
                    className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all font-medium disabled:opacity-50 text-sm sm:text-base"
                  >
                    <QrCode className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="whitespace-nowrap">Generate Counter QR</span>
                  </button>
                </div>
              )}
            </div>

            {!selectedRestaurant && (
              <div className="bg-zinc-800 dark:bg-zinc-800 bg-zinc-100 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-8 text-center">
                <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-4">Please select a restaurant to manage QR codes</p>
                <button
                  onClick={() => setCurrentView('restaurants')}
                  className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-2 rounded-lg transition-all font-medium"
                >
                  Select Restaurant
                </button>
              </div>
            )}

            {selectedRestaurant && (
              <>
                <div className="bg-zinc-800 dark:bg-zinc-800 bg-zinc-100 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-4 sm:p-6 mb-6">
                  <p className="text-amber-50 dark:text-amber-50 text-zinc-900 text-sm sm:text-base">
                    Managing QR codes for: <span className="font-semibold">{selectedRestaurant.name}</span>
                  </p>
                </div>

                {showQRGenerator && (
                  <div className="bg-zinc-800 dark:bg-zinc-800 bg-zinc-100 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-4 sm:p-6 mb-6">
                    <h3 className="text-lg sm:text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4">Generate Table QR Code</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Table Number *</label>
                        <input
                          type="text"
                          value={newTableNumber}
                          onChange={(e) => setNewTableNumber(e.target.value)}
                          placeholder="Table number"
                          className="w-full px-4 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-200 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-500 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm sm:text-base"
                        />
                      </div>
                      <div className="flex gap-2 sm:gap-4">
                        <button
                          onClick={handleGenerateTableQR}
                          disabled={loading}
                          className="flex-1 sm:flex-none bg-amber-600 hover:bg-amber-500 text-white px-4 sm:px-6 py-2 rounded-lg transition-all font-medium disabled:opacity-50 text-sm sm:text-base"
                        >
                          Generate
                        </button>
                        <button
                          onClick={() => {
                            setShowQRGenerator(false);
                            setNewTableNumber('');
                          }}
                          className="flex-1 sm:flex-none bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 px-4 sm:px-6 py-2 rounded-lg transition-all font-medium text-sm sm:text-base"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Multiple Counter QR Codes Management */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg sm:text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">Counter QR Codes</h3>
                    <button
                      onClick={() => {
                        setShowCounterQRForm(true);
                        setNewCounterQRName('');
                        setNewCounterQRDescription('');
                        setNewCounterQRBranchId('');
                      }}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all font-medium text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Create Counter QR
                    </button>
                  </div>

                  {/* Create Counter QR Form */}
                  {showCounterQRForm && (
                    <div className="bg-zinc-800 dark:bg-zinc-800 bg-zinc-100 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-4 sm:p-6 mb-4">
                      <h4 className="text-md font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4">Create New Counter QR Code</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Name *</label>
                          <input
                            type="text"
                            value={newCounterQRName}
                            onChange={(e) => setNewCounterQRName(e.target.value)}
                            placeholder="e.g., Main Counter, Takeaway Counter"
                            className="w-full px-4 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-200 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                        />
                      </div>
                        <div>
                          <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Description</label>
                          <textarea
                            value={newCounterQRDescription}
                            onChange={(e) => setNewCounterQRDescription(e.target.value)}
                            placeholder="Optional description"
                            rows={2}
                            className="w-full px-4 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-200 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Branch (Optional)</label>
                          <select
                            value={newCounterQRBranchId}
                            onChange={(e) => setNewCounterQRBranchId(e.target.value)}
                            className="w-full px-4 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-200 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                          >
                            <option value="">No specific branch</option>
                            {restaurantBranches.map((branch) => (
                              <option key={branch._id} value={branch._id}>
                                {branch.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleCreateCounterQR}
                            disabled={loading || !newCounterQRName.trim()}
                            className="flex-1 bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-lg transition-all font-medium disabled:opacity-50"
                          >
                            Create
                          </button>
                          <button
                            onClick={() => {
                              setShowCounterQRForm(false);
                              setNewCounterQRName('');
                              setNewCounterQRDescription('');
                              setNewCounterQRBranchId('');
                            }}
                            className="flex-1 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 px-4 py-2 rounded-lg transition-all font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Counter QR Codes List */}
                  {counterQRs.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {counterQRs.filter(qr => qr?._id).map((qr) => (
                        <div
                          key={qr._id}
                          className={`bg-zinc-800 dark:bg-zinc-800 bg-zinc-100 rounded-xl border ${
                            qr.isActive 
                              ? 'border-amber-600/20 dark:border-amber-600/20 border-amber-300/20' 
                              : 'border-zinc-700/50 dark:border-zinc-700/50 border-zinc-300/50 opacity-60'
                          } p-4`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="text-md font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-1">{qr.name}</h4>
                              {qr.description && (
                                <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-1">{qr.description}</p>
                              )}
                              {qr.branch && (
                                <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600">Branch: {qr.branch.name}</p>
                              )}
                              <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium ${
                                qr.isActive
                                  ? 'bg-green-500/20 dark:bg-green-500/20 bg-green-100 text-green-400 dark:text-green-400 text-green-700'
                                  : 'bg-red-500/20 dark:bg-red-500/20 bg-red-100 text-red-400 dark:text-red-400 text-red-700'
                              }`}>
                                {qr.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-center mb-3">
                            <img
                              src={qr.qrCodeData}
                              alt={`QR Code for ${qr.name}`}
                              className="w-32 h-32 rounded-lg border border-zinc-700 dark:border-zinc-700 border-zinc-300"
                            />
                          </div>
                          <div className="space-y-2">
                            <a
                              href={qr.qrCodeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg transition-all font-medium text-xs w-full"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Open Link
                            </a>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                onClick={() => {
                                  setEditingCounterQR(qr);
                                }}
                                className="flex items-center justify-center gap-1 bg-amber-600 hover:bg-amber-500 text-white px-2 py-1.5 rounded-lg transition-all font-medium text-xs"
                              >
                                <Edit className="w-3 h-3" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleRegenerateCounterQR(qr._id)}
                                disabled={loading}
                                className="flex items-center justify-center gap-1 bg-purple-600 hover:bg-purple-500 text-white px-2 py-1.5 rounded-lg transition-all font-medium text-xs disabled:opacity-50"
                                title="Regenerate QR Code"
                            >
                                <RefreshCw className="w-3 h-3" />
                                Regen
                              </button>
                        </div>
                          <button
                              onClick={() => handleOpenManageCounterQR(qr)}
                              className="flex items-center justify-center gap-1 bg-green-600 hover:bg-green-500 text-white px-3 py-1.5 rounded-lg transition-all font-medium text-xs w-full"
                              title="Manage Menu Filters"
                          >
                              <Settings className="w-3 h-3" />
                              Manage Filters
                          </button>
                          <button
                              onClick={() => handleDeleteCounterQRById(qr._id, qr.name)}
                              className="flex items-center justify-center gap-1 bg-red-600 hover:bg-red-500 text-white px-3 py-1.5 rounded-lg transition-all font-medium text-xs w-full"
                          >
                              <Trash2 className="w-3 h-3" />
                            Delete
                          </button>
                        </div>
                      </div>
                      ))}
                  </div>
                  ) : (
                    <div className="bg-zinc-800 dark:bg-zinc-800 bg-zinc-100 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-4 sm:p-6">
                      <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm sm:text-base text-center">
                        No counter QR codes yet. Click "Create Counter QR" to add one.
                      </p>
                    </div>
                  )}
                </div>

                {/* Edit Counter QR Modal */}
                {editingCounterQR && (
                  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-zinc-800 dark:bg-zinc-800 bg-zinc-100 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                      <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4">Edit Counter QR Code</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Name *</label>
                          <input
                            type="text"
                            value={editingCounterQR.name}
                            onChange={(e) => setEditingCounterQR({ ...editingCounterQR, name: e.target.value })}
                            className="w-full px-4 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-200 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Description</label>
                          <textarea
                            value={editingCounterQR.description || ''}
                            onChange={(e) => setEditingCounterQR({ ...editingCounterQR, description: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-200 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-2">Branch</label>
                          <select
                            value={editingCounterQR.branchId || ''}
                            onChange={(e) => setEditingCounterQR({ ...editingCounterQR, branchId: e.target.value || null })}
                            className="w-full px-4 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-200 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                          >
                            <option value="">No specific branch</option>
                            {restaurantBranches.map((branch) => (
                              <option key={branch._id} value={branch._id}>
                                {branch.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="isActive"
                            checked={editingCounterQR.isActive}
                            onChange={(e) => setEditingCounterQR({ ...editingCounterQR, isActive: e.target.checked })}
                            className="w-4 h-4 rounded border-zinc-700 text-amber-600 focus:ring-amber-500"
                          />
                          <label htmlFor="isActive" className="text-sm text-amber-100 dark:text-amber-100 text-zinc-700">Active</label>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleUpdateCounterQR}
                            disabled={loading || !editingCounterQR.name.trim()}
                            className="flex-1 bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-lg transition-all font-medium disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingCounterQR(null)}
                            className="flex-1 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 px-4 py-2 rounded-lg transition-all font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <h3 className="text-lg sm:text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">Table QR Codes</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {tables.filter(table => table.qrCodeData).map((table) => (
                    <div
                      key={table._id}
                      className="bg-zinc-800 dark:bg-zinc-800 bg-zinc-100 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-4 sm:p-6"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg sm:text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">Table {table.tableNumber}</h3>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                            table.isActive
                              ? 'bg-green-500/20 dark:bg-green-500/20 bg-green-100 text-green-400 dark:text-green-400 text-green-700'
                              : 'bg-red-500/20 dark:bg-red-500/20 bg-red-100 text-red-400 dark:text-red-400 text-red-700'
                          }`}
                        >
                          {table.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {table.qrCodeData && (
                        <div className="mb-4 flex flex-col gap-3">
                          <img
                            src={table.qrCodeData}
                            alt={`Table ${table.tableNumber} QR Code`}
                            className="w-full rounded-lg"
                          />
                          {table.qrCodeUrl && (
                            <a
                              href={table.qrCodeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white px-4 py-2 rounded-lg transition-all font-medium text-sm"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Open Menu Link
                            </a>
                          )}
                        </div>
                      )}
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDownloadQR('table', table._id)}
                            className="flex-1 bg-amber-600 hover:bg-amber-500 text-white px-3 sm:px-4 py-2 rounded-lg transition-all font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
                          >
                            <Download className="w-4 h-4" />
                            <span className="hidden sm:inline">Download</span>
                            <span className="sm:hidden">DL</span>
                          </button>
                          {table.qrCodeData && (
                            <button
                              onClick={() => handleDeleteTableQR(table._id, table.tableNumber)}
                              className="bg-red-600 hover:bg-red-500 text-white px-3 sm:px-4 py-2 rounded-lg transition-all font-medium flex items-center justify-center gap-2"
                              title="Delete QR Code"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => handleOpenManageQR(table)}
                          className="w-full bg-blue-600 hover:bg-blue-500 text-white px-3 sm:px-4 py-2 rounded-lg transition-all font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
                        >
                          <Settings className="w-4 h-4" />
                          Manage QR
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Manage QR Modal */}
        {showManageQRModal && selectedTableForManage && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => {
            setShowManageQRModal(false);
            setSelectedTableForManage(null);
          }}>
            <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-2xl border border-amber-600/30 dark:border-amber-600/30 border-amber-300/30 p-6 shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto my-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-1">Manage QR Menu Filters</h3>
                  <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600">Table {selectedTableForManage.tableNumber}</p>
                </div>
                <button
                  onClick={() => {
                    setShowManageQRModal(false);
                    setSelectedTableForManage(null);
                    setSelectedLogoIndexForManageQR(null);
                  }}
                  className="w-8 h-8 rounded-lg bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-white dark:hover:text-white hover:text-zinc-900 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Veg/Non-Veg Filter */}
                <div>
                  <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">Food Type Filter</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTableMenuFilters({ ...tableMenuFilters, vegFilter: 'all' })}
                      className={`flex-1 px-4 py-2 rounded-lg transition-all font-medium ${
                        tableMenuFilters.vegFilter === 'all'
                          ? 'bg-amber-600 text-white'
                          : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300'
                      }`}
                    >
                      All Items
                    </button>
                    <button
                      onClick={() => setTableMenuFilters({ ...tableMenuFilters, vegFilter: 'veg' })}
                      className={`flex-1 px-4 py-2 rounded-lg transition-all font-medium ${
                        tableMenuFilters.vegFilter === 'veg'
                          ? 'bg-green-600 text-white'
                          : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300'
                      }`}
                    >
                      🟢 Veg Only
                    </button>
                    <button
                      onClick={() => setTableMenuFilters({ ...tableMenuFilters, vegFilter: 'non-veg' })}
                      className={`flex-1 px-4 py-2 rounded-lg transition-all font-medium ${
                        tableMenuFilters.vegFilter === 'non-veg'
                          ? 'bg-red-600 text-white'
                          : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300'
                      }`}
                    >
                      🔴 Non-Veg Only
                    </button>
                  </div>
                </div>

                {/* Logo Selection */}
                {selectedRestaurant && (selectedRestaurant.logos && selectedRestaurant.logos.length > 0 || selectedRestaurant.logoUrl) && (
                  <div>
                    <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">Select Logo</label>
                    <select
                      value={selectedLogoIndexForManageQR !== null ? selectedLogoIndexForManageQR : ''}
                      onChange={(e) => setSelectedLogoIndexForManageQR(e.target.value === '' ? null : parseInt(e.target.value))}
                      className="w-full px-4 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-200 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm"
                    >
                      <option value="">Default Logo</option>
                      {selectedRestaurant.logos?.filter(l => l?.url).map((logo, index) => (
                        <option key={index} value={index}>
                          Logo {index + 1} ({logo.type || 'regular'})
                        </option>
                      ))}
                    </select>
                    {selectedLogoIndexForManageQR !== null && selectedRestaurant.logos && selectedRestaurant.logos[selectedLogoIndexForManageQR] && (
                      <div className="mt-3 p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20">
                        <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-2">Selected Logo Preview:</p>
                        <div className={`bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 rounded-lg border border-zinc-700 dark:border-zinc-700 border-zinc-300 p-2 flex items-center justify-center ${
                          selectedRestaurant.logos[selectedLogoIndexForManageQR].type === 'horizontal' ? 'h-16' : 
                          selectedRestaurant.logos[selectedLogoIndexForManageQR].type === 'vertical' ? 'w-20 h-auto max-h-48' : 
                          'h-24'
                        }`}>
                          <img 
                            src={selectedRestaurant.logos[selectedLogoIndexForManageQR].url} 
                            alt={`Logo ${selectedLogoIndexForManageQR + 1}`} 
                            className={`${
                              selectedRestaurant.logos[selectedLogoIndexForManageQR].type === 'horizontal' ? 'w-full h-full' : 
                              selectedRestaurant.logos[selectedLogoIndexForManageQR].type === 'vertical' ? 'w-full h-auto' : 
                              'w-full h-full'
                            } object-contain`}
                          />
                        </div>
                        <p className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 mt-2 text-center">
                          Type: {selectedRestaurant.logos[selectedLogoIndexForManageQR].type || 'regular'}
                        </p>
                      </div>
                    )}
                    {selectedLogoIndexForManageQR === null && selectedRestaurant.logoUrl && (
                      <div className="mt-3 p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20">
                        <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-2">Default Logo Preview:</p>
                        <div className="bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 rounded-lg border border-zinc-700 dark:border-zinc-700 border-zinc-300 p-2 flex items-center justify-center h-24">
                          <img 
                            src={selectedRestaurant.logoUrl.startsWith('http://') || selectedRestaurant.logoUrl.startsWith('https://') 
                              ? selectedRestaurant.logoUrl 
                              : `${API_BASE_URL}${selectedRestaurant.logoUrl}`} 
                            alt="Default Logo" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Category Filter */}
                {availableCategories.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">Category Filter</label>
                    <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-3">Select categories to show (leave empty to show all)</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {availableCategories.map((category) => (
                        <label key={category} className="flex items-center gap-3 p-2 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={tableMenuFilters.categories.includes(category)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTableMenuFilters({
                                  ...tableMenuFilters,
                                  categories: [...tableMenuFilters.categories, category],
                                });
                              } else {
                                setTableMenuFilters({
                                  ...tableMenuFilters,
                                  categories: tableMenuFilters.categories.filter(c => c !== category),
                                });
                              }
                            }}
                            className="w-4 h-4 text-amber-600 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 border-zinc-600 dark:border-zinc-600 border-zinc-300 rounded focus:ring-amber-500"
                          />
                          <span className="text-sm text-amber-50 dark:text-amber-50 text-zinc-900">{category}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show Only Available */}
                <div>
                  <label className="flex items-center justify-between p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg">
                    <div>
                      <span className="text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 block">Show Only Available Items</span>
                      <span className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600">Hide unavailable items from menu</span>
                    </div>
                    <button
                      onClick={() => setTableMenuFilters({ ...tableMenuFilters, showOnlyAvailable: !tableMenuFilters.showOnlyAvailable })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        tableMenuFilters.showOnlyAvailable ? 'bg-amber-600' : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          tableMenuFilters.showOnlyAvailable ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </label>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20">
                  <button
                    onClick={handleUpdateTableMenuFilters}
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white px-6 py-3 rounded-xl transition-all font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Save Filters
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowManageQRModal(false);
                      setSelectedTableForManage(null);
                      setSelectedLogoIndexForManageQR(null);
                    }}
                    className="px-6 py-3 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-xl transition-all font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Manage Counter QR Menu Filters Modal */}
        {showManageCounterQRModal && selectedCounterQRForManage && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => {
            setShowManageCounterQRModal(false);
            setSelectedCounterQRForManage(null);
          }}>
            <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-2xl border border-amber-600/30 dark:border-amber-600/30 border-amber-300/30 p-6 shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto my-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-1">Manage Counter QR Menu Filters</h3>
                  <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600">{selectedCounterQRForManage.name}</p>
                </div>
                <button
                  onClick={() => {
                    setShowManageCounterQRModal(false);
                    setSelectedCounterQRForManage(null);
                    setSelectedLogoIndexForManageCounterQR(null);
                  }}
                  className="w-8 h-8 rounded-lg bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-white dark:hover:text-white hover:text-zinc-900 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Veg/Non-Veg Filter */}
                <div>
                  <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">Food Type Filter</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCounterQRMenuFilters({ ...counterQRMenuFilters, vegFilter: 'all' })}
                      className={`flex-1 px-4 py-2 rounded-lg transition-all font-medium ${
                        counterQRMenuFilters.vegFilter === 'all'
                          ? 'bg-amber-600 text-white'
                          : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300'
                      }`}
                    >
                      All Items
                    </button>
                    <button
                      onClick={() => setCounterQRMenuFilters({ ...counterQRMenuFilters, vegFilter: 'veg' })}
                      className={`flex-1 px-4 py-2 rounded-lg transition-all font-medium ${
                        counterQRMenuFilters.vegFilter === 'veg'
                          ? 'bg-green-600 text-white'
                          : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300'
                      }`}
                    >
                      🟢 Veg Only
                    </button>
                    <button
                      onClick={() => setCounterQRMenuFilters({ ...counterQRMenuFilters, vegFilter: 'non-veg' })}
                      className={`flex-1 px-4 py-2 rounded-lg transition-all font-medium ${
                        counterQRMenuFilters.vegFilter === 'non-veg'
                          ? 'bg-red-600 text-white'
                          : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300'
                      }`}
                    >
                      🔴 Non-Veg Only
                    </button>
                  </div>
                </div>

                {/* Logo Selection */}
                {selectedRestaurant && (selectedRestaurant.logos && selectedRestaurant.logos.length > 0 || selectedRestaurant.logoUrl) && (
                  <div>
                    <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">Select Logo</label>
                    <select
                      value={selectedLogoIndexForManageCounterQR !== null ? selectedLogoIndexForManageCounterQR : ''}
                      onChange={(e) => setSelectedLogoIndexForManageCounterQR(e.target.value === '' ? null : parseInt(e.target.value))}
                      className="w-full px-4 py-2 bg-zinc-900 dark:bg-zinc-900 bg-zinc-200 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm"
                    >
                      <option value="">Default Logo</option>
                      {selectedRestaurant.logos?.filter(l => l?.url).map((logo, index) => (
                        <option key={index} value={index}>
                          Logo {index + 1} ({logo.type || 'regular'})
                        </option>
                      ))}
                    </select>
                    {selectedLogoIndexForManageCounterQR !== null && selectedRestaurant.logos && selectedRestaurant.logos[selectedLogoIndexForManageCounterQR] && (
                      <div className="mt-3 p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20">
                        <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-2">Selected Logo Preview:</p>
                        <div className={`bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 rounded-lg border border-zinc-700 dark:border-zinc-700 border-zinc-300 p-2 flex items-center justify-center ${
                          selectedRestaurant.logos[selectedLogoIndexForManageCounterQR].type === 'horizontal' ? 'h-16' : 
                          selectedRestaurant.logos[selectedLogoIndexForManageCounterQR].type === 'vertical' ? 'w-20 h-auto max-h-48' : 
                          'h-24'
                        }`}>
                          <img 
                            src={selectedRestaurant.logos[selectedLogoIndexForManageCounterQR].url} 
                            alt={`Logo ${selectedLogoIndexForManageCounterQR + 1}`} 
                            className={`${
                              selectedRestaurant.logos[selectedLogoIndexForManageCounterQR].type === 'horizontal' ? 'w-full h-full' : 
                              selectedRestaurant.logos[selectedLogoIndexForManageCounterQR].type === 'vertical' ? 'w-full h-auto' : 
                              'w-full h-full'
                            } object-contain`}
                          />
                        </div>
                        <p className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 mt-2 text-center">
                          Type: {selectedRestaurant.logos[selectedLogoIndexForManageCounterQR].type || 'regular'}
                        </p>
                      </div>
                    )}
                    {selectedLogoIndexForManageCounterQR === null && selectedRestaurant.logoUrl && (
                      <div className="mt-3 p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20">
                        <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-2">Default Logo Preview:</p>
                        <div className="bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 rounded-lg border border-zinc-700 dark:border-zinc-700 border-zinc-300 p-2 flex items-center justify-center h-24">
                          <img 
                            src={selectedRestaurant.logoUrl.startsWith('http://') || selectedRestaurant.logoUrl.startsWith('https://') 
                              ? selectedRestaurant.logoUrl 
                              : `${API_BASE_URL}${selectedRestaurant.logoUrl}`} 
                            alt="Default Logo" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Category Filter */}
                {availableCategories.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 mb-3">Category Filter</label>
                    <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-3">Select categories to show (leave empty to show all)</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {availableCategories.map((category) => (
                        <label key={category} className="flex items-center gap-3 p-2 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={counterQRMenuFilters.categories.includes(category)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCounterQRMenuFilters({
                                  ...counterQRMenuFilters,
                                  categories: [...counterQRMenuFilters.categories, category],
                                });
                              } else {
                                setCounterQRMenuFilters({
                                  ...counterQRMenuFilters,
                                  categories: counterQRMenuFilters.categories.filter(c => c !== category),
                                });
                              }
                            }}
                            className="w-4 h-4 text-amber-600 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 border-zinc-600 dark:border-zinc-600 border-zinc-300 rounded focus:ring-amber-500"
                          />
                          <span className="text-sm text-amber-50 dark:text-amber-50 text-zinc-900">{category}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show Only Available */}
                <div>
                  <label className="flex items-center justify-between p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg">
                    <div>
                      <span className="text-sm font-medium text-amber-100 dark:text-amber-100 text-zinc-700 block">Show Only Available Items</span>
                      <span className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600">Hide unavailable items from menu</span>
                    </div>
                    <button
                      onClick={() => setCounterQRMenuFilters({ ...counterQRMenuFilters, showOnlyAvailable: !counterQRMenuFilters.showOnlyAvailable })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        counterQRMenuFilters.showOnlyAvailable ? 'bg-amber-600' : 'bg-zinc-700 dark:bg-zinc-700 bg-zinc-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          counterQRMenuFilters.showOnlyAvailable ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </label>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20">
                  <button
                    onClick={handleUpdateCounterQRMenuFilters}
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white px-6 py-3 rounded-xl transition-all font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Save Filters
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowManageCounterQRModal(false);
                      setSelectedCounterQRForManage(null);
                    }}
                    className="px-6 py-3 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-xl transition-all font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentView === 'reports' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">Platform Reports</h2>
              <div className="flex items-center gap-2 text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">
                <FileText className="w-4 h-4" />
                <span>Software Analytics</span>
              </div>
            </div>

            {loading && !reports ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              </div>
            ) : reports ? (
              <div className="space-y-6">
                {/* Platform Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">Total Restaurants</span>
                      <Building2 className="w-5 h-5 text-amber-500" />
                    </div>
                    <p className="text-3xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">{reports.platform?.totalRestaurants || 0}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 mt-1">{reports.platform?.activeRestaurants || 0} active</p>
                  </div>

                  <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">Total Users</span>
                      <Users className="w-5 h-5 text-amber-500" />
                    </div>
                    <p className="text-3xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">{reports.platform?.totalUsers || 0}</p>
                  </div>

                  <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">Total Orders</span>
                      <Activity className="w-5 h-5 text-amber-500" />
                    </div>
                    <p className="text-3xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">{reports.platform?.totalOrders || 0}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 mt-1">{reports.platform?.paidOrders || 0} paid</p>
                  </div>

                  <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">Total Revenue</span>
                      <TrendingUp className="w-5 h-5 text-amber-500" />
                    </div>
                    <p className="text-3xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">
                      ₹{reports.platform?.totalRevenue?.toLocaleString('en-IN') || '0'}
                    </p>
                  </div>
                </div>

                {/* Time-based Statistics */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4">Time-based Statistics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg p-4 border border-amber-600/10 dark:border-amber-600/10 border-zinc-300/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">Today</span>
                        <Calendar className="w-5 h-5 text-amber-500" />
                      </div>
                      <p className="text-2xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">{reports.timeStats?.today?.orders || 0} orders</p>
                      <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600 mt-1">
                        ₹{reports.timeStats?.today?.revenue?.toLocaleString('en-IN') || '0'}
                      </p>
                    </div>

                    <div className="bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg p-4 border border-amber-600/10 dark:border-amber-600/10 border-zinc-300/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">This Week</span>
                        <Calendar className="w-5 h-5 text-amber-500" />
                      </div>
                      <p className="text-2xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">{reports.timeStats?.week?.orders || 0} orders</p>
                    </div>

                    <div className="bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg p-4 border border-amber-600/10 dark:border-amber-600/10 border-zinc-300/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">This Month</span>
                        <Calendar className="w-5 h-5 text-amber-500" />
                      </div>
                      <p className="text-2xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">{reports.timeStats?.month?.orders || 0} orders</p>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={[
                      { period: 'Today', orders: reports.timeStats?.today?.orders || 0, revenue: reports.timeStats?.today?.revenue || 0 },
                      { period: 'This Week', orders: reports.timeStats?.week?.orders || 0, revenue: 0 },
                      { period: 'This Month', orders: reports.timeStats?.month?.orders || 0, revenue: 0 }
                    ]}>
                      <defs>
                        <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="period" stroke="#9ca3af" />
                      <YAxis stroke="#9ca3af" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1f2937', 
                          border: '1px solid #f59e0b',
                          borderRadius: '8px',
                          color: '#f3f4f6'
                        }} 
                      />
                      <Area type="monotone" dataKey="orders" stroke="#f59e0b" fillOpacity={1} fill="url(#colorOrders)" name="Orders" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Orders Statistics with Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Orders by Status - Pie Chart */}
                  <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                    <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4">Orders by Status</h3>
                    {reports.orders?.byStatus && Object.keys(reports.orders.byStatus).length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={Object.entries(reports.orders.byStatus).map(([status, count]: [string, any]) => ({
                              name: status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                              value: count
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {Object.entries(reports.orders.byStatus).map((_, index) => (
                              <Cell key={`cell-${index}`} fill={['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'][index % 6]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#1f2937', 
                              border: '1px solid #f59e0b',
                              borderRadius: '8px',
                              color: '#f3f4f6'
                            }} 
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-12 text-zinc-400">No order data available</div>
                    )}
                  </div>

                  {/* Orders by Type - Bar Chart */}
                  <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                    <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4">Orders by Type</h3>
                    {reports.orders?.byType && Object.keys(reports.orders.byType).length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={Object.entries(reports.orders.byType).map(([type, data]: [string, any]) => ({
                          name: type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                          orders: data.count || 0,
                          revenue: data.revenue || 0
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="name" stroke="#9ca3af" />
                          <YAxis stroke="#9ca3af" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#1f2937', 
                              border: '1px solid #f59e0b',
                              borderRadius: '8px',
                              color: '#f3f4f6'
                            }} 
                          />
                          <Legend />
                          <Bar dataKey="orders" fill="#f59e0b" name="Orders" />
                          <Bar dataKey="revenue" fill="#3b82f6" name="Revenue (₹)" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-center py-12 text-zinc-400">No order type data available</div>
                    )}
                  </div>
                </div>

                {/* Plan Distribution - Pie Chart */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4">Plan Distribution</h3>
                  {reports.plans && Object.keys(reports.plans).length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={Object.entries(reports.plans).map(([plan, count]: [string, any]) => ({
                              name: plan,
                              value: count
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {Object.entries(reports.plans).map(([plan], index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={
                                  plan === 'PLATINUM' ? '#f59e0b' :
                                  plan === 'GOLD' ? '#3b82f6' :
                                  '#6b7280'
                                } 
                              />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#1f2937', 
                              border: '1px solid #f59e0b',
                              borderRadius: '8px',
                              color: '#f3f4f6'
                            }} 
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="flex flex-col justify-center space-y-3">
                        {reports.plans && Object.entries(reports.plans).map(([plan, count]: [string, any]) => (
                          <div key={plan} className={`p-4 rounded-lg border ${
                            plan === 'PLATINUM' ? 'bg-amber-600/20 border-amber-600/30' :
                            plan === 'GOLD' ? 'bg-blue-600/20 border-blue-600/30' :
                            'bg-zinc-700/20 border-zinc-600/30'
                          }`}>
                            <div className="flex items-center justify-between">
                              <p className={`text-lg font-serif ${
                                plan === 'PLATINUM' ? 'text-amber-50' :
                                plan === 'GOLD' ? 'text-blue-50' :
                                'text-zinc-50'
                              }`}>{plan}</p>
                              <p className="text-2xl font-bold text-amber-50">{count}</p>
                            </div>
                            <p className="text-xs text-zinc-400 mt-1">restaurants</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-zinc-400">No plan data available</div>
                  )}
                </div>

                {/* User Role Distribution - Bar Chart */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4">User Role Distribution</h3>
                  {reports.users && Object.keys(reports.users).length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={Object.entries(reports.users).map(([role, count]: [string, any]) => ({
                        name: role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                        users: count
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="name" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1f2937', 
                            border: '1px solid #f59e0b',
                            borderRadius: '8px',
                            color: '#f3f4f6'
                          }} 
                        />
                        <Bar dataKey="users" fill="#f59e0b" name="Users" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-12 text-zinc-400">No user data available</div>
                  )}
                </div>


                {/* Platform Activity Log */}
                {reports.recentActivity && reports.recentActivity.length > 0 && (
                  <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                    <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4">Platform Activity Log</h3>
                    <div className="space-y-3">
                      {reports.recentActivity.map((activity: PlatformStats['recentActivity'][0], index: number) => (
                        <div key={index} className="flex items-start gap-3 p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg border border-amber-600/10 dark:border-amber-600/10 border-zinc-300/20">
                          <Activity className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-amber-50 dark:text-amber-50 text-zinc-900 text-sm">
                              <span className="font-medium capitalize">{activity.action?.replace('_', ' ')}</span>
                              {activity.userId && ` by ${activity.userId.name || activity.userId.email}`}
                              {activity.restaurantId && ` for ${activity.restaurantId.name}`}
                            </p>
                            <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600 mt-1">
                              {new Date(activity.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-zinc-600 dark:text-zinc-600 text-zinc-400 mx-auto mb-4" />
                <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600">No reports data available</p>
              </div>
            )}
          </div>
        )}

        {currentView === 'profile' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">Profile Settings</h2>
              <div className="flex items-center gap-2 text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">
                <UserCircle className="w-4 h-4" />
                <span>Manage your account</span>
              </div>
            </div>

            {loading && !currentUser ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              </div>
            ) : currentUser ? (
              <div className="space-y-6">
                {/* Profile Header */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                  <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-600 to-amber-500 flex items-center justify-center text-white font-bold text-2xl">
                      {currentUser.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-1">
                        {currentUser.name}
                      </h3>
                      <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-2">{currentUser.email}</p>
                      <span className="inline-flex items-center gap-2 px-3 py-1 bg-amber-600/20 dark:bg-amber-600/20 bg-amber-100 text-amber-400 dark:text-amber-400 text-amber-700 rounded-lg text-sm font-medium">
                        <Shield className="w-4 h-4" />
                        Super Admin
                      </span>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4">Personal Information</h3>
                  
                  {/* Name Field */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-2">
                      Full Name
                    </label>
                    {!editingName ? (
                      <div className="flex items-center justify-between p-4 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg border border-amber-600/10 dark:border-amber-600/10 border-zinc-300/20">
                        <span className="text-amber-50 dark:text-amber-50 text-zinc-900">{currentUser.name}</span>
                        <button
                          onClick={() => {
                            setEditingName(true);
                            setProfileName(currentUser.name);
                          }}
                          className="flex items-center gap-2 px-3 py-1.5 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-700 rounded-lg transition-all text-sm"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={profileName}
                          onChange={(e) => setProfileName(e.target.value)}
                          className="w-full px-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 rounded-lg text-amber-50 dark:text-amber-50 text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                          placeholder="Enter your name"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleUpdateName}
                            disabled={loading || !profileName.trim()}
                            className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Check className="w-4 h-4" />
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingName(false);
                              setProfileName(currentUser.name);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-700 rounded-lg transition-all font-medium"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-2">
                      Email Address
                    </label>
                    {!editingEmail ? (
                      <div className="flex items-center justify-between p-4 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg border border-amber-600/10 dark:border-amber-600/10 border-zinc-300/20">
                        <span className="text-amber-50 dark:text-amber-50 text-zinc-900">{currentUser.email}</span>
                        <button
                          onClick={() => {
                            setEditingEmail(true);
                            setProfileEmail(currentUser.email);
                          }}
                          className="flex items-center gap-2 px-3 py-1.5 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-700 rounded-lg transition-all text-sm"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <input
                          type="email"
                          value={profileEmail}
                          onChange={(e) => setProfileEmail(e.target.value)}
                          className="w-full px-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 rounded-lg text-amber-50 dark:text-amber-50 text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                          placeholder="Enter your email"
                        />
                        <p className="text-xs text-zinc-400 dark:text-zinc-400 text-zinc-600">
                          A confirmation email will be sent to your new email address.
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={handleUpdateEmail}
                            disabled={loading || !profileEmail.trim()}
                            className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Check className="w-4 h-4" />
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingEmail(false);
                              setProfileEmail(currentUser.email);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-700 rounded-lg transition-all font-medium"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Change Password */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4 flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    Change Password
                  </h3>
                  
                  {passwordSuccess && (
                    <div className="mb-4 p-4 bg-green-500/10 dark:bg-green-500/10 bg-green-50 border border-green-500/30 dark:border-green-500/30 border-green-300 rounded-lg">
                      <p className="text-green-400 dark:text-green-400 text-green-700 text-sm">
                        Password changed successfully!
                      </p>
                    </div>
                  )}

                  {passwordError && (
                    <div className="mb-4 p-4 bg-red-500/10 dark:bg-red-500/10 bg-red-50 border border-red-500/30 dark:border-red-500/30 border-red-300 rounded-lg">
                      <p className="text-red-400 dark:text-red-400 text-red-700 text-sm">{passwordError}</p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-2">
                        Current Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400 dark:text-zinc-400 text-zinc-600" />
                        <input
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 rounded-lg text-amber-50 dark:text-amber-50 text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                          placeholder="Enter current password"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400 dark:text-zinc-400 text-zinc-600" />
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 rounded-lg text-amber-50 dark:text-amber-50 text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                          placeholder="Enter new password (min. 6 characters)"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-2">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400 dark:text-zinc-400 text-zinc-600" />
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 rounded-lg text-amber-50 dark:text-amber-50 text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                          placeholder="Confirm new password"
                        />
                      </div>
                    </div>

                    <button
                      onClick={handleChangePassword}
                      disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {changingPassword ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Changing Password...
                        </>
                      ) : (
                        <>
                          <Key className="w-5 h-5" />
                          Change Password
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Theme Settings */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Appearance Settings
                  </h3>
                  
                  <div className="flex items-center justify-between p-4 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg border border-amber-600/10 dark:border-amber-600/10 border-zinc-300/20">
                    <div className="flex items-center gap-3">
                      {theme === 'dark' ? (
                        <Moon className="w-5 h-5 text-amber-500" />
                      ) : (
                        <Sun className="w-5 h-5 text-amber-500" />
                      )}
                      <div>
                        <p className="text-amber-50 dark:text-amber-50 text-zinc-900 font-medium">Theme</p>
                        <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">
                          {theme === 'dark' ? 'Dark mode' : 'Light mode'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={toggleTheme}
                      className={`relative w-14 h-7 rounded-full p-1 transition-colors ${
                        theme === 'dark' ? 'bg-amber-600' : 'bg-zinc-300'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-full bg-white transition-transform ${
                          theme === 'dark' ? 'translate-x-7' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Account Information */}
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-xl border border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 p-6">
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    Account Information
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg">
                      <span className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">User ID</span>
                      <span className="text-amber-50 dark:text-amber-50 text-zinc-900 font-mono text-sm">{currentUser.id}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-lg">
                      <span className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">Role</span>
                      <span className="inline-flex items-center gap-2 px-3 py-1 bg-amber-600/20 dark:bg-amber-600/20 bg-amber-100 text-amber-400 dark:text-amber-400 text-amber-700 rounded-lg text-sm font-medium">
                        <Shield className="w-4 h-4" />
                        Super Admin
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <UserCircle className="w-16 h-16 text-zinc-600 dark:text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600">Unable to load profile information</p>
              </div>
            )}
          </div>
        )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      {/* Test Print Modal */}
      {showTestPrintModal && selectedRestaurantForPrint && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto my-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-600/20 dark:bg-amber-600/20 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Printer className="w-6 h-6 text-amber-400 dark:text-amber-400 text-amber-700" />
                </div>
                <div>
                  <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">Test Print</h3>
                  <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">{selectedRestaurantForPrint.name}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowTestPrintModal(false);
                  setSelectedRestaurantForPrint(null);
                  setRestaurantOrders([]);
                  setSelectedOrderId('');
                  setSelectedPrintType('VEG_KOT');
                }}
                className="text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-zinc-200 dark:hover:text-zinc-200 hover:text-zinc-800 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Important Note */}
            <div className="mb-6 p-4 bg-amber-500/10 dark:bg-amber-500/10 bg-amber-50 border border-amber-500/30 dark:border-amber-500/30 border-amber-300/30 rounded-xl">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-400 dark:text-amber-400 text-amber-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-200 dark:text-amber-200 text-amber-800 mb-1">
                    ✅ This PDF is exactly what will be printed
                  </p>
                  <p className="text-xs text-amber-300/80 dark:text-amber-300/80 text-amber-700 space-y-1">
                    <span className="block">• <strong>KOT prints:</strong> 80mm thermal printer format - matches the ESC/POS commands sent to your printer</span>
                    <span className="block">• <strong>Full Bill:</strong> A4 format for regular printers</span>
                    <span className="block">• <strong>Content & Layout:</strong> Identical to actual print output - same order details, items, prices, and formatting</span>
                    <span className="block">• When you configure your printer in the system, this exact format will be sent automatically</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Order Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">
                Select Order (Optional - leave empty for sample order)
              </label>
              {loadingOrders ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-400 dark:text-amber-400 text-amber-700" />
                </div>
              ) : (
                <select
                  value={selectedOrderId}
                  onChange={(e) => setSelectedOrderId(e.target.value)}
                  className="w-full px-4 py-2.5 bg-zinc-900 dark:bg-zinc-900 bg-zinc-100 border border-zinc-700 dark:border-zinc-700 border-zinc-300 rounded-lg text-white dark:text-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                >
                  <option value="">Use Sample Order</option>
                  {restaurantOrders.map((order) => {
                    const orderNumber = order.orderNumber || order._id.toString().slice(-6);
                    const orderDate = new Date(order.createdAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
                    const customerName = order.customerName || 'Customer';
                    const tableInfo = order.tableNumber ? `Table ${order.tableNumber}` : order.orderType;
                    return (
                      <option key={order._id} value={order._id}>
                        #{orderNumber} - {customerName} - {tableInfo} - {orderDate} - ₹{order.totalAmount.toFixed(2)}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>

            {/* Print Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-amber-50 dark:text-amber-50 text-zinc-900 mb-3">
                Select Print Type
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <button
                  onClick={() => setSelectedPrintType('VEG_KOT')}
                  className={`px-4 py-3 rounded-xl border transition-all font-medium text-sm ${
                    selectedPrintType === 'VEG_KOT'
                      ? 'bg-green-600/30 dark:bg-green-600/30 bg-green-100 border-green-600/50 dark:border-green-600/50 border-green-500/50 text-green-400 dark:text-green-400 text-green-700'
                      : 'bg-zinc-700/50 dark:bg-zinc-700/50 bg-zinc-200 border-zinc-600/30 dark:border-zinc-600/30 border-zinc-400/30 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600/50 dark:hover:bg-zinc-600/50 hover:bg-zinc-300'
                  }`}
                >
                  🥬 VEG KOT
                </button>
                <button
                  onClick={() => setSelectedPrintType('NON_VEG_KOT')}
                  className={`px-4 py-3 rounded-xl border transition-all font-medium text-sm ${
                    selectedPrintType === 'NON_VEG_KOT'
                      ? 'bg-red-600/30 dark:bg-red-600/30 bg-red-100 border-red-600/50 dark:border-red-600/50 border-red-500/50 text-red-400 dark:text-red-400 text-red-700'
                      : 'bg-zinc-700/50 dark:bg-zinc-700/50 bg-zinc-200 border-zinc-600/30 dark:border-zinc-600/30 border-zinc-400/30 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600/50 dark:hover:bg-zinc-600/50 hover:bg-zinc-300'
                  }`}
                >
                  🍗 NON-VEG KOT
                </button>
                <button
                  onClick={() => setSelectedPrintType('BEVERAGE')}
                  className={`px-4 py-3 rounded-xl border transition-all font-medium text-sm ${
                    selectedPrintType === 'BEVERAGE'
                      ? 'bg-blue-600/30 dark:bg-blue-600/30 bg-blue-100 border-blue-600/50 dark:border-blue-600/50 border-blue-500/50 text-blue-400 dark:text-blue-400 text-blue-700'
                      : 'bg-zinc-700/50 dark:bg-zinc-700/50 bg-zinc-200 border-zinc-600/30 dark:border-zinc-600/30 border-zinc-400/30 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600/50 dark:hover:bg-zinc-600/50 hover:bg-zinc-300'
                  }`}
                >
                  🥤 BEVERAGE
                </button>
                <button
                  onClick={() => setSelectedPrintType('VEG')}
                  className={`px-4 py-3 rounded-xl border transition-all font-medium text-sm ${
                    selectedPrintType === 'VEG'
                      ? 'bg-green-600/20 dark:bg-green-600/20 bg-green-50 border-green-600/30 dark:border-green-600/30 border-green-500/30 text-green-300 dark:text-green-300 text-green-600'
                      : 'bg-zinc-700/50 dark:bg-zinc-700/50 bg-zinc-200 border-zinc-600/30 dark:border-zinc-600/30 border-zinc-400/30 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600/50 dark:hover:bg-zinc-600/50 hover:bg-zinc-300'
                  }`}
                >
                  VEG (Legacy)
                </button>
                <button
                  onClick={() => setSelectedPrintType('NON-VEG')}
                  className={`px-4 py-3 rounded-xl border transition-all font-medium text-sm ${
                    selectedPrintType === 'NON-VEG'
                      ? 'bg-red-600/20 dark:bg-red-600/20 bg-red-50 border-red-600/30 dark:border-red-600/30 border-red-500/30 text-red-300 dark:text-red-300 text-red-600'
                      : 'bg-zinc-700/50 dark:bg-zinc-700/50 bg-zinc-200 border-zinc-600/30 dark:border-zinc-600/30 border-zinc-400/30 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600/50 dark:hover:bg-zinc-600/50 hover:bg-zinc-300'
                  }`}
                >
                  NON-VEG (Legacy)
                </button>
                <button
                  onClick={() => setSelectedPrintType('FULL_BILL')}
                  className={`px-4 py-3 rounded-xl border transition-all font-medium text-sm ${
                    selectedPrintType === 'FULL_BILL'
                      ? 'bg-amber-600/30 dark:bg-amber-600/30 bg-amber-100 border-amber-600/50 dark:border-amber-600/50 border-amber-500/50 text-amber-400 dark:text-amber-400 text-amber-700'
                      : 'bg-zinc-700/50 dark:bg-zinc-700/50 bg-zinc-200 border-zinc-600/30 dark:border-zinc-600/30 border-zinc-400/30 text-zinc-300 dark:text-zinc-300 text-zinc-700 hover:bg-zinc-600/50 dark:hover:bg-zinc-600/50 hover:bg-zinc-300'
                  }`}
                >
                  📄 FULL BILL
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={async () => {
                  try {
                    setTestingPrint(true);
                    setError(null);
                    const blob = await superAdminAPI.testPrintPDF(
                      selectedRestaurantForPrint._id,
                      selectedPrintType,
                      selectedOrderId || undefined
                    );
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    const orderNumber = selectedOrderId ? restaurantOrders.find(o => o._id === selectedOrderId)?.orderNumber || 'order' : 'sample';
                    const typeLabel = selectedPrintType.toLowerCase().replace('_', '-');
                    a.download = `test-${typeLabel}-${orderNumber}-${selectedRestaurantForPrint.name.replace(/\s+/g, '-')}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  } catch (err) {
                    logger.error('Test print error:', err);
                    setError(err instanceof Error ? err.message : 'Failed to generate test print');
                  } finally {
                    setTestingPrint(false);
                  }
                }}
                disabled={testingPrint}
                className="flex-1 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white px-6 py-3 rounded-xl transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-amber-600/30"
              >
                {testingPrint ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Generating PDF...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    <span>Download PDF</span>
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowTestPrintModal(false);
                  setSelectedRestaurantForPrint(null);
                  setRestaurantOrders([]);
                  setSelectedOrderId('');
                  setSelectedPrintType('VEG_KOT');
                }}
                className="px-6 py-3 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-xl transition-all font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {clearDataConfirm.isOpen && clearDataConfirm.restaurant && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-2xl border border-orange-600/30 dark:border-orange-600/30 border-orange-300/30 shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto my-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-600/20 dark:bg-orange-600/20 bg-orange-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-400 dark:text-orange-400 text-orange-700" />
              </div>
              <div>
                <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">Delete Restaurant Data</h3>
                <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">Restaurant will remain active</p>
              </div>
            </div>

            <div className="mb-6 p-4 bg-orange-500/10 dark:bg-orange-500/10 bg-orange-50 border border-orange-500/30 dark:border-orange-500/30 border-orange-300/30 rounded-xl">
              <p className="text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">
                Are you sure you want to clear all data for <span className="font-semibold text-orange-400 dark:text-orange-400 text-orange-700">{clearDataConfirm.restaurant.name}</span>?
              </p>
              <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">
                This will permanently delete:
              </p>
              <ul className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm mt-2 ml-4 list-disc space-y-1">
                <li>All users associated with this restaurant</li>
                <li>All branches and locations</li>
                <li>All menu items and categories</li>
                <li>All orders and order history</li>
                <li>All table and counter QR data</li>
                <li>All reservations</li>
                <li>All printer and payment config data</li>
              </ul>
              <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm mt-3">
                The restaurant profile itself will NOT be deleted.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => handleClearRestaurantData(clearDataConfirm.restaurant!)}
                disabled={clearingRestaurantData || loadingRestaurants}
                className="flex-1 bg-orange-600 hover:bg-orange-500 text-white px-6 py-3 rounded-xl transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {clearingRestaurantData ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Clearing...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    <span>Delete Data Only</span>
                  </>
                )}
              </button>
              <button
                onClick={() => setClearDataConfirm({ isOpen: false, restaurant: null })}
                disabled={clearingRestaurantData}
                className="flex-1 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 px-6 py-3 rounded-xl transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm.isOpen && deleteConfirm.restaurant && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-800 dark:to-zinc-900 from-white to-zinc-50 rounded-2xl border border-red-600/30 dark:border-red-600/30 border-red-300/30 shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto my-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-600/20 dark:bg-red-600/20 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-400 dark:text-red-400 text-red-700" />
              </div>
              <div>
                <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900">Delete Restaurant</h3>
                <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6 p-4 bg-red-500/10 dark:bg-red-500/10 bg-red-50 border border-red-500/30 dark:border-red-500/30 border-red-300/30 rounded-xl">
              <p className="text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">
                Are you sure you want to delete <span className="font-semibold text-red-400 dark:text-red-400 text-red-700">{deleteConfirm.restaurant.name}</span>?
              </p>
              <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">
                This will permanently delete:
              </p>
              <ul className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm mt-2 ml-4 list-disc space-y-1">
                <li>All users associated with this restaurant</li>
                <li>All branches and locations</li>
                <li>All menu items and categories</li>
                <li>All orders and order history</li>
                <li>All tables and QR codes</li>
                <li>All reservations</li>
                <li>The restaurant logo file</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => handleDeleteRestaurant(deleteConfirm.restaurant!)}
                disabled={deletingRestaurant || loadingRestaurants}
                className="flex-1 bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-xl transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deletingRestaurant ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Delete Forever</span>
                  </>
                )}
              </button>
              <button
                onClick={() => setDeleteConfirm({ isOpen: false, restaurant: null })}
                disabled={deletingRestaurant}
                className="flex-1 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 px-6 py-3 rounded-xl transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Crop Modal */}
      {showCropModal && imageToCrop && (
        <ImageCropModal
          imageSrc={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            setShowCropModal(false);
            setImageToCrop(null);
            setLogoFile(null);
            setLogoPreview(null);
          }}
          aspect={
            logoType === 'regular' ? 1 : 
            logoType === 'horizontal' ? 500/200 : // 500x200 aspect ratio (2.5:1)
            logoType === 'vertical' ? 200/500 : // 200x500 aspect ratio (0.4:1 or 2:5)
            undefined
          }
        />
      )}

      {/* PDF Upload Preview Modal */}
      {showPDFUploadModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/90 dark:from-zinc-800 dark:to-zinc-800/90 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-1">Extracted Menu Preview</h2>
                  <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600">
                    Review the extracted menu items and confirm to save them
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPDFUploadModal(false);
                    setExtractedMenuData([]);
                  }}
                  className="text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-zinc-300 dark:hover:text-zinc-300 hover:text-zinc-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {extractedMenuData && extractedMenuData.length > 0 ? (
                extractedMenuData.map((categoryData, idx) => (
                  <div key={idx} className="bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl p-4 border border-zinc-700 dark:border-zinc-700 border-zinc-300">
                    <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-4">
                      {categoryData.category || `Category ${idx + 1}`}
                    </h3>
                    <div className="space-y-2">
                      {categoryData.items && categoryData.items.length > 0 ? (
                        categoryData.items.map((item, itemIdx) => (
                        <div key={itemIdx} className="flex items-center justify-between p-3 bg-zinc-800/50 dark:bg-zinc-800/50 bg-zinc-50 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-amber-50 dark:text-amber-50 text-zinc-900 font-medium">{item.name}</span>
                              {item.itemType && (
                                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                  item.itemType === 'veg'
                                    ? 'bg-green-500/20 text-green-400 dark:text-green-400 text-green-700 border border-green-500/30' 
                                    : item.itemType === 'non-veg'
                                    ? 'bg-red-500/20 text-red-400 dark:text-red-400 text-red-700 border border-red-500/30'
                                    : 'bg-blue-500/20 text-blue-400 dark:text-blue-400 text-blue-700 border border-blue-500/30'
                                }`}>
                                  {item.itemType === 'veg' ? '🟢 VEG' : item.itemType === 'non-veg' ? '🔴 NON-VEG' : '🥤 BEVERAGE'}
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600 mt-1">{item.description}</p>
                            )}
                          </div>
                          <div className="text-amber-400 dark:text-amber-400 text-amber-600 font-semibold ml-4">
                            ₹{item.price.toFixed(2)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm">No items found in this category</p>
                    )}
                  </div>
                </div>
              ))
              ) : (
                <div className="text-center py-12">
                  <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 mb-4">No menu items were extracted from the PDF.</p>
                  <p className="text-sm text-zinc-500 dark:text-zinc-500 text-zinc-600">Please check the PDF format and ensure it contains menu items with prices.</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-amber-600/20 dark:border-amber-600/20 border-zinc-300/20 flex items-center justify-between gap-4">
              <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600">
                {extractedMenuData.length} categories • {extractedMenuData.reduce((sum, cat) => sum + (cat.items?.length || 0), 0)} items
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowPDFUploadModal(false);
                    setExtractedMenuData([]);
                  }}
                  disabled={loading}
                  className="px-6 py-2 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmExtractedMenu}
                  disabled={loading}
                  className="px-6 py-2 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-lg hover:from-amber-500 hover:to-amber-600 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Confirm & Save</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Loyalty Points Confirmation Modal */}
      {showResetLoyaltyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowResetLoyaltyModal(false)}>
          <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-red-600/20 dark:border-red-600/20 border-red-300/20 p-6 shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto my-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-500 rounded-xl flex items-center justify-center shadow-lg shadow-red-600/30">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-amber-50 dark:text-amber-50 text-zinc-900">Reset All Loyalty Points</h3>
                  <p className="text-sm text-zinc-400 dark:text-zinc-400 text-zinc-600">This action cannot be undone</p>
                </div>
              </div>
              <button
                onClick={() => setShowResetLoyaltyModal(false)}
                className="text-zinc-400 dark:text-zinc-400 text-zinc-600 hover:text-white dark:hover:text-white hover:text-zinc-900 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6 p-4 bg-red-500/10 dark:bg-red-500/10 bg-red-50 border border-red-500/30 dark:border-red-500/30 border-red-300/30 rounded-lg">
              <p className="text-red-400 dark:text-red-400 text-red-700 text-sm font-medium mb-2">⚠️ Warning: Destructive Action</p>
              <p className="text-zinc-300 dark:text-zinc-300 text-zinc-700 text-sm">
                This will permanently delete ALL loyalty points and total spent amounts for ALL customers across ALL restaurants. 
                This action cannot be undone.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={confirmResetLoyaltyPoints}
                disabled={resettingLoyalty}
                className="flex-1 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-600 text-white px-4 py-3 rounded-xl transition-all font-medium disabled:opacity-50 disabled:cursor-wait flex items-center justify-center gap-2 shadow-lg shadow-red-600/30"
              >
                {resettingLoyalty ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    Confirm Reset
                  </>
                )}
              </button>
              <button
                onClick={() => setShowResetLoyaltyModal(false)}
                disabled={resettingLoyalty}
                className="px-4 py-3 bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 rounded-xl transition-all font-medium disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modals */}
      {deleteTableQRConfirm && (
        <DeleteConfirmModal
          isOpen={deleteTableQRConfirm.isOpen}
          title="Delete Table QR Code"
          message={`Are you sure you want to delete the QR code for Table ${deleteTableQRConfirm.tableNumber}?`}
          itemName={`Table ${deleteTableQRConfirm.tableNumber}`}
          loading={loading}
          onConfirm={confirmDeleteTableQR}
          onCancel={() => setDeleteTableQRConfirm(null)}
          confirmText="Delete"
        />
      )}

      {deleteCounterQRConfirm && (
        <DeleteConfirmModal
          isOpen={deleteCounterQRConfirm.isOpen}
          title={deleteCounterQRConfirm.name ? `Delete Counter QR Code "${deleteCounterQRConfirm.name}"` : "Delete Counter QR Code"}
          message={deleteCounterQRConfirm.name 
            ? `Are you sure you want to delete the counter QR code "${deleteCounterQRConfirm.name}"?`
            : "Are you sure you want to delete the counter QR code?"}
          itemName={deleteCounterQRConfirm.name}
          loading={loading}
          onConfirm={deleteCounterQRConfirm.counterQRId ? confirmDeleteCounterQRById : confirmDeleteCounterQR}
          onCancel={() => setDeleteCounterQRConfirm(null)}
          confirmText="Delete"
        />
      )}

      {deleteMenuCategoryConfirm && (
        <DeleteConfirmModal
          isOpen={deleteMenuCategoryConfirm.isOpen}
          title="Delete Menu Category"
          message="Are you sure you want to delete this menu category? This will also delete all items in this category."
          loading={loading}
          onConfirm={confirmDeleteMenuCategory}
          onCancel={() => setDeleteMenuCategoryConfirm(null)}
          confirmText="Delete"
        />
      )}

      {resetLoyaltyConfirm && (
        <DeleteConfirmModal
          isOpen={resetLoyaltyConfirm}
          title="Reset All Loyalty Points"
          message="⚠️ WARNING: This will permanently delete ALL loyalty points and total spent for ALL customers. This action cannot be undone!"
          variant="warning"
          loading={resettingLoyalty}
          onConfirm={confirmResetLoyaltyPoints}
          onCancel={() => setResetLoyaltyConfirm(false)}
          confirmText="Reset All Points"
        />
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
